import { FormEvent, useCallback, useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { brandDirectory, catalogProducts, categories, categoryTree as staticCategoryTree, ePointSnapshot, heroSlides, kitchenProducts, money, products, quickBenefits, quickCategories, refrigeratorCategoryPageSnapshot, refrigeratorSearchSnapshot, refrigeratorShelfSnapshot, riceCookerSnapshot, tabletSnapshot, themeHalls, tvCategorySnapshot, type Product, wasteDisposerSnapshot } from "./data/storefront";
import { deleteHajinProductInquiry, deleteHajinProductReview, isHajinStoreApiEnabled, loadHajinCategoryProducts, loadHajinProduct, loadHajinProductInquiries, loadHajinProductReviews, loadHajinProducts, loadHajinStorefront, submitHajinProductInquiry, submitHajinProductReview, unlockHajinProductInquiry, updateHajinProductInquiry, updateHajinProductReview, uploadHajinProductReviewMedia, type HajinProductInquiry, type HajinProductReview, type HajinStorefrontData } from "./data/storeApi";
import HajinLiveChat from "./HajinLiveChat";

// 원본 카테고리의 주목상품과 AJAX 일반상품은 서로 다른 응답이므로 분리한다.
// 일반상품 첫 행은 원본 응답의 노출 순서(S6289048 → S6293382 → S5511992 → S6289414)를 유지한다.
const kitchenAttentionSnapshot = kitchenProducts.slice(0, 4);
const kitchenListingSnapshot = refrigeratorCategoryPageSnapshot;
const medihiemRecommended = ["s6289048", "s6293382", "s5511992", "s6289414", "s6296247"]
  .map((slug) => refrigeratorSearchSnapshot.find((item) => item.slug === slug))
  .filter((item): item is Product => Boolean(item));

const etlandDisplayAsset = (path: string) => `/assets/etland/${path.split("/").at(-1)}`;
const customerCenterGuideText = "자세한 내용 문의는 우측 하단 실시간 상담 클릭 후 고객센터 문의 남겨주시면 됩니다.";
const cardPaymentNoticeText = [
  "현재 한정 수량 소진 되어, 카드 결제 이벤트가 조기 마감 되었습니다.",
  "저희 전자랜드 온라인점 결제 시 외부 플랫폼 수수료가 발생하지 않아, 고객님들께 추가 할인 혜택으로 돌려드리고 있습니다.",
  customerCenterGuideText,
];

type HajinCustomerSession = { username: string; name: string };

const normalizeHajinCustomer = (customer?: { username?: string | null; name?: string | null } | null): HajinCustomerSession | null => {
  if (!customer?.username) return null;
  return {
    username: customer.username,
    name: customer.name?.trim() || customer.username,
  };
};

const hajinCustomerDisplayName = (customer?: HajinCustomerSession | null) =>
  customer?.name || customer?.username || "회원";

const announceHajinCustomerSession = (customer?: { username?: string | null; name?: string | null } | null) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("hajin:customer-session", { detail: normalizeHajinCustomer(customer) }));
};

const heroImageByTitle: Record<string, string> = {
  "할인을 더 위로": etlandDisplayAsset("2026/06/26/0bb37751-f05e-4deb-9fbf-a981393cbb28.jpg"),
  "에스틸로 브랜드 기획전": etlandDisplayAsset("2026/06/29/ba8f14d4-e46a-41b7-8561-c045e6797b4e.jpg"),
  "닥터하우스 브랜드데이": etlandDisplayAsset("2026/07/23/90311308-0620-48b8-9788-81f24e59fb72.jpg"),
  "쿨한 포인트 증정 이벤트": etlandDisplayAsset("2026/06/26/dbccc042-28d5-4fb1-9fd3-1491ecc5375a.jpg"),
  "캐리어 냉장고": etlandDisplayAsset("2026/07/10/4778cf6d-f7d5-4fee-ae9e-40235decbcf6.jpg"),
  "한여름 치트키 모음전": etlandDisplayAsset("2026/05/15/e9bd1ca7-0763-4024-bbab-a4b036bf2b10.jpg"),
  "New Galaxy 사전구매": etlandDisplayAsset("2026/07/07/a9112d44-58b9-456d-8f97-b57b7d923d26.jpg"),
  "폭염탈출 레전드 세일": etlandDisplayAsset("2026/07/01/51295ed0-dcb1-4960-ad56-2d3c9d76d570.jpg"),
  "BOSE 브랜드 기획전": etlandDisplayAsset("2026/07/03/167c1919-75ff-4261-959f-7340f702a8a8.jpg"),
  "드리미 브랜드 기획전": etlandDisplayAsset("2026/06/15/a45cca50-dc5f-4f7f-bb93-4db9e65f39a3.jpg"),
  "제철 여름 주방템": etlandDisplayAsset("2026/06/01/fc0542e7-56ea-4932-a7fa-e6b8bf17922c.jpg"),
  "에어컨 쿨세일": etlandDisplayAsset("2026/04/24/8624b852-d8de-463c-ae38-935193ce87e7.jpg"),
  "아낙 비움청소기 출시": etlandDisplayAsset("2026/04/24/99bd1cef-0b2b-419f-bf26-8c4328f525ae.jpg"),
  "LG 다품목 혜택": etlandDisplayAsset("2025/09/09/2944dbfd-dbdd-42a4-b18f-b6dd79aec1f8.jpg"),
  "시스템 에어컨 설치 서비스": etlandDisplayAsset("2025/12/11/0d22b363-02ee-4386-8100-3d59f66fe42b.jpg"),
  "포인트마켓": etlandDisplayAsset("2025/12/12/5b53b214-06b5-4b6e-8682-d77087a22caa.jpg"),
  "랜드 PICK 손풍기": etlandDisplayAsset("2026/05/08/8c7b32ec-3736-4971-bf77-ee2d7c22a5cc.jpg"),
};

const quickBenefitImages = [
  "8f7ee67f-6448-4d5d-8e5e-874f00fec2fe.jpg", "cec30607-7c00-4e24-8888-529efb61d211.jpg", "4d5a65be-53a2-4f0e-840b-a26d87e1f13b.jpg", "d9afe19c-7604-4fab-a8ec-7aeee310e045.jpg", "a9d53a08-9318-46eb-ba15-a794150a0a06.jpg",
  "81ec93a4-3dfb-46a9-84fa-bdca4b5096a2.jpg", "e4bc4d64-b350-43c9-8705-2f1fa720e110.jpg", "74956f10-d4b2-4160-b7ea-ac21eae2a55b.jpg", "2a6d6549-7c84-4265-bdb4-2d8261013f08.jpg", "8f88e9b6-2e8d-46bd-abc9-ee94965ef060.jpg",
].map((name) => `/assets/etland/${name}`);

const quickCategoryImages = [
  "4cf6921f-c4f7-47c4-ae45-3fccda665629.jpg", "db2a095e-80fa-483d-85a8-fb0cf9c87502.jpg", "101544c9-ba34-45d7-aeda-a1f65f7417aa.jpg", "b1d30ed6-b316-490b-90ff-df5189be214f.jpg", "7d59eef7-7b6b-4fda-8b3b-f878c9a7f9db.png",
  "72524143-d106-42e9-b19e-813d091068c0.png", "b4e99e72-8679-4d8c-9115-e1bba394e78d.png", "dffa1124-f528-4e22-a014-a8c5b93c5212.png", "81accfd6-5ac0-4cfe-92be-0c7eef64d003.png", "60748c75-c97b-4be9-bb5b-add59b9d2f56.jpg",
].map((name) => `/assets/etland/${name}`);

const todayDeals = [
  { name: "모리츠 슬림 엣지 제습기 MO-DH2027", regular: "199,000원", discount: 39, price: "121,420원", stock: 999, image: "/assets/etland/S6296285_0_320.jpg" },
  { name: "[카처/OCHANDHELDCOMPACT] 무선 휴대용 고압 세척기 컴팩트 가정용 핸디형", regular: "198,000원", discount: 15, price: "168,636원", stock: 6, image: "/assets/etland/S6296177_0_320.jpg" },
  { name: "LG 벽걸이 6평 에어컨 SQ06EA1WCS", regular: "1,000,000원", discount: 47, price: "533,600원", stock: 6, image: "/assets/etland/S6295849_0_320.jpg" },
  { name: "풀무원 2등급 김치냉장고 148L", regular: "1,000,000원", discount: 43, price: "571,820원", stock: 7, image: "/assets/etland/S6296402_0_320.jpg" },
  { name: "드리미 MF10 블레이드리스 팬", regular: "399,000원", discount: 18, price: "327,020원", stock: 5, image: "/assets/etland/S6295714_0_320.jpg" },
];

const themeHallImages = [
  "f883d1a7-aefb-4eb5-abe7-44cee3344541.jpg", "998ca25d-585e-4518-b176-3792ae0f0b24.jpg", "71a59559-67ee-4937-952c-0c66e597f1af.jpg", "8cdddb93-145a-4c6d-b913-8c17eb30cd09.jpg",
  "ca4026cc-32f4-495b-83f5-1986cb01fc09.jpg", "b17da2e6-cefd-4266-a260-0b860ce4290a.jpg", "a236fda6-6f00-41b5-9993-52c9b8c85d9f.jpg", "af342cf0-dc80-4a9b-8bc0-6c88fd09b56f.jpg",
  "64f4d66a-d64f-426a-b222-99b391253f3d.jpg", "24bfa4bf-e151-42a2-b3cd-6da6d51568fe.jpg", "79fd3313-03be-4ff6-a66e-77029b0d8900.jpg", "56ee6396-75a5-4006-80ed-e52f0f8f8271.jpg",
].map((name) => `/assets/etland/${name}`);

const eventShelfProducts = [
  { image: "/assets/etland/S4520572-SYS_20220310154457_0_320.jpg", name: "[캐리어/KRD-T155SEH1] 클라윈드 슬림형 냉장고 155L 배송설치 포함", discount: 32, price: 245_000 },
  { image: "/assets/etland/S6296249-_0_320.jpg", name: "[캐리어/CRF-TD155WDE] 클라윈드 슬림형 냉장고 155L 화이트", discount: 7, price: 297_620 },
  { image: "/assets/etland/S6296399-SYS_20260710165311_0_320.jpg", name: "[캐리어/MRNF508SPM1] 모드비 508L 피트인 파스텔 냉장고 실버 메탈", discount: 19, price: 816_620 },
  { image: "/assets/etland/S6275406-SYS_20221104114731_0_320.jpg", name: "[캐리어/KRD-T155WEH1] 클라윈드 슬림형 냉장고 155L 배송설치 포함", discount: 36, price: 244_020 },
  { image: "/assets/etland/S6296247-_0_320.jpg", name: "[캐리어/KRD-T182WEH1] 클라윈드 슬림형 냉장고 182L 화이트 배송설치 포함", discount: 8, price: 217_460 },
  { image: "/assets/etland/S6289414-SYS_20231023171433_0_320.jpg", name: "[캐리어/MRNS525SPM1] 모드비 양문형 냉장고 525L 실버 설치포함 폐가전수거", discount: 30, price: 738_920 },
  { image: "/assets/etland/S6296423-SYS_2026071416816_0_320.png", name: "[캐리어/MRNF601SPM2] 모드비 601L 4도어 냉장고 김치에디션", discount: 7, price: 931_960 },
];

const sourceEventOrder = ["S6296249", "S6296247", "S6289414", "S6296399", "S6275406", "S4520572", "S6296423"];
eventShelfProducts.sort((left, right) => sourceEventOrder.findIndex((code) => left.image.includes(code)) - sourceEventOrder.findIndex((code) => right.image.includes(code)));

type Page = { kind: "home" } | { kind: "category"; slug: string; subCategory?: string } | { kind: "product"; slug: string } | { kind: "search"; query: string } | { kind: "cart" } | { kind: "order" } | { kind: "account" } | { kind: "signup" } | { kind: "benefit"; slug: string };

function getPage(): Page {
  const { pathname, search } = window.location;
  if (pathname.startsWith("/category/")) return { kind: "category", slug: pathname.split("/")[2] || "", subCategory: new URLSearchParams(search).get("sub") || undefined };
  if (pathname.startsWith("/products/")) return { kind: "product", slug: pathname.split("/")[2] || "" };
  if (pathname === "/search") return { kind: "search", query: new URLSearchParams(search).get("q") || "" };
  if (pathname === "/cart") return { kind: "cart" };
  if (pathname === "/order") return { kind: "order" };
  if (pathname === "/account") return { kind: "account" };
  if (pathname === "/signup") return { kind: "signup" };
  if (pathname === "/benefits/biz") return { kind: "home" };
  if (pathname.startsWith("/benefits/")) return { kind: "benefit", slug: pathname.split("/")[2] || "" };
  return { kind: "home" };
}

function go(path: string) {
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function categorySearchTerm(label: string | undefined) {
  if (!label) return undefined;
  return label.replace(/\([^)]*\)/g, " ").replace(/\s+/g, " ").trim() || undefined;
}

function categoryNameSetFromProducts(list: Product[]) {
  return new Set(list.flatMap((product) => product.categoryNames || []).filter(Boolean));
}

function isDisplayBrandFacet(product: Product, categoryNames: Set<string>) {
  const brand = product.brand?.trim();
  if (!brand) return false;
  // 수집 원천에 따라 brand 칼럼에 "냉장고/냉동고" 같은 카테고리명이
  // 들어온 데이터가 섞여 있다. 공개몰 필터에서는 실제 브랜드 후보만 노출한다.
  if (categoryNames.has(brand) || product.categoryNames?.includes(brand)) return false;
  if (brand.includes("/") && categoryNames.has(brand)) return false;
  return true;
}

type KakaoPostcodeResult = {
  zonecode?: string;
  address?: string;
  roadAddress?: string;
  jibunAddress?: string;
  buildingName?: string;
  bname?: string;
  addressType?: string;
  apartment?: string;
};

type KakaoPostcodeWindow = Window & {
  daum?: {
    Postcode?: new (options: { oncomplete: (data: KakaoPostcodeResult) => void }) => {
      open: (options?: { popupName?: string; left?: number; top?: number }) => void;
    };
  };
  __hajinPostcodeLoading?: Promise<void>;
};

function openHajinPostcode(onSelect: (payload: { zipCode: string; address: string }) => void) {
  const targetWindow = window as KakaoPostcodeWindow;
  const launch = () => {
    const Postcode = targetWindow.daum?.Postcode;
    if (!Postcode) {
      window.alert("우편번호 검색 서비스를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.");
      return;
    }
    new Postcode({
      oncomplete: (data) => {
        const roadAddress = data.roadAddress || data.address || data.jibunAddress || "";
        const extraAddress = data.addressType === "R"
          ? [data.bname, data.buildingName && data.apartment === "Y" ? data.buildingName : ""].filter(Boolean).join(", ")
          : "";
        onSelect({
          zipCode: data.zonecode || "",
          address: extraAddress ? `${roadAddress} (${extraAddress})` : roadAddress,
        });
      },
    }).open({ popupName: "hajinPostcodeSearch" });
  };

  if (targetWindow.daum?.Postcode) {
    launch();
    return;
  }

  if (!targetWindow.__hajinPostcodeLoading) {
    targetWindow.__hajinPostcodeLoading = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js";
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("postcode-load-failed"));
      document.head.appendChild(script);
    });
  }

  targetWindow.__hajinPostcodeLoading.then(launch).catch(() => {
    window.alert("우편번호 검색 서비스를 불러오지 못했습니다. 네트워크 상태를 확인해 주세요.");
  });
}

function millisecondsUntilKoreaMidnight(now = new Date()) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(now);
  const value = (type: string) => Number(parts.find((part) => part.type === type)?.value || 0);
  const nowAsKorea = Date.UTC(value("year"), value("month") - 1, value("day"), value("hour"), value("minute"), value("second"));
  const nextMidnight = Date.UTC(value("year"), value("month") - 1, value("day") + 1, 0, 0, 2);
  return Math.max(1_000, nextMidnight - nowAsKorea);
}

function Icon({ children }: { children: string }) {
  return <span aria-hidden className="grid size-9 place-items-center rounded-full text-lg transition hover:bg-stone-100">{children}</span>;
}

function Layer({ children, onClose, className = "", initialFocusSelector, overlayClass = "bg-black/45" }: { children: ReactNode; onClose: () => void; className?: string; initialFocusSelector?: string; overlayClass?: string }) {
  const layerRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const focusables = () => [...(layerRef.current?.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), select:not([disabled]), [href], [tabindex]:not([tabindex="-1"])') ?? [])];
    const focusLayer = () => {
      const initial = initialFocusSelector ? layerRef.current?.querySelector<HTMLElement>(initialFocusSelector) : null;
      if (initial) { initial.focus(); return; }
      const first = focusables()[0];
      if (first) first.focus();
      else layerRef.current?.focus();
    };
    focusLayer();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") { onCloseRef.current(); return; }
      if (event.key !== "Tab" || !layerRef.current) return;
      const items = focusables();
      if (!items.length) { event.preventDefault(); focusLayer(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => { document.removeEventListener("keydown", handleKeyDown); if (previousFocus && previousFocus !== document.body && previousFocus.isConnected) previousFocus.focus(); };
  }, [initialFocusSelector]);
  return <div className={`fixed inset-0 z-[60] ${overlayClass} ${className}`} onMouseDown={(event) => { if (event.currentTarget === event.target) onClose(); }}><div ref={layerRef} tabIndex={-1}>{children}</div></div>;
}

function SoldOutOverlay() {
  return (
    <>
      <span className="absolute left-2 top-2 z-20 rounded-full bg-stone-950/90 px-2.5 py-1 text-[11px] font-black text-white shadow-sm">품절</span>
      <span className="absolute inset-0 z-10 grid place-items-center bg-white/50 text-[15px] font-black tracking-[0.14em] text-stone-950 backdrop-blur-[1px]">SOLD OUT</span>
    </>
  );
}

function ProductVisual({ product, large = false, tall = false, image, showBadge = true }: { product: Product; large?: boolean; tall?: boolean; image?: string; showBadge?: boolean }) {
  if (image || product.image) {
    return <div className={`relative grid ${tall ? "aspect-[235/267]" : "aspect-square"} place-items-center overflow-hidden rounded-xl bg-[#f5f5f5]`}>
      <img src={image || product.image} alt="" loading="lazy" decoding="async" className="size-full object-contain" />
      {showBadge && product.badge ? <span className="absolute left-0 top-0 bg-[#222] px-1 py-0 text-[10px] font-normal leading-[17px] text-white">{product.badge}</span> : null}
      {product.soldOut ? <SoldOutOverlay /> : null}
    </div>;
  }
  const tablet = product.name.includes("갤럭시탭");
  if (product.name.includes("밥솥")) return <div className={`relative grid ${tall ? "aspect-[235/267]" : "aspect-square"} place-items-center overflow-hidden bg-gradient-to-br ${product.tone}`}><div className="absolute inset-0 bg-[radial-gradient(circle_at_62%_24%,rgba(255,255,255,.9),transparent_35%)]" /><div className={`relative rounded-[42%] border-[7px] border-stone-300 bg-[linear-gradient(145deg,#f8fafc,#9ca3af)] shadow-2xl ${large ? "h-[60%] w-[72%]" : "h-[53%] w-[70%]"}`}><div className="absolute inset-x-[15%] top-[12%] h-[34%] rounded-[45%] border-[5px] border-stone-400 bg-stone-700 shadow-inner"><span className="absolute left-1/2 top-1/2 h-[40%] w-[48%] -translate-x-1/2 -translate-y-1/2 rounded bg-slate-300" /></div><span className="absolute bottom-[15%] left-1/2 h-[8%] w-[22%] -translate-x-1/2 rounded-full bg-stone-600/60" /></div>{showBadge && product.badge ? <span className="absolute left-0 top-0 bg-[#222] px-2 py-1 text-[11px] font-bold text-white">{product.badge}</span> : null}{product.soldOut ? <SoldOutOverlay /> : null}</div>;
  return (
    <div className={`relative grid ${tall ? "aspect-[235/267]" : "aspect-square"} place-items-center overflow-hidden rounded-2xl bg-gradient-to-br ${product.tone}`}>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_65%_30%,rgba(255,255,255,.9),transparent_34%)]" />
      {tablet ? <div className={`relative rounded-[7%] border-[7px] border-slate-800 bg-slate-900 shadow-2xl ${large ? "h-[62%] w-[82%]" : "h-[54%] w-[80%]"}`}><div className="absolute inset-[5%] overflow-hidden rounded-[3%] bg-[linear-gradient(135deg,#dbeafe,#94a3b8_46%,#f8fafc)]"><span className="absolute -right-[8%] top-[8%] size-[55%] rounded-full border-[14px] border-white/40" /><span className="absolute bottom-[10%] left-[12%] size-[28%] rounded-full bg-white/40" /></div><span className="absolute left-1/2 top-[-4px] size-1.5 -translate-x-1/2 rounded-full bg-slate-500" /></div> : <div className={`relative rounded-[22%] border border-white/50 bg-white/75 shadow-2xl backdrop-blur ${large ? "h-3/5 w-3/5" : "h-3/5 w-3/5"}`}><div className="absolute inset-x-[14%] top-[13%] h-[52%] rounded-xl bg-slate-900/85 shadow-inner" /><div className="absolute bottom-[14%] left-1/2 h-[7%] w-[34%] -translate-x-1/2 rounded-full bg-slate-500/30" /></div>}
      {showBadge && product.badge ? <span className="absolute left-3 top-3 rounded-full bg-hajin-600 px-2.5 py-1 text-[11px] font-bold text-white">{product.badge}</span> : null}
      {product.soldOut ? <SoldOutOverlay /> : null}
    </div>
  );
}

function ProductCard({ product, onAdd }: { product: Product; onAdd: (product: Product) => void }) {
  const [cartLayer, setCartLayer] = useState(false);
  return (
    <article className="group min-w-0">
      <button onClick={() => go(`/products/${product.slug}`)} className="block w-full text-left"><ProductVisual product={product} /></button>
      <div className="pt-2.5 sm:pt-3">
        <button onClick={() => go(`/products/${product.slug}`)} className="line-clamp-2 w-full text-left text-[13px] font-medium leading-[1.45] text-stone-800 hover:underline sm:text-sm sm:leading-5">{product.name}</button>
        <div className="mt-2 flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
          {product.discount ? <strong className="text-[16px] tracking-tight text-rose-600 sm:text-lg">{product.discount}%</strong> : null}
          <strong className="text-[17px] tracking-tight sm:text-lg">{money(product.price)}원</strong>
        </div>
        {product.listPrice ? <p className="mt-0.5 text-xs text-stone-400 line-through">{money(product.listPrice)}원</p> : null}
        <BenefitLines product={product} />
      </div>
      {cartLayer ? <Layer onClose={() => setCartLayer(false)} className="grid place-items-center p-5"><section role="dialog" aria-modal="true" aria-label="장바구니 담기 완료" className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-2xl"><p className="text-xl text-hajin-600">✓</p><h2 className="mt-3 text-lg font-black">장바구니에 담았습니다.</h2><p className="mt-2 text-sm text-stone-500">{product.name}</p><div className="mt-6 grid grid-cols-2 gap-2"><button onClick={() => setCartLayer(false)} className="rounded-xl border border-stone-300 py-3 text-sm font-bold">계속 쇼핑</button><button onClick={() => go("/cart")} className="rounded-xl bg-hajin-600 py-3 text-sm font-bold text-white">장바구니 보기</button></div></section></Layer> : null}
    </article>
  );
}

function BenefitLines({ product }: { product: Product }) {
  const benefits = product.benefits || ["▣ 9,570원 쿠폰할인", "▰ 15,000원 카드혜택", "● 5,000P 적립"];
  return <div className="mt-2 grid gap-1 text-[11px] leading-[1.35] sm:mt-3 sm:text-xs">{benefits.map((benefit) => <span key={benefit} className={`line-clamp-1 ${benefit.startsWith("★") ? "text-amber-400" : benefit.startsWith("▣") ? "text-sky-600" : benefit.startsWith("▰") ? "text-emerald-600" : "text-amber-500"}`}>{benefit}</span>)}</div>;
}

function Header({ cartCount, navigationCategories }: { cartCount: number; navigationCategories: typeof categories }) {
  const [keyword, setKeyword] = useState("");
  const [open, setOpen] = useState(false);
  const [topCategory, setTopCategory] = useState<number | null>(null);
  const [secondCategory, setSecondCategory] = useState<number | null>(null);
  const [thirdCategory, setThirdCategory] = useState<number | null>(null);
  // 진입·복귀 기준을 분리해 헤더 높이 변화로 발생하던 스크롤 경계 깜빡임을 막는다.
  const [compact, setCompact] = useState(() => window.scrollY >= 90);
  const [customer, setCustomer] = useState<HajinCustomerSession | null>(null);
  useEffect(() => {
    let active = true;
    const syncCustomer = () => fetch("/api/store/hajin/me", { credentials: "include" })
      .then((response) => response.ok ? response.json() : null)
      .then((payload) => {
        if (active) setCustomer(payload?.authenticated ? normalizeHajinCustomer(payload.customer) : null);
      })
      .catch(() => {});
    const onSessionChange = (event: Event) => {
      setCustomer(normalizeHajinCustomer((event as CustomEvent<HajinCustomerSession | null>).detail));
    };
    syncCustomer();
    window.addEventListener("hajin:customer-session", onSessionChange);
    window.addEventListener("pageshow", syncCustomer);
    return () => {
      active = false;
      window.removeEventListener("hajin:customer-session", onSessionChange);
      window.removeEventListener("pageshow", syncCustomer);
    };
  }, []);
  useEffect(() => {
    let frame = 0;
    const syncCompactHeader = () => {
      frame = 0;
      const y = window.scrollY;
      setCompact((current) => current ? (y <= 30 ? false : current) : (y >= 90 ? true : current));
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(syncCompactHeader);
    };
    syncCompactHeader();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);
  const logout = async () => {
    await fetch("/api/store/hajin/logout", { method: "POST", credentials: "include" }).catch(() => {});
    window.location.assign("/");
  };
  const restoreCategoryTrail = () => {
    const match = window.location.pathname.match(/^\/category\/([^/]+)/);
    const topIndex = match ? categoryTree.findIndex((category) => category.slug === decodeURIComponent(match[1])) : -1;
    if (topIndex < 0) {
      setTopCategory(null);
      setSecondCategory(null);
      setThirdCategory(null);
      return;
    }

    const selectedName = new URLSearchParams(window.location.search).get("sub");
    const top = categoryTree[topIndex];
    const secondIndex = selectedName
      ? top.children.findIndex((item) => item.name === selectedName || item.children.some((child) => child.name === selectedName || child.children.includes(selectedName)))
      : -1;
    const second = secondIndex >= 0 ? top.children[secondIndex] : null;
    const thirdIndex = selectedName && second
      ? second.children.findIndex((item) => item.name === selectedName || item.children.includes(selectedName))
      : -1;

    setTopCategory(topIndex);
    setSecondCategory(secondIndex >= 0 ? secondIndex : null);
    setThirdCategory(thirdIndex >= 0 ? thirdIndex : null);
  };
  const openCategory = (slug: string, label?: string) => {
    setOpen(false);
    setTopCategory(null);
    setSecondCategory(null);
    setThirdCategory(null);
    go(`/category/${slug}${label ? `?sub=${encodeURIComponent(label)}` : ""}`);
  };
  const submit = (event: FormEvent) => { event.preventDefault(); const query = keyword.trim(); if (query) go(`/search?q=${encodeURIComponent(query)}`); };
  const categoryTree = navigationCategories.map((category) => ({ ...category, children: staticCategoryTree.find((item) => item.slug === category.slug)?.children || [] }));
  const toggleCategories = () => {
    if (open) {
      setOpen(false);
      setTopCategory(null);
      setSecondCategory(null);
      setThirdCategory(null);
      return;
    }
    restoreCategoryTrail();
    setOpen(true);
  };
  const currentTop = categoryTree[topCategory ?? 0];
  const currentSecond = secondCategory === null ? null : currentTop.children[secondCategory];
  const currentThird = thirdCategory === null || !currentSecond ? null : currentSecond.children[thirdCategory];
  return (
    <header className="sticky top-0 z-40 bg-white shadow-[0_1px_0_rgba(0,0,0,.08)]">
      <div className={`relative mx-auto flex max-w-[1280px] items-center px-3 transition-[height] duration-150 sm:px-4 ${compact ? "h-[58px] gap-3 md:h-[62px] md:gap-5" : "h-[58px] gap-3 md:h-[70px] md:gap-10"}`}>
        <button onClick={toggleCategories} aria-label="전체 카테고리 열기" aria-expanded={open} className={`grid size-9 shrink-0 place-items-center text-[25px] leading-none md:size-10 md:text-[28px] ${compact ? "" : "md:hidden"}`}>{open ? "×" : "☰"}</button>
        <button onClick={() => go("/")} aria-label="전자랜드 온라인점 홈" className={`flex shrink-0 items-end text-left ${compact ? "w-[150px] md:w-[174px]" : "w-[150px] md:w-[190px]"}`}>
          <span className="flex items-end whitespace-nowrap text-stone-950">
            <span className={`font-black tracking-[-.12em] ${compact ? "text-[25px] md:text-[27px]" : "text-[25px] md:text-[30px]"}`}>전자랜드</span>
            <span className={`ml-1.5 pb-[3px] font-black tracking-[-.08em] ${compact ? "text-[11px] md:text-[13px]" : "text-[11px] md:text-[14px]"}`}>온라인점</span>
          </span>
        </button>
        <form onSubmit={submit} className="hidden max-w-[385px] flex-1 md:block"><div className="flex h-[34px] items-center border-b-2 border-stone-950"><input value={keyword} onChange={(event) => setKeyword(event.target.value)} className="h-full min-w-0 flex-1 bg-transparent text-sm outline-none" aria-label="상품 검색" /><button className="grid size-6 shrink-0 place-items-center" aria-label="검색"><span aria-hidden className="relative block size-[15px] rounded-full border-2 border-stone-950 after:absolute after:-bottom-[5px] after:-right-[4px] after:h-[7px] after:w-[2px] after:rotate-[-45deg] after:bg-stone-950" /></button></div></form>
        <button onClick={() => go("/products/s6296247")} className="relative -left-[6px] hidden min-w-0 flex-1 items-center gap-[9px] text-left lg:flex"><img src="/assets/etland/S6296247-_0_320.jpg" alt="캐리어 냉장고 행사" className="size-[42px] shrink-0 object-contain" /><span className="min-w-0 text-[12px] leading-[15px] text-stone-950"><strong className="block truncate text-[#3478ff]">한정특가 진행중 !</strong><span className="block truncate">1인 가구 필수 냉장고 + 2만원 상품권 증정 <small className="ml-1 rounded bg-stone-400 px-1 text-[9px] text-white">GO ›</small></span></span></button>
        <div className="ml-auto flex h-full items-center gap-3 sm:gap-5 md:gap-7"><button onClick={() => go("/account")} className="grid justify-items-center gap-0.5 text-[10px] font-medium text-stone-900 sm:text-[12px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-hajin-600"><span aria-hidden className="block size-5 bg-[url('/assets/etland/mypage_pc.png')] bg-contain bg-center bg-no-repeat sm:size-[23px]" /><span>마이페이지</span></button><button onClick={() => go("/cart")} className="relative grid justify-items-center gap-0.5 text-[10px] font-medium text-stone-900 sm:text-[12px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-hajin-600"><span aria-hidden className="block h-5 w-6 bg-[url('/assets/etland/cart_pc.png')] bg-contain bg-center bg-no-repeat sm:h-[23px] sm:w-[27px]" /><span>장바구니</span>{cartCount ? <span className="absolute -right-1 top-2 grid size-4 place-items-center rounded-full bg-rose-600 text-[9px] font-bold text-white">{cartCount}</span> : null}</button></div>
      </div>
      <nav className={`overflow-hidden border-y border-stone-100 transition-[max-height,opacity] duration-150 ${compact ? "max-h-0 border-y-0 opacity-0" : "max-h-10 opacity-100"}`}>
        <div className="mx-auto flex h-[40px] max-w-[1280px] items-center gap-4 px-3 sm:gap-6 sm:px-5 md:gap-8">
          <button onClick={toggleCategories} className="hidden h-full shrink-0 items-center gap-2 text-[14px] font-bold md:flex"><span className="text-xl">{open ? "×" : "☰"}</span>카테고리</button>
          <div className="flex min-w-0 flex-1 items-center gap-5 overflow-x-auto whitespace-nowrap pr-2 text-[13px] font-medium sm:gap-7 sm:text-[14px]">
            <button onClick={() => go("/benefits/cash-special")} className="relative h-full px-1 font-bold text-emerald-700 after:absolute after:bottom-0 after:left-1/2 after:h-[2px] after:w-full after:-translate-x-1/2 after:scale-x-0 after:bg-emerald-700 after:transition-transform after:duration-200 hover:after:scale-x-100 focus-visible:after:scale-x-100">현금 할인 특가상품</button>
            <button onClick={() => go("/category/best")} className="relative h-full px-1 after:absolute after:bottom-0 after:left-1/2 after:h-[2px] after:w-full after:-translate-x-1/2 after:scale-x-0 after:bg-stone-900 after:transition-transform after:duration-200 hover:after:scale-x-100 focus-visible:after:scale-x-100">베스트상품</button>
            <button onClick={() => go("/benefits/dcs")} className="relative h-full px-1 after:absolute after:bottom-0 after:left-1/2 after:h-[2px] after:w-full after:-translate-x-1/2 after:scale-x-0 after:bg-stone-900 after:transition-transform after:duration-200 hover:after:scale-x-100 focus-visible:after:scale-x-100">DCS<span className="absolute -right-3 -top-3 text-[9px] text-rose-500">NEW</span></button>
            <button onClick={() => go("/benefits/night-special")} className="relative h-full px-1 after:absolute after:bottom-0 after:left-1/2 after:h-[2px] after:w-full after:-translate-x-1/2 after:scale-x-0 after:bg-stone-900 after:transition-transform after:duration-200 hover:after:scale-x-100 focus-visible:after:scale-x-100">심야특가</button>
            <button onClick={() => go("/benefits/rental")}>가전렌탈</button>
            <button onClick={() => go("/benefits/display")} className="relative h-full px-1 after:absolute after:bottom-0 after:left-1/2 after:h-[2px] after:w-full after:-translate-x-1/2 after:scale-x-0 after:bg-stone-900 after:transition-transform after:duration-200 hover:after:scale-x-100 focus-visible:after:scale-x-100">전시상품</button>
          </div>
          <div className="hidden shrink-0 items-center gap-3 text-[12px] text-stone-500 xl:flex">
            {customer ? <><button onClick={() => go("/account")} className="font-bold text-stone-800">{hajinCustomerDisplayName(customer)}님</button><span className="text-stone-300">|</span><button onClick={logout}>로그아웃</button></> : <><button onClick={() => go("/account")}>로그인</button><span className="text-stone-300">|</span><button onClick={() => go("/signup")}>회원가입</button></>}
            <span className="text-stone-300">|</span><button onClick={() => go("/benefits/customer-center")}>고객센터</button>
          </div>
        </div>
      </nav>
      {open ? <Layer onClose={() => { setOpen(false); setTopCategory(null); setSecondCategory(null); setThirdCategory(null); }} className={compact ? "top-[58px] md:top-[70px]" : "top-[98px] md:top-[113px]"} initialFocusSelector="[data-category-title]"><section role="dialog" aria-modal="true" aria-label="전체 카테고리" className="mx-3 grid h-[min(680px,calc(100dvh-110px))] w-[calc(100vw-24px)] grid-cols-1 overflow-y-auto bg-white sm:mx-5 sm:w-[min(915px,calc(100vw-40px))] sm:grid-cols-2 md:ml-[max(calc((100vw-1280px)/2+5px),20px)] md:mr-0 md:h-[678px] md:w-[915px] md:grid-cols-4 md:overflow-hidden"><div className="relative overflow-auto border-b border-stone-200 md:border-b-0 md:border-r md:after:absolute md:after:-right-px md:after:top-0 md:after:h-[67px] md:after:w-px md:after:bg-white"><h2 data-category-title tabIndex={-1} className="px-5 pb-3 pt-4 text-lg font-black outline-none md:px-[30px] md:pb-[15px] md:pt-5 md:text-xl">카테고리</h2><button type="button" onMouseEnter={() => { setTopCategory(null); setSecondCategory(null); setThirdCategory(null); }} onFocus={() => { setTopCategory(null); setSecondCategory(null); setThirdCategory(null); }} onClick={() => { setOpen(false); setTopCategory(null); setSecondCategory(null); setThirdCategory(null); go("/benefits/cash-special"); }} className="flex w-full items-center justify-between bg-emerald-50 px-5 py-2 text-left text-sm font-black text-emerald-700 hover:bg-emerald-100 md:px-[30px] md:py-[7px]"><span>현금 할인 특가상품</span><span aria-hidden>›</span></button>{categoryTree.map((category, index) => <button key={category.slug} onMouseEnter={() => { setTopCategory(index); setSecondCategory(null); setThirdCategory(null); }} onFocus={() => { setTopCategory(index); setSecondCategory(null); setThirdCategory(null); }} onClick={() => openCategory(category.slug)} className={`flex w-full items-center justify-between px-5 py-2 text-left text-sm font-bold md:px-[30px] md:py-[5px] ${topCategory === index ? "bg-blue-50 text-[#2d63e8]" : "hover:bg-stone-50"}`}><span>{category.name}</span></button>)}</div>{topCategory === null ? <><div className="hidden border-r border-stone-200 md:block" /><div className="hidden border-r border-stone-200 md:block" /><div className="hidden md:block" /></> : <><div className="relative overflow-auto border-b border-stone-200 pt-4 md:border-b-0 md:border-r md:pt-[72px]"><p className="px-5 text-sm font-bold underline md:px-[30px]">{currentTop.name}</p>{currentTop.children.map((category, index) => <button key={category.name} onMouseEnter={() => { setSecondCategory(index); setThirdCategory(null); }} onFocus={() => { setSecondCategory(index); setThirdCategory(null); }} onClick={() => openCategory(currentTop.slug, category.name)} className={`block w-full px-5 py-2 text-left text-sm font-bold md:px-[30px] md:py-[5px] ${secondCategory === index ? "bg-blue-50 text-[#2d63e8]" : "hover:text-[#2d63e8]"}`}>{category.name}</button>)}</div>{currentSecond ? <><div className="relative overflow-auto border-b border-stone-200 pt-4 sm:border-b-0 md:border-r md:pt-[72px]"><p className="px-5 text-sm font-bold underline md:px-[30px]">{currentSecond.name}</p>{currentSecond.children.map((category, index) => <button key={category.name} onMouseEnter={() => setThirdCategory(index)} onFocus={() => setThirdCategory(index)} onClick={() => openCategory(currentTop.slug, category.name)} className={`block w-full px-5 py-2 text-left text-sm font-medium md:px-[30px] md:py-[5px] ${thirdCategory === index ? "bg-blue-50 text-[#2d63e8]" : "hover:text-[#2d63e8]"}`}>{category.name}</button>)}</div><div className="overflow-auto pt-4 md:pt-[97px]">{currentThird?.children.map((name) => <button key={name} onClick={() => openCategory(currentTop.slug, name)} className="block w-full px-5 py-2 text-left text-sm font-medium hover:bg-blue-50 hover:text-[#2d63e8] md:px-[30px] md:py-[5px]">{name}</button>)}</div></> : <><div className="hidden border-r border-stone-200 md:block" /><div className="hidden md:block" /></>}</>}</section></Layer> : null}
    </header>
  );
}

function Home({ onAdd, liveStore, apiEnabled, onTodayDealExpired }: { onAdd: (product: Product) => void; liveStore: HajinStorefrontData | null; apiEnabled: boolean; onTodayDealExpired: () => void }) {
  const [slide, setSlide] = useState(0);
  const registeredProducts = liveStore?.products || [];
  const homeEventProducts = liveStore?.sections.find((section) => section.key === "hajin.home.event-products")?.products || registeredProducts;
  const shelfProducts = (count: number, fallback: Product[]) => registeredProducts.length
    ? Array.from({ length: count }, (_, index) => registeredProducts[index % registeredProducts.length])
    : apiEnabled ? [] : fallback;
  const slides = [
    ["생활을 더 편리하게 만드는", "여름 가전 페스타", "인기 계절가전 최대 45% 혜택", "from-emerald-950 via-hajin-900 to-emerald-700", "COOL SUMMER"],
    ["일상의 모든 장면을 선명하게", "H-LAB AI DIGITAL", "노트북 · 태블릿 사전 예약 혜택", "from-slate-950 via-slate-800 to-indigo-800", "INTELLIGENT LIFE"],
    ["공간을 바꾸는 한 가지", "HAJIN HOME WEEK", "오늘만 추가 10% 쿠폰", "from-orange-950 via-orange-700 to-amber-500", "MAKE YOUR HOME"],
  ];
  const current = slides[slide];
  return <main>
    <DualHero slides={liveStore?.heroSlides.length ? liveStore.heroSlides : undefined} />
    <div className="-mb-8"><TargetQuickZone products={liveStore?.todayProducts || registeredProducts} fallbackEnabled={!apiEnabled} onDealExpired={onTodayDealExpired} /></div>
    <ThemeConnectionV2 />
    <div className="hidden">
    <section className="mx-auto max-w-[1280px] px-5 pt-5">
      <div className="grid overflow-hidden rounded-2xl border border-stone-100 bg-white shadow-card lg:grid-cols-[206px_1fr]">
        <aside className="hidden border-r border-stone-100 py-2 lg:block">
          {categories.map((category, index) => <button key={category.slug} onClick={() => go(`/category/${category.slug}`)} className="flex w-full items-center justify-between px-5 py-[9px] text-left text-[13px] text-stone-700 transition hover:bg-hajin-50 hover:font-bold hover:text-hajin-600"><span>{category.name}</span><span className="text-stone-300">{index < 6 ? "›" : ""}</span></button>)}
          <button className="mt-1 w-full border-t border-stone-100 px-5 py-3 text-left text-xs font-bold text-stone-500">전체 카테고리 보기 +</button>
        </aside>
        <section className={`relative min-h-[360px] overflow-hidden bg-gradient-to-br ${current[3]} px-7 py-10 text-white sm:px-12`}>
          <div className="absolute right-0 top-0 h-full w-[45%] bg-[radial-gradient(circle_at_65%_42%,rgba(255,255,255,.3),transparent_25%),radial-gradient(circle_at_80%_85%,rgba(255,255,255,.15),transparent_34%)]" />
          <div className="relative"><p className="text-xs font-bold tracking-[.18em] text-white/70">{current[4]}</p><p className="mt-6 text-sm font-bold text-white/80">{current[0]}</p><h1 className="mt-2 text-4xl font-black tracking-[-0.08em] sm:text-5xl">{current[1]}</h1><p className="mt-5 text-base font-bold">{current[2]}</p><button className="mt-8 rounded-full bg-white px-5 py-3 text-sm font-bold text-stone-900">기획전 보러가기 <span className="ml-4">→</span></button></div>
          <div className="absolute bottom-8 left-7 flex items-center gap-2 sm:left-12">{slides.map((_, index) => <button key={index} onClick={() => setSlide(index)} className={`h-2 rounded-full transition ${slide === index ? "w-7 bg-white" : "w-2 bg-white/45"}`} aria-label={`${index + 1}번 배너`} />)}<span className="ml-2 text-xs text-white/80">0{slide + 1} / 03</span></div>
          <div className="absolute right-[10%] top-1/2 hidden size-52 -translate-y-1/2 rounded-full border border-white/20 bg-white/10 backdrop-blur md:block"><div className="absolute inset-[16%] rounded-[27%] border-[11px] border-white/90 bg-slate-900 shadow-2xl" /><div className="absolute bottom-[8%] left-1/2 h-3 w-24 -translate-x-1/2 rounded-full bg-white/70" /></div>
        </section>
      </div>
    </section>
    </div>
    <section className="mx-auto grid max-w-[1280px] grid-cols-5 gap-2 px-5 py-7 sm:grid-cols-10 lg:hidden">{categories.map((category) => <button key={category.slug} onClick={() => go(`/category/${category.slug}`)} className="group grid justify-items-center gap-2 py-2 text-xs font-medium"><span className="grid size-12 place-items-center rounded-2xl bg-stone-100 text-xl text-stone-600 transition group-hover:bg-hajin-100 group-hover:text-hajin-600">{category.icon}</span><span className="text-center leading-4">{category.name}</span></button>)}</section>
    <BrandPromoBanner />
    {apiEnabled ? <EventProductShelf list={homeEventProducts.slice(0, 6)} /> : <>
      <EventProductShelf list={shelfProducts(6, refrigeratorSearchSnapshot.slice(0, 6))} />
      <SourceFourShelf title="여름철 악취 해결! 인기 음식물처리기" list={shelfProducts(4, wasteDisposerSnapshot)} />
      <SourceSixShelf title="전자랜드 BEST" list={shelfProducts(6, refrigeratorSearchSnapshot.slice(1, 7))} />
      <SourceBanner image="/assets/etland/banner-main-3.png" alt="태블릿 행사" />
      <div className="-mb-[70px]"><SourceFourShelf title="태블릿 인기상품" list={shelfProducts(4, tabletSnapshot)} /></div>
      <SourceFourShelf title="주방 소형 가전 찾으세요?" list={shelfProducts(4, riceCookerSnapshot)} />
      <SourceSixShelf title="e.POINT로 많이 구매한 상품이에요" list={shelfProducts(6, ePointSnapshot)} />
      <SourceBanner image="/assets/etland/banner-main-2.png" alt="워터 라이프 기획전" />
      <SourceFourShelf title="내게 꼭 맞는 냉장고 찾으세요?" list={shelfProducts(4, refrigeratorShelfSnapshot)} />
      <div className="mt-[123px]"><TrendingStrip list={shelfProducts(6, refrigeratorSearchSnapshot.slice(0, 6))} /></div>
    </>}
    <BrandLineup brands={liveStore?.brands} />
  </main>;
}

function DualHero({ slides = heroSlides }: { slides?: Array<typeof heroSlides[number] & { imageUrl?: string; linkUrl?: string | null }> }) {
  const displaySlides: Array<typeof heroSlides[number] & { imageUrl?: string; linkUrl?: string | null }> = slides.length ? slides : heroSlides;
  const [slide, setSlide] = useState(0);
  const [paused, setPaused] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const move = (offset: number) => setSlide((value) => (value + offset + displaySlides.length) % displaySlides.length);
  // Four panels are intentional: the viewport begins partway through the
  // previous panel, so three panels leave an exposed blank area at the right.
  const visibleSlides = [-1, 0, 1, 2].map((offset) => displaySlides[(slide + offset + displaySlides.length) % displaySlides.length]);
  useEffect(() => setSlide((value) => displaySlides.length ? value % displaySlides.length : 0), [displaySlides.length]);
  useEffect(() => {
    if (paused || displaySlides.length < 2) return;
    // Keep this independent of the click handler.  The handler is recreated on
    // every render, which previously made the autoplay lifecycle fragile when
    // CMS banner data changed while the page was open.
    const timer = window.setInterval(() => {
      setSlide((value) => (value + 1) % displaySlides.length);
    }, 4500);
    return () => window.clearInterval(timer);
  }, [paused, displaySlides.length]);
  return <section className="overflow-hidden pt-1"><div className="md:hidden"><TargetHeroPanel item={displaySlides[slide]} mobile /></div><div className="hidden h-[293px] gap-1 md:flex" style={{ transform: "translateX(calc(50vw - 1277px))" }}>{visibleSlides.map((item, index) => <TargetHeroPanel key={`${item.title}-${slide}-${index}`} item={item} />)}</div><div className="flex items-center justify-center gap-1.5 py-3 text-sm text-stone-700 md:py-4"><button onClick={() => setPaused(!paused)} className="grid size-7 place-items-center rounded-full border border-stone-300" aria-label={paused ? "자동 재생" : "일시 정지"}>{paused ? "▶" : "Ⅱ"}</button><button onClick={() => setListOpen(true)} className="rounded-full border border-stone-300 px-3 py-1 text-xs"><strong>{String(slide + 1).padStart(2, "0")}</strong> / {String(displaySlides.length).padStart(2, "0")} ＋</button><button onClick={() => move(-1)} className="grid size-7 place-items-center rounded-full border border-stone-300" aria-label="이전 배너">‹</button><button onClick={() => move(1)} className="grid size-7 place-items-center rounded-full border border-stone-300" aria-label="다음 배너">›</button></div>{listOpen ? <Layer onClose={() => setListOpen(false)} overlayClass="bg-black/60"><section role="dialog" aria-modal="true" aria-label="메인 배너 전체보기" className="absolute left-3 right-3 top-[86px] h-[min(68dvh,520px)] overflow-y-auto bg-white shadow-xl md:left-[calc(50%+32px)] md:right-auto md:top-[114px] md:h-[340px] md:w-[301px]"><div className="sticky top-0 z-10 flex h-[44px] items-center justify-center border-b border-stone-900 bg-white"><h2 className="text-sm font-bold">전체보기</h2><button onClick={() => setListOpen(false)} className="absolute right-3 top-2 text-2xl font-thin leading-6 outline-none focus-visible:outline-none" aria-label="배너 목록 닫기">×</button></div><div className="px-[17px] py-[11px]">{displaySlides.map((item, index) => <button key={item.title} onClick={() => { setSlide(index); setListOpen(false); }} className="mb-2.5 flex w-full gap-[13px] text-left">{item.imageUrl || heroImageByTitle[item.title] ? <img src={item.imageUrl || heroImageByTitle[item.title]} alt="" className="h-[78px] w-[82px] shrink-0 object-cover" /> : <span className={`grid h-[78px] w-[82px] shrink-0 place-items-center bg-gradient-to-br text-xl font-black text-white ${item.tone}`}>{String(index + 1).padStart(2, "0")}</span>}<span className="min-w-0 pt-[6px]"><strong className="block line-clamp-2 whitespace-pre-line text-sm font-bold leading-5 text-stone-950">{item.title.replace(" ", "\n")}</strong><span className="mt-1 block whitespace-nowrap text-xs text-stone-400">2026.07.01 ~ 2026.07.31</span></span></button>)}</div></section></Layer> : null}</section>;
}

function TargetHeroPanel({ item, mobile = false }: { item: typeof heroSlides[number] & { imageUrl?: string; linkUrl?: string | null }; mobile?: boolean }) {
  const image = item.imageUrl || heroImageByTitle[item.title];
  const title = item.title === "할인을 더 위로" ? "할인을\n더 위로" : item.title === "에스틸로 브랜드 기획전" ? "에스틸로\n브랜드 기획전" : item.title;
  return <button onClick={() => item.linkUrl ? go(item.linkUrl) : undefined} className={`relative shrink-0 overflow-hidden bg-gradient-to-br text-left ${mobile ? "h-[208px] w-full" : "h-[293px] w-[638px]"} ${item.tone}`}>
    {image ? <img src={image} alt="" className="absolute inset-0 size-full object-cover" /> : null}
    <div className={`relative z-10 ${mobile ? "px-6 pt-9" : "px-10 pt-[56px]"}`}><h1 className={`max-w-[250px] whitespace-pre-line font-black leading-[1.08] tracking-[-.09em] text-stone-950 ${mobile ? "text-[29px]" : "text-[40px]"}`}>{title}</h1><p className={`${mobile ? "mt-3 text-[14px]" : "mt-4 text-[18px]"} font-medium tracking-[-.04em] text-stone-800`}>{item.detail}</p></div>
    {!image ? <BannerArtwork kind={item.art} /> : null}
  </button>;
}

function BannerArtwork({ kind }: { kind: typeof heroSlides[number]["art"] }) {
  const display = kind === "laundry" ? "◉ ◌" : kind === "digital" ? "▰" : kind === "kitchen" ? "◒" : kind === "living" ? "◯" : kind === "city" ? "↑ ↑" : "✦";
  return <span aria-hidden className="absolute bottom-[-28px] right-6 grid size-52 place-items-center rounded-full border-[18px] border-white/55 bg-white/25 text-6xl font-black tracking-[-.2em] text-stone-800/70 shadow-2xl backdrop-blur-sm">{display}</span>;
}

function TargetQuickZoneLegacySnapshot() {
  const [timer, setTimer] = useState(9 * 60 * 60 + 59 * 60 + 59);
  const [quickPage, setQuickPage] = useState(0);
  useEffect(() => { const id = window.setInterval(() => setTimer((value) => Math.max(0, value - 1)), 1000); return () => window.clearInterval(id); }, []);
  const time = [Math.floor(timer / 3600), Math.floor((timer % 3600) / 60), timer % 60].map((value) => String(value).padStart(2, "0")).join(" : ");
  const start = quickPage * 7;
  const benefits = quickBenefits.slice(start, start + 7);
  const quickCategoriesPage = quickCategories.slice(start, start + 7);
  const nextQuick = () => setQuickPage((page) => (page + 1) % Math.ceil(quickBenefits.length / 7));
  return <section className="mx-auto flex max-w-[1280px] justify-between pb-[67px] pt-5"><aside className="relative -top-[5px] ml-10 grid w-[462px] shrink-0 grid-cols-[202px_250px] items-center gap-[10px]"><div><strong className="inline-block h-10 rounded-full bg-[#0081ff] px-5 text-[20px] font-bold leading-10 tracking-[-.04em] text-white">오늘의 상품</strong><p className="mt-[6px] text-[34px] font-black leading-none tracking-[-.07em] text-[#0081ff]">{time}</p><p className="mt-3 text-sm leading-6 text-stone-700">모리츠 슬림 엣지 제습기<br />MO-DH2027</p><p className="mt-1 text-sm text-stone-400 line-through">199,000원</p><p className="mt-1 text-[24px] font-black leading-none"><span className="mr-1 text-red-600">39%</span>121,420원</p><span className="mt-4 inline-block h-7 bg-black px-2 text-sm font-bold leading-7 text-white">남은수량 12개</span></div><div className="relative grid size-[250px] place-items-center overflow-hidden rounded-full border-[6px] border-stone-200 border-t-[#0081ff] bg-white"><img src="/assets/etland/S6296285_0_320.jpg" alt="모리츠 슬림 엣지 제습기" className="size-[82%] object-contain" /></div></aside><div className="relative -top-[9px] w-[728px] shrink-0 px-10"><div className="grid grid-cols-7 justify-between gap-y-[58px]">{benefits.map((label, localIndex) => { const index = start + localIndex; return <button key={label} className="group grid justify-items-center gap-2 text-xs font-medium"><img src={quickBenefitImages[index]} alt="" className="size-[68px] rounded-full object-cover group-hover:animate-[hajin-flash_1.1s]" />{label}</button>; })}{quickCategoriesPage.map((label, localIndex) => { const index = start + localIndex; return <button key={label} onClick={() => go(`/category/${categories[index % categories.length].slug}`)} className="group grid justify-items-center gap-2 text-xs font-medium"><img src={quickCategoryImages[index]} alt="" className="size-[68px] rounded-full object-cover group-hover:animate-[hajin-flash_1.1s]" />{label}</button>; })}</div><button onClick={nextQuick} className="absolute right-0 top-1/2 grid size-10 -translate-y-1/2 place-items-center rounded-full border border-stone-300 bg-white text-xl shadow-sm" aria-label="퀵메뉴 다음">›</button></div></section>;
}

function TargetQuickZone({ products: registeredProducts, fallbackEnabled, onDealExpired }: { products: Array<Product & { dealStartedAt?: string; dealExpiresAt?: string }>; fallbackEnabled: boolean; onDealExpired: () => void }) {
  const [now, setNow] = useState(() => new Date());
  const [quickPage, setQuickPage] = useState(0);
  const [todayIndex, setTodayIndex] = useState(0);
  const [ringProgress, setRingProgress] = useState(1);
  const [ringResetting, setRingResetting] = useState(false);
  const [todayTransition, setTodayTransition] = useState(0);
  const [todayDirection, setTodayDirection] = useState<"next" | "prev">("next");
  const shownDealRef = useRef("");
  useEffect(() => { const id = window.setInterval(() => setNow(new Date()), 1_000); return () => window.clearInterval(id); }, []);
  useEffect(() => setTodayIndex((index) => registeredProducts.length ? index % registeredProducts.length : 0), [registeredProducts.length]);
  const hasRegisteredProducts = registeredProducts.length > 0;
  const todayProduct = hasRegisteredProducts ? registeredProducts[todayIndex % registeredProducts.length] : null;
  const todayDealKey = todayProduct ? `${todayProduct.id}:${todayProduct.dealStartedAt || ""}:${todayProduct.dealExpiresAt || ""}` : `fallback:${todayIndex}`;
  const dealStartedMs = todayProduct?.dealStartedAt ? Date.parse(todayProduct.dealStartedAt) : NaN;
  const dealExpiresMs = todayProduct?.dealExpiresAt ? Date.parse(todayProduct.dealExpiresAt) : NaN;
  const hasDealTimer = Number.isFinite(dealStartedMs) && Number.isFinite(dealExpiresMs) && dealExpiresMs > dealStartedMs;
  const remainingMilliseconds = hasDealTimer ? Math.max(0, dealExpiresMs - now.getTime()) : millisecondsUntilKoreaMidnight(now);
  const remainingSeconds = Math.ceil(remainingMilliseconds / 1_000);
  const time = [Math.floor(remainingSeconds / 3600), Math.floor((remainingSeconds % 3600) / 60), remainingSeconds % 60].map((value) => String(value).padStart(2, "0")).join(" : ");
  const remainingProgress = hasDealTimer ? Math.min(1, Math.max(0, remainingMilliseconds / (dealExpiresMs - dealStartedMs))) : Math.min(1, Math.max(0, remainingMilliseconds / 86_400_000));
  // 파란 링은 남은 시간 대신 경과 시간을 표현한다. 시간이 지날수록 채워진다.
  const elapsedProgress = 1 - remainingProgress;
  const ringCircumference = 295.31;
  useEffect(() => {
    const isNewDeal = shownDealRef.current !== todayDealKey;
    shownDealRef.current = todayDealKey;
    if (!isNewDeal) { setRingProgress(elapsedProgress); return; }
    // 상품을 넘긴 직후에는 완전히 채운 상태를 먼저 보여 준 뒤 실제 경과율로 이동한다.
    setRingResetting(true);
    setRingProgress(1);
    const settleTimer = window.setTimeout(() => {
      setRingResetting(false);
      window.requestAnimationFrame(() => setRingProgress(elapsedProgress));
    }, 360);
    return () => window.clearTimeout(settleTimer);
  }, [todayDealKey, elapsedProgress]);
  useEffect(() => {
    if (!hasDealTimer) return;
    const timer = window.setTimeout(onDealExpired, Math.max(250, dealExpiresMs - Date.now() + 150));
    return () => window.clearTimeout(timer);
  }, [todayDealKey, dealExpiresMs, hasDealTimer]);
  const fallbackToday = todayDeals[todayIndex];
  const start = quickPage * 7;
  const quickCategoriesPage = quickCategories.slice(start, start + 7);
  const nextQuick = () => setQuickPage((page) => (page + 1) % Math.ceil(quickCategories.length / 7));
  const todayCount = hasRegisteredProducts ? registeredProducts.length : todayDeals.length;
  const todayName = todayProduct?.name || (fallbackEnabled ? fallbackToday.name : "상품을 불러오는 중입니다.");
  const todayImage = todayProduct?.image || (fallbackEnabled ? fallbackToday.image : undefined);
  const todayRegular = todayProduct ? `${money(todayProduct.listPrice || todayProduct.price)}원` : fallbackEnabled ? fallbackToday.regular : "";
  const todayDiscount = todayProduct ? todayProduct.discount || 0 : fallbackEnabled ? fallbackToday.discount : 0;
  const todayPrice = todayProduct ? `${money(todayProduct.price)}원` : fallbackEnabled ? fallbackToday.price : "";
  const todayStock = todayProduct ? (todayProduct.soldOut ? "품절" : "판매중") : fallbackEnabled ? `${fallbackToday.stock}개` : "";
  const moveToday = (amount: number) => {
    setTodayDirection(amount >= 0 ? "next" : "prev");
    setTodayTransition((value) => value + 1);
    setTodayIndex((index) => (index + amount + todayCount) % todayCount);
  };
  const openQuickCategory = (label: string) => go(`/search?q=${encodeURIComponent(label)}`);
  return <section className="mx-auto flex max-w-[1280px] flex-col gap-8 overflow-hidden px-5 pb-[92px] pt-[15px] lg:flex-row lg:justify-between lg:gap-0 lg:px-0">
    <aside key={`${todayDealKey}-${todayTransition}`} className={`relative -top-[3px] grid w-full grid-cols-[minmax(0,1fr)_132px] items-center gap-3 sm:grid-cols-[minmax(0,1fr)_160px] lg:ml-[45px] lg:w-[462px] lg:shrink-0 lg:grid-cols-[202px_250px] ${todayTransition ? todayDirection === "next" ? "animate-hajin-slide-next" : "animate-hajin-slide-prev" : ""}`}>
      <div className="relative top-2 sm:top-3"><strong className="inline-block h-9 rounded-full bg-[#0081ff] px-4 text-[18px] font-bold leading-9 tracking-[-.04em] text-white sm:h-10 sm:px-5 sm:text-[20px] sm:leading-10">오늘의 상품</strong><p className="mt-[6px] text-[29px] font-black leading-none tracking-[-.07em] text-[#0081ff] sm:text-[34px]">{time}</p><p className="mt-2 line-clamp-2 text-[13px] leading-5 text-stone-700 sm:mt-3 sm:text-sm sm:leading-6">{todayName}</p><p className="mt-1 text-xs text-stone-400 line-through sm:text-sm">{todayRegular}</p><p className="mt-1 text-[21px] font-black leading-none sm:text-[24px]"><span className="mr-1 text-red-600">{todayDiscount}%</span>{todayPrice}</p><span className="mt-3 inline-block h-6 bg-black px-2 text-xs font-bold leading-6 text-white sm:mt-4 sm:h-7 sm:text-sm sm:leading-7">{todayStock && `남은수량 ${todayStock}`}</span><div className="mt-3 flex items-center gap-2 text-xs"><button onClick={() => moveToday(-1)} className="grid size-5 place-items-center border border-stone-300" aria-label="오늘의 상품 이전">‹</button><span>{todayIndex + 1} / {todayCount}</span><button onClick={() => moveToday(1)} className="grid size-5 place-items-center border border-stone-300" aria-label="오늘의 상품 다음">›</button></div></div>
      <div className="hajin-today-orb relative grid size-[132px] place-items-center rounded-full sm:size-[160px] lg:size-[250px]">
        <svg aria-hidden viewBox="0 0 100 100" className="pointer-events-none absolute inset-0 size-full -rotate-90">
          <circle cx="50" cy="50" r="47" fill="none" stroke="#e7e5e4" strokeWidth="3" />
          <circle cx="50" cy="50" r="47" fill="none" stroke="#0081ff" strokeWidth="3" strokeLinecap="round" strokeDasharray={ringCircumference} strokeDashoffset={ringCircumference * (1 - ringProgress)} className={`transition-[stroke-dashoffset] ease-out ${ringResetting ? "duration-0" : "duration-[900ms]"}`} />
        </svg>
        <span className="hajin-today-orb__stage">{todayImage ? <img key={todayIndex} src={todayImage} alt={todayName} className="hajin-today-orb__image animate-[hajin-flash_.35s_ease-out]" /> : null}</span>
      </div>
    </aside>
    <div className="relative flex w-full items-center justify-center lg:w-[728px] lg:shrink-0 lg:self-center lg:px-6">
      <div className="flex w-full snap-x snap-mandatory gap-4 overflow-x-auto px-1 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:grid lg:max-w-[680px] lg:grid-cols-7 lg:justify-items-center lg:gap-y-6 lg:overflow-visible lg:p-0">
        {quickCategoriesPage.map((label, localIndex) => { const index = start + localIndex; return <button key={label} onClick={() => openQuickCategory(label)} className="group grid shrink-0 snap-start justify-items-center gap-2 text-[12px] font-medium sm:gap-3 sm:text-sm lg:shrink"><img src={quickCategoryImages[index]} alt={label} loading="lazy" className="h-[82px] w-[58px] rounded-full object-cover transition-transform group-hover:-translate-y-1 group-hover:animate-[hajin-flash_1.1s] sm:h-[106px] sm:w-[74px] lg:h-[126px] lg:w-[88px]" /><span>{label}</span></button>; })}
      </div>
      <button onClick={nextQuick} className="absolute right-0 top-1/2 hidden size-10 -translate-y-1/2 place-items-center rounded-full border border-stone-300 bg-white text-xl shadow-sm lg:grid" aria-label="퀵메뉴 다음">›</button>
    </div>
  </section>;
}

function TargetQuickZoneLegacy() {
  const [timer, setTimer] = useState(7 * 60 * 60 + 34 * 60 + 42);
  const [quickPage, setQuickPage] = useState(0);
  useEffect(() => { const id = window.setInterval(() => setTimer((value) => Math.max(0, value - 1)), 1000); return () => window.clearInterval(id); }, []);
  const time = [Math.floor(timer / 3600), Math.floor((timer % 3600) / 60), timer % 60].map((value) => String(value).padStart(2, "0")).join(" : ");
  const start = quickPage * 7;
  const benefits = quickBenefits.slice(start, start + 7);
  const quickCategoriesPage = quickCategories.slice(start, start + 7);
  const nextQuick = () => setQuickPage((page) => (page + 1) % Math.ceil(quickBenefits.length / 7));
  return <section className="mx-auto flex max-w-[1280px] justify-between pb-[65px] pt-5"><aside className="relative -top-[27px] ml-10 grid w-[462px] shrink-0 grid-cols-[202px_250px] items-center gap-[10px]"><div><strong className="inline-block h-10 rounded-full bg-[#0081ff] px-5 text-[20px] font-bold leading-10 tracking-[-.04em] text-white">오늘의 상품</strong><p className="mt-[6px] text-[34px] font-black leading-none tracking-[-.07em] text-[#0081ff]">{time}</p><p className="mt-3 text-sm leading-6 text-stone-700">고소리 무선 폴딩 선풍기 15인치<br />7엽날개 대용량 저소음</p><p className="mt-1 text-sm text-stone-400 line-through">259,000원</p><p className="mt-1 text-[24px] font-black leading-none"><span className="mr-1 text-red-600">46%</span>141,020원</p><span className="mt-4 inline-block h-7 bg-black px-2 text-sm font-bold leading-7 text-white">남은수량 9개</span></div><div className="relative grid size-[250px] place-items-center overflow-hidden rounded-full border-[6px] border-stone-200 border-t-[#0081ff] bg-white"><img src="/assets/etland/db2a095e-80fa-483d-85a8-fb0cf9c87502.jpg" alt="무선 폴딩 선풍기" className="size-[82%] object-contain" /></div></aside><div className="relative -top-[31px] w-[728px] shrink-0 px-10"><div className="grid grid-cols-7 justify-between gap-y-[58px]">{benefits.map((label, localIndex) => { const index = start + localIndex; return <button key={label} className="group grid justify-items-center gap-2 text-xs font-medium"><img src={quickBenefitImages[index]} alt="" className="size-[68px] rounded-full object-cover transition group-hover:-translate-y-1" />{label}</button>; })}{quickCategoriesPage.map((label, localIndex) => { const index = start + localIndex; return <button key={label} onClick={() => go(`/category/${categories[index % categories.length].slug}`)} className="group grid justify-items-center gap-2 text-xs font-medium"><img src={quickCategoryImages[index]} alt="" className="size-[68px] rounded-full object-cover transition group-hover:-translate-y-1" />{label}</button>; })}</div><button onClick={nextQuick} className="absolute right-0 top-1/2 grid size-10 -translate-y-1/2 place-items-center rounded-full border border-stone-300 bg-white text-xl shadow-sm" aria-label="퀵메뉴 다음">›</button></div></section>;
}

function ThemeConnectionV2() {
  const [start, setStart] = useState(0);
  const [paused, setPaused] = useState(false);
  const [enteredIndex, setEnteredIndex] = useState<number | null>(null);
  const visible = [0, 1, 2].map((offset) => {
    const index = (start + offset) % themeHalls.length;
    return { ...themeHalls[index], image: themeHallImages[index] };
  });
  const moveTheme = (direction: number) => {
    setEnteredIndex(direction > 0 ? 2 : 0);
    setStart((value) => (value + direction + themeHalls.length) % themeHalls.length);
  };
  useEffect(() => {
    if (paused || themeHalls.length < 2) return;
    const id = window.setInterval(() => moveTheme(1), 5_000);
    return () => window.clearInterval(id);
  }, [paused]);
  const themeQuery = (detail: string) => {
    if (detail.includes("제습기")) return "제습기";
    if (detail.includes("여름") || detail.includes("바람")) return "선풍기";
    if (detail.includes("야구") || detail.includes("스포츠")) return "스포츠";
    if (detail.includes("AI") || detail.includes("PC") || detail.includes("노트북") || detail.includes("책상")) return "노트북";
    if (detail.includes("공기청정기")) return "공기청정기";
    if (detail.includes("가구원")) return "냉장고";
    return "인기상품";
  };
  const openTheme = (detail: string) => go(`/search?q=${encodeURIComponent(themeQuery(detail))}`);
  return <section id="theme-halls" className="mt-[38px] scroll-mt-32 bg-[linear-gradient(to_bottom,rgba(233,244,255,.98),#6db3ff)]">
    <div className="mx-auto max-w-[1280px] px-4 py-8 pb-12 sm:px-5 sm:pb-[85px] lg:px-0">
      <div className="grid gap-5 lg:grid-cols-[230px_minmax(0,1fr)] lg:gap-[30px]">
        <aside className="pt-2"><p className="text-[25px] font-bold leading-[1.2] tracking-[-.08em] sm:text-[28px]">가전과 일상을<br />연결하다</p><button onClick={() => openTheme(visible[0]?.detail || "")} className="mt-5 border-b border-black text-sm sm:mt-10 sm:text-base">관련 상품 보기</button></aside>
        <div className="min-w-0">
          <div className="flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2 sm:grid sm:grid-cols-3 sm:overflow-visible">{visible.map((card, index) => <button key={card.title} onClick={() => openTheme(card.detail)} aria-label={`${card.title.replace("\n", " ")} ${card.detail} 관련 상품 보기`} className={`relative h-[340px] w-[calc(100vw-32px)] shrink-0 snap-center overflow-hidden rounded-[26px] text-left transition-all duration-500 hover:-translate-y-1 sm:h-[401px] sm:w-auto sm:min-w-0 sm:rounded-[30px] ${enteredIndex === index ? "animate-[hajin-flash_.45s_ease-out]" : ""}`}><img src={card.image} alt="" loading="lazy" className="absolute inset-0 size-full object-cover" /><span className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/60 via-black/10 to-transparent" /><span className={`absolute bottom-0 inset-x-0 px-6 pb-6 text-[24px] font-bold leading-[1.3] sm:px-8 sm:pb-[30px] sm:text-[28px] ${enteredIndex === index ? "animate-hajin-rise" : ""} ${card.light ? "text-black" : "text-white"}`}>{card.title.split("\n").map((line) => <span key={line} className="block">{line}</span>)}<small className="mt-2 block text-sm font-normal">{card.detail}</small><small className="mt-3 block text-xs font-bold">관련 상품 보기 →</small></span></button>)}</div>
          <div className="mt-5 flex items-center gap-4 sm:mt-[30px] sm:gap-5"><div className="h-1 flex-1 overflow-hidden rounded bg-[#666]"><div className="h-full bg-white transition-all duration-500" style={{ width: `${((start % themeHalls.length) + 1) / themeHalls.length * 100}%` }} /></div><div className="flex gap-3 text-white"><button onClick={() => moveTheme(-1)} aria-label="테마관 이전">‹</button><button onClick={() => moveTheme(1)} aria-label="테마관 다음">›</button><button onClick={() => setPaused((value) => !value)} aria-label={paused ? "테마관 자동 재생" : "테마관 일시 정지"}>{paused ? "▶" : "Ⅱ"}</button></div></div>
        </div>
      </div>
    </div>
  </section>;
}

function ThemeConnection() {
  const [start, setStart] = useState(0);
  const [paused, setPaused] = useState(false);
  const visible = [-1, 0, 1, 2].map((offset) => { const index = (start + offset + themeHalls.length) % themeHalls.length; return { ...themeHalls[index], image: themeHallImages[index] }; });
  useEffect(() => { if (paused) return; const id = window.setInterval(() => setStart((value) => (value + 1) % themeHalls.length), 5000); return () => window.clearInterval(id); }, [paused]);
  const themeQuery = (detail: string) => {
    if (detail.includes("제습기")) return "제습기";
    if (detail.includes("여름") || detail.includes("바람")) return "선풍기";
    if (detail.includes("야구") || detail.includes("스포츠")) return "스포츠";
    if (detail.includes("AI") || detail.includes("PC") || detail.includes("노트북") || detail.includes("책상")) return "노트북";
    if (detail.includes("공기청정기")) return "공기청정기";
    if (detail.includes("가구원")) return "냉장고";
    return "인기상품";
  };
  const openTheme = (detail: string) => go(`/search?q=${encodeURIComponent(themeQuery(detail))}`);
  return <section id="theme-halls" className="mt-[38px] scroll-mt-32 bg-[linear-gradient(to_bottom,rgba(233,244,255,.98),#6db3ff)]"><div className="relative mx-auto max-w-[1280px] overflow-hidden pb-[85px]"><div className="absolute inset-y-0 left-0 z-10 w-[260px] bg-[linear-gradient(to_bottom,rgba(233,244,255,.98),#6db3ff)] pt-2"><p className="text-[28px] font-bold leading-[1.2] tracking-[-.08em]">가전과 일상을<br />연결하다</p><button onClick={() => openTheme(visible[1]?.detail || "")} className="mt-10 border-b border-black text-base">관련 상품 보기</button></div><div className="pt-5"><div className="flex gap-[11px]">{visible.map((card, index) => <button key={`${card.title}-${start}-${index}`} onClick={() => openTheme(card.detail)} aria-label={`${card.title.replace("\n", " ")} ${card.detail} 관련 상품 보기`} className="relative h-[401px] w-[333px] shrink-0 overflow-hidden rounded-[30px] text-left transition-transform hover:-translate-y-1"><img src={card.image} alt="" className="absolute inset-0 size-full object-cover" /><span className={`absolute bottom-0 inset-x-0 px-10 pb-[30px] text-[28px] font-bold leading-[1.3] ${card.light ? "text-black" : "text-white"}`}>{card.title.split("\n").map((line) => <span key={line} className="block">{line}</span>)}<small className="mt-2 block text-sm font-normal">{card.detail}</small><small className="mt-3 block text-xs font-bold">관련 상품 보기 →</small></span></button>)}</div><div className="ml-[260px] mt-[30px] flex items-center justify-end gap-[30px]"><div className="h-1 flex-1 overflow-hidden rounded bg-[#666]"><div className="h-full bg-white transition-all" style={{ width: `${((start % 10) + 1) * 10}%` }} /></div><div className="flex gap-1 text-white"><button onClick={() => setStart((value) => (value - 1 + themeHalls.length) % themeHalls.length)} aria-label="테마관 이전">‹</button><button onClick={() => setStart((value) => (value + 1) % themeHalls.length)} aria-label="테마관 다음">›</button><button onClick={() => setPaused(!paused)} aria-label={paused ? "테마관 자동 재생" : "Ⅱ"}>{paused ? "▶" : "Ⅱ"}</button></div></div></div></div></section>;
}

const benefitPageCopy: Record<string, { eyebrow: string; title: string; description: string; action: string; query: string }> = {
  "cash-special": { eyebrow: "CASH DISCOUNT", title: "현금 할인 특가상품", description: "무통장입금·현금 결제 고객을 위한 추가 할인 특가 상품을 모았습니다.", action: "현금 특가상품 보기", query: "특가" },
  member: { eyebrow: "MEMBER BENEFITS", title: "회원혜택", description: "가입부터 구매까지, 전자랜드 온라인점 회원만을 위한 쿠폰·포인트·행사 혜택을 모았습니다.", action: "혜택 상품 보기", query: "인기상품" },
  attendance: { eyebrow: "DAILY CHECK-IN", title: "출석체크", description: "매일 한 번, 오늘의 전자랜드 온라인점 출석을 확인해 보세요.", action: "오늘 출석하기", query: "인기상품" },
  themes: { eyebrow: "THEME HALL", title: "테마관", description: "계절과 라이프스타일에 맞춰 고른 전자랜드 온라인점 테마별 상품을 만나보세요.", action: "테마 상품 보기", query: "선풍기" },
  promotions: { eyebrow: "PROMOTION", title: "프로모션", description: "진행 중인 브랜드 행사와 기획전을 한 곳에서 확인하세요.", action: "행사 상품 보기", query: "특가" },
  points: { eyebrow: "POINT EVENT", title: "포인트 증정", description: "구매와 이벤트 참여로 적립되는 포인트 혜택을 확인하세요.", action: "포인트 혜택 보기", query: "인기상품" },
  reviews: { eyebrow: "BEST REVIEW", title: "리뷰 이벤트", description: "구매 후기를 남기고 다음 고객에게 도움이 되는 경험을 공유해 주세요.", action: "리뷰 작성 안내", query: "인기상품" },
  "internet-tv": { eyebrow: "INTERNET & TV", title: "인터넷&TV", description: "TV와 디지털 생활을 위한 상품과 혜택을 모았습니다.", action: "TV 상품 보기", query: "TV" },
  stores: { eyebrow: "STORE VISIT", title: "매장방문", description: "가까운 전자랜드 매장에서 제품을 직접 보고 상담받아 보세요.", action: "인기 가전 보기", query: "냉장고" },
  land500: { eyebrow: "LAND500", title: "LAND500", description: "전자랜드 온라인점 멤버십 전용의 특별한 쇼핑 혜택을 확인하세요.", action: "혜택 상품 보기", query: "인기상품" },
  cleaning: { eyebrow: "APPLIANCE CARE", title: "가전청소", description: "오래 쓰는 가전을 위한 관리·청소 관련 상품을 확인하세요.", action: "청소 상품 보기", query: "청소기" },
  dcs: { eyebrow: "DCS", title: "DCS", description: "디지털 라이프를 위한 DCS 상품과 진행 중인 행사를 확인하세요.", action: "DCS 상품 보기", query: "노트북" },
  "night-special": { eyebrow: "NIGHT SPECIAL", title: "심야특가", description: "매일 밤 진행되는 한정 특가 상품을 확인하세요.", action: "심야특가 상품 보기", query: "특가" },
  rental: { eyebrow: "APPLIANCE RENTAL", title: "가전렌탈", description: "설치와 관리까지 함께하는 가전 렌탈 상품을 확인하세요.", action: "렌탈 상품 보기", query: "가전" },
  display: { eyebrow: "DISPLAY PRODUCT", title: "전시상품", description: "전시·리퍼 상품의 혜택과 재고를 확인하세요.", action: "전시상품 보기", query: "전시상품" },
};

const etlandSourceAsset = (path: string) => `https://www.etlandmall.co.kr${path}`;

function MemberBenefitsPage() {
  const [notice, setNotice] = useState("");
  return (
    <main className="min-h-[680px] bg-[#f5f6fa] pb-20">
      <section className="mx-auto max-w-[1080px] px-5 py-14">
        <div className="rounded-3xl bg-white px-5 py-12 text-center shadow-sm sm:px-12">
          <span className="rounded-full bg-[#e9f0ff] px-4 py-2 text-sm font-black text-hajin-600">회원 전용</span>
          <h1 className="mt-5 text-3xl font-black tracking-[-.07em]">신규회원 웰컴 혜택</h1>
          <p className="mt-4 text-sm leading-6 text-stone-600">가입부터 구매까지 전자랜드 온라인점 회원을 위한 쿠폰과 포인트 혜택을 확인하세요.</p>
          <div className="mx-auto mt-10 grid max-w-[720px] gap-4 sm:grid-cols-3">
            {["가입 웰컴 쿠폰", "구매 적립 혜택", "회원 전용 행사"].map((label) => <article key={label} className="rounded-2xl bg-stone-50 px-5 py-8 text-base font-bold text-stone-800">{label}</article>)}
          </div>
          <button onClick={() => setNotice("가입 후 쿠폰과 포인트는 마이페이지에서 확인할 수 있습니다.")} className="mt-10 rounded-xl bg-hajin-600 px-7 py-3 text-sm font-bold text-white">회원가입하고 혜택 받기</button>
          {notice ? <p className="mt-4 text-sm font-bold text-hajin-600">{notice}</p> : null}
        </div>
        <section className="mt-8 rounded-xl bg-white p-5 text-sm text-stone-600"><details><summary className="cursor-pointer font-bold text-stone-900">꼭 확인하세요</summary><p className="mt-4 leading-6">쿠폰과 포인트는 신규 가입 및 행사 조건에 따라 발급되며, 사용기한과 적용 조건은 각 혜택 안내를 기준으로 합니다.</p></details></section>
      </section>
    </main>
  );
}

function LegacyMemberBenefitsPage() {
  const [tab, setTab] = useState<"member" | "biz">("member");
  const [notice, setNotice] = useState("");
  const coupons = ["01", "02", "03", "04", "05", "06"];
  return <main className="min-h-[760px] bg-[#f5f6fa] pb-20"><section className="overflow-hidden bg-[#161819]"><div className="mx-auto max-w-[1200px]"><img src={etlandSourceAsset("/nas/cdn/pc/img/event/241227/241227_welcome_benefits_bg_tit01.png")} alt="MEMBERSHIP ONLY 회원 혜택 모음 ZIP" className="mx-auto block w-full max-w-[1200px]" /></div></section><nav className="sticky top-[110px] z-20 border-b border-stone-200 bg-white"><div className="mx-auto grid max-w-[860px] grid-cols-2"><button onClick={() => setTab("member")} className={`py-5 text-base font-black ${tab === "member" ? "border-b-4 border-hajin-600 text-hajin-600" : "text-stone-400"}`}>전자랜드 회원</button><button onClick={() => setTab("biz")} className={`py-5 text-base font-black ${tab === "biz" ? "border-b-4 border-hajin-600 text-hajin-600" : "text-stone-400"}`}>사업자 혜택</button></div></nav>{tab === "member" ? <div className="mx-auto max-w-[1080px] px-5 py-14"><section className="rounded-3xl bg-white px-5 py-12 text-center shadow-sm"><span className="rounded-full bg-[#e9f0ff] px-4 py-2 text-sm font-black text-hajin-600">모든 상품 적용</span><h1 className="mt-5 text-3xl font-black tracking-[-.07em]">신규회원 웰컴 혜택</h1><div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{coupons.slice(0, 4).map((code) => <img key={code} src={etlandSourceAsset(`/nas/cdn/pc/img/event/241227/241227_welcome_benefits_coupon${code}.png`)} alt="신규회원 쿠폰" className="w-full" />)}</div><button onClick={() => setNotice("가입 후 쿠폰과 포인트는 마이페이지에서 확인할 수 있습니다.")} className="mx-auto mt-10 block"><img src={etlandSourceAsset("/nas/cdn/pc/img/event/241227/241227_welcome_benefits_btn01.png")} alt="1초 만에 가입하기" className="max-w-full" /></button>{notice ? <p className="mt-4 text-sm font-bold text-hajin-600">{notice}</p> : null}</section><section className="mt-8 rounded-3xl bg-[#1b1f28] px-5 py-12 text-center text-white"><span className="rounded-full bg-white/15 px-4 py-2 text-sm font-black">APP에서 보면</span><h2 className="mt-5 text-3xl font-black tracking-[-.07em]">보너스 혜택</h2><div className="mx-auto mt-10 grid max-w-[530px] gap-5 sm:grid-cols-2">{coupons.slice(4).map((code) => <img key={code} src={etlandSourceAsset(`/nas/cdn/pc/img/event/241227/241227_welcome_benefits_coupon${code}.png`)} alt="앱 전용 쿠폰" className="w-full" />)}</div><button onClick={() => setNotice("앱 전용 혜택은 준비 중입니다.")} className="mx-auto mt-9 block"><img src={etlandSourceAsset("/nas/cdn/pc/img/event/241227/241227_welcome_benefits_btn02.png")} alt="한 번에 다운 받기" className="max-w-full" /></button></section></div> : <section className="bg-white py-16 text-center"><img src={etlandSourceAsset("/nas/cdn/pc/img/event/241227/241227_welcome_benefits_bg_tit02.png")} alt="사업자 혜택" className="mx-auto max-w-full" /><button onClick={() => go("/search?q=인기상품")} className="mx-auto mt-8 block"><img src={etlandSourceAsset("/nas/cdn/pc/img/event/241227/241227_welcome_benefits_btn03.png")} alt="사업자 혜택 보기" className="max-w-full" /></button></section>}<section className="mx-auto max-w-[1080px] px-5"><details className="rounded-xl bg-white p-5 text-sm text-stone-600"><summary className="cursor-pointer font-bold text-stone-900">꼭 확인하세요</summary><p className="mt-4 leading-6">쿠폰과 포인트는 신규 가입 및 행사 조건에 따라 발급되며, 사용기한과 적용 조건은 각 혜택 안내를 기준으로 합니다.</p></details></section></main>;
}

function SourceImageLanding({ title, images, children }: { title: string; images: string[]; children?: ReactNode }) {
  return <main className="min-h-[680px] bg-white pb-16"><h1 className="sr-only">{title}</h1><div className="mx-auto max-w-[1200px]">{images.map((path) => <img key={path} src={etlandSourceAsset(path)} alt="" className="block w-full" />)}{children}</div></main>;
}

function InternetTvPage({ store = false }: { store?: boolean }) {
  const [submitted, setSubmitted] = useState(false);
  const [carrier, setCarrier] = useState("SKT");
  const prefix = store ? "/nas/cdn/pc/img/event/240603/" : "/nas/cdn/pc/img/event/251016/";
  const images = store ? ["240603_offC_top.jpg", "240603_offC_img01.png", "240603_offC_img01-1.png", "240603_offC_img02.png", "240603_offC_img02-1.png", "240603_offC_img03.png", "240603_offC_img03-1.png", "240603_offC_img04.png", "240603_offC_img04-1.png"] : ["251016_appl_img01.jpg", "251016_appl_img02.jpg", "251016_appl_img03.png", "251016_appl_img05.jpg", "251016_appl_img06.jpg"];
  return <main className="bg-white pb-20"><div className="mx-auto max-w-[1200px]">{images.map((image) => <img key={image} src={etlandSourceAsset(`${prefix}${image}`)} alt="" className="block w-full" />)}<section className="mx-auto max-w-[850px] rounded-2xl bg-[#f3f7ff] p-7 sm:p-10"><h1 className="text-3xl font-black">{store ? "매장 상담 신청하기" : "인터넷+TV 상담 신청하기"}</h1><p className="mt-3 text-sm text-stone-600">이름과 연락처를 남겨주시면 담당자가 영업일 기준으로 연락드립니다.</p><form onSubmit={(event) => { event.preventDefault(); setSubmitted(true); }} className="mt-7 grid gap-4 sm:grid-cols-2"><input required placeholder="이름" className="rounded-lg border border-stone-300 bg-white px-4 py-3" /><input required placeholder="휴대폰번호" inputMode="tel" className="rounded-lg border border-stone-300 bg-white px-4 py-3" />{!store ? <div className="sm:col-span-2"><p className="mb-2 text-sm font-bold">기존 통신사</p><div className="flex flex-wrap gap-2">{["SKT", "KT", "U+", "신규가입"].map((item) => <button type="button" onClick={() => setCarrier(item)} key={item} className={`rounded-full border px-4 py-2 text-sm font-bold ${carrier === item ? "border-hajin-600 bg-hajin-600 text-white" : "border-stone-300 bg-white"}`}>{item}</button>)}</div></div> : null}<label className="flex items-center gap-2 text-sm sm:col-span-2"><input required type="checkbox" className="size-4 accent-hajin-600" />개인정보 수집 및 이용에 동의합니다.</label><button className="rounded-lg bg-hajin-600 py-4 font-bold text-white sm:col-span-2">상담 신청하기</button></form>{submitted ? <p className="mt-4 rounded-lg bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-700">상담 신청이 접수되었습니다. 담당자가 확인 후 연락드립니다.</p> : null}</section></div></main>;
}

function Land500Page() {
  const plans = [["라이트", "1만원", "가입 즉시 1만원 증정", "구매 금액의 0.2% 적립"], ["스탠다드", "3만원", "가입 즉시 3만원 증정", "구매 금액의 0.5% 적립"]];
  return <main className="min-h-[700px] bg-[#f4f7fb] py-14"><section className="mx-auto max-w-[960px] px-5 text-center"><p className="text-lg font-bold">가전양판 최초 유료 멤버십 · 최저가로 가는 슈퍼패스!</p><img src={etlandSourceAsset("/nas/cdn/p/images/land500/logo_500.png")} alt="LAND 500 CLUB" className="mx-auto mt-5 w-[320px] max-w-full" /><h1 className="mt-10 text-3xl font-black">LAND 500 CLUB만의 혜택</h1><div className="mt-6 grid gap-4 sm:grid-cols-4">{["회원 전용 온라인 최저가", "가입 시 연회비 페이백", "구매 금액 최대 1% 적립", "매장·쇼핑몰 혜택 적용"].map((item) => <article key={item} className="rounded-2xl bg-white p-5 text-sm font-bold shadow-sm">{item}</article>)}</div><div className="mt-10 grid gap-5 md:grid-cols-2">{plans.map(([name, price, welcome, reward]) => <article key={name} className="rounded-3xl bg-white p-8 text-left shadow-sm"><p className="text-sm font-bold text-hajin-600">LAND 500</p><h2 className="mt-2 text-3xl font-black">{name}</h2><p className="mt-3 text-lg">연회비 <strong>{price}</strong></p><ul className="mt-6 space-y-3 text-sm text-stone-600"><li>· {welcome}</li><li>· {reward}</li></ul><button onClick={() => go("/account")} className="mt-8 w-full rounded-xl bg-stone-900 py-3 font-bold text-white">가입하기</button></article>)}</div></section></main>;
}

function ReviewEventPage() {
  const reviews = ["/nas/cdn/attach/default/2026/06/10/b66efdc1-df1c-4347-94c2-62c151a6a88e.jpg", "/nas/cdn/attach/default/2026/06/17/d63f9c60-fd8b-4949-8975-8d43d31aa1a3.jpg", "/nas/cdn/attach/default/2026/06/21/7ff29f34-5838-446a-9c51-f73e716beb76.jpeg"];
  return <main className="bg-white pb-20"><div className="mx-auto max-w-[1200px]"><img src={etlandSourceAsset("/nas/cdn/attach/marketing/2026/01/06/9d0f0a0e-3110-4b15-bf06-dcb835a043de.jpg")} alt="베스트 리뷰 이벤트" className="block w-full" /><img src={etlandSourceAsset("/nas/cdn/attach/marketing/2026/01/06/a899234c-053b-47e7-8ef6-68f1e1b001c4.jpg")} alt="리뷰 이벤트 혜택" className="block w-full" /><section className="px-5 py-12"><h1 className="text-center text-3xl font-black">이번 주 베스트 리뷰</h1><div className="mt-8 grid gap-5 sm:grid-cols-3">{reviews.map((image) => <article key={image} className="overflow-hidden rounded-2xl border border-stone-200"><img src={etlandSourceAsset(image)} alt="베스트 리뷰" className="aspect-square w-full object-cover" /><p className="p-4 text-sm leading-6 text-stone-600">제품 성능과 사용 경험을 자세히 공유한 고객님의 후기입니다.</p></article>)}</div></section></div></main>;
}

type SourceMenu = "best" | "dcs" | "night-special" | "display";

const sourceMenuCopy: Record<SourceMenu, { eyebrow: string; title: string; description: string }> = {
  best: { eyebrow: "BEST PRODUCTS", title: "베스트상품", description: "원 전자랜드 베스트상품을 카테고리별로 확인하세요." },
  dcs: { eyebrow: "DCS", title: "DCS", description: "원 전자랜드 DCS 상품을 카테고리별로 확인하세요." },
  "night-special": { eyebrow: "NIGHT SPECIAL", title: "심야특가", description: "원 전자랜드 심야특가의 현재 진행 상품입니다." },
  display: { eyebrow: "DISPLAY PRODUCT", title: "전시상품", description: "원 전자랜드 전시상품을 카테고리별로 확인하세요." },
};

function SourceMenuPage({ source, liveStore }: { source: SourceMenu; liveStore: HajinStorefrontData | null }) {
  const copy = sourceMenuCopy[source];
  const sections = (liveStore?.sections || []).filter((section) => section.key.startsWith(`hajin.home.menu.${source}.`));
  const [selectedKey, setSelectedKey] = useState("");
  useEffect(() => {
    if (!sections.length) return;
    setSelectedKey((current) => sections.some((section) => section.key === current) ? current : sections[0].key);
  }, [liveStore]);
  const selected = sections.find((section) => section.key === selectedKey) || sections[0];
  return <main className="min-h-[720px] bg-white pb-20">
    <section className="mx-auto max-w-[1280px] px-5 pb-10 pt-8">
      <div className="border-b-2 border-stone-900 pb-5">
        <h1 className="text-[30px] font-black tracking-[-.07em] text-stone-950">{copy.title}</h1>
      </div>
      {!liveStore ? <p className="py-20 text-center text-sm text-stone-500">상품을 불러오는 중입니다.</p> : !sections.length ? <p className="py-20 text-center text-sm text-stone-500">현재 진행 중인 상품이 없습니다.</p> : <>
        {sections.length > 1 ? <div className="flex flex-wrap gap-x-7 gap-y-3 border-b border-stone-200 py-5">{sections.map((section) => <button key={section.key} onClick={() => setSelectedKey(section.key)} className={`text-sm font-bold ${selected?.key === section.key ? "text-hajin-600" : "text-stone-500"}`}>{section.title}</button>)}</div> : null}
        {selected?.products.length ? <div className="pt-8"><SourceFourShelf title={selected.title || copy.title} list={selected.products} sourceList /></div> : <div className="py-20 text-center"><p className="text-lg font-bold">현재 진행 중인 상품이 없습니다.</p></div>}
      </>}
    </section>
  </main>;
}

function CustomerCenterPage() {
  const categories = ["전체", "주문·결제", "배송", "교환·반품", "회원·쿠폰", "기타"] as const;
  const faqs = [
    { category: "주문·결제", question: "주문을 취소하고 싶어요.", answer: "상품 준비 전 주문은 마이페이지의 주문 내역에서 취소할 수 있습니다. 이미 출고가 시작된 주문은 우측 하단 실시간 상담으로 문의해 주세요." },
    { category: "주문·결제", question: "카드 결제는 어떻게 진행되나요?", answer: "결제수단에서 카드 결제를 선택하면 PG 결제창 연결 절차가 진행됩니다. 현재 카드 결제 연결 점검 안내가 표시될 경우 무통장입금을 이용하거나 상담센터로 문의해 주세요." },
    { category: "주문·결제", question: "현금영수증을 신청할 수 있나요?", answer: "무통장입금 주문에서는 결제 전 현금영수증 신청 여부와 소득공제·지출증빙, 발급 정보를 선택할 수 있습니다." },
    { category: "배송", question: "배송지는 어디에서 변경하나요?", answer: "주문 전에는 회원정보에 저장된 기본 배송지를 선택하거나 새 배송지를 추가할 수 있습니다. 주문 후 배송지 변경은 출고 전까지 상담센터에 요청해 주세요." },
    { category: "배송", question: "배송은 언제 받을 수 있나요?", answer: "일반 상품은 결제 확인 후 보통 3~5일 이내 배송됩니다. 설치 상품과 도서산간 지역은 일정이 달라질 수 있으며, 상세 상품 안내를 우선 확인해 주세요." },
    { category: "교환·반품", question: "교환이나 반품은 어떻게 신청하나요?", answer: "상품 수령 후 7일 이내에 주문 내역 또는 실시간 상담을 통해 접수해 주세요. 상품 상태와 설치·사용 여부에 따라 반품이 제한될 수 있습니다." },
    { category: "회원·쿠폰", question: "쿠폰 코드는 어디에 입력하나요?", answer: "주문서의 쿠폰 할인 영역에서 보유 쿠폰을 선택하거나 쿠폰 코드를 직접 입력해 적용할 수 있습니다. 쿠폰은 조건과 유효기간을 확인해 주세요." },
    { category: "회원·쿠폰", question: "관리자가 보낸 쪽지는 어디에서 확인하나요?", answer: "화면 오른쪽 아래 종 모양의 쪽지함에서 제목을 선택하면 받은 쪽지의 제목과 전체 내용을 확인할 수 있습니다." },
    { category: "기타", question: "상품이나 주문 관련 문의는 어디로 하나요?", answer: "오른쪽 아래 고객센터 문의하기를 누르면 실시간 상담창이 열립니다. 운영시간 외 문의는 남겨주시면 확인 후 답변드립니다." },
  ] as const;
  const [selectedCategory, setSelectedCategory] = useState<(typeof categories)[number]>("전체");
  const [openQuestion, setOpenQuestion] = useState<string | null>(faqs[0].question);
  const visibleFaqs = selectedCategory === "전체" ? faqs : faqs.filter((faq) => faq.category === selectedCategory);
  return <main className="min-h-[720px] bg-[#f7f8fa] pb-20"><section className="bg-[linear-gradient(120deg,#0d3471,#1366dc_58%,#66a6ff)] text-white"><div className="mx-auto max-w-[1000px] px-5 py-14"><p className="text-xs font-black tracking-[.18em] text-white/70">CUSTOMER CENTER</p><h1 className="mt-3 text-4xl font-black tracking-[-.08em]">고객센터</h1><p className="mt-4 text-sm leading-6 text-white/85">주문, 배송, 교환·반품과 회원 혜택에 대한 궁금한 점을 빠르게 확인하세요.</p></div></section><section className="mx-auto max-w-[1000px] px-5 py-9"><section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"><div className="border-b border-slate-100 px-6 py-6"><h2 className="text-xl font-black text-slate-900">자주 묻는 질문</h2><p className="mt-1 text-sm text-slate-500">원하는 항목을 선택해 답변을 확인해 주세요.</p><div className="mt-5 flex flex-wrap gap-2">{categories.map((category) => <button type="button" key={category} onClick={() => { setSelectedCategory(category); setOpenQuestion(null); }} className={`rounded-full px-4 py-2 text-sm font-bold transition ${selectedCategory === category ? "bg-[#1366dc] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{category}</button>)}</div></div><div>{visibleFaqs.map((faq) => { const opened = openQuestion === faq.question; return <article key={faq.question} className="border-b border-slate-100 last:border-0"><button type="button" onClick={() => setOpenQuestion(opened ? null : faq.question)} aria-expanded={opened} className="flex w-full items-center gap-4 px-6 py-5 text-left hover:bg-slate-50"><span className="grid size-7 shrink-0 place-items-center rounded-full bg-blue-50 text-xs font-black text-[#1366dc]">Q</span><strong className="min-w-0 flex-1 text-sm text-slate-800">{faq.question}</strong><span className={`text-lg text-slate-400 transition ${opened ? "rotate-45" : ""}`}>+</span></button>{opened ? <div className="flex gap-4 bg-slate-50 px-6 py-5"><span className="grid size-7 shrink-0 place-items-center rounded-full bg-slate-200 text-xs font-black text-slate-600">A</span><p className="pt-0.5 text-sm leading-6 text-slate-600">{faq.answer}</p></div> : null}</article>; })}</div></section></section></main>;
}

function CashSpecialPage({ liveStore }: { liveStore: HajinStorefrontData | null }) {
  const products = liveStore?.cashSpecialProducts || [];
  const loading = isHajinStoreApiEnabled() && !liveStore;
  const categories = useMemo(() => {
    const counts = new Map<string, number>();
    products.forEach((product) => {
      [...new Set(product.categoryNames || [])].forEach((name) => counts.set(name, (counts.get(name) || 0) + 1));
    });
    return [...counts.entries()]
      .sort(([left], [right]) => left.localeCompare(right, "ko"))
      .map(([name, count]) => ({ name, count }));
  }, [products]);
  const [selectedCategory, setSelectedCategory] = useState("전체");
  useEffect(() => {
    if (selectedCategory !== "전체" && !categories.some((category) => category.name === selectedCategory)) setSelectedCategory("전체");
  }, [categories, selectedCategory]);
  const visibleProducts = selectedCategory === "전체"
    ? products
    : products.filter((product) => product.categoryNames?.includes(selectedCategory));

  return <main className="min-h-[720px] bg-white pb-20">
    <section className="mx-auto max-w-[1280px] px-5 pb-10 pt-8">
      <div className="border-b-2 border-stone-900 pb-5">
        <h1 className="text-[30px] font-black tracking-[-.07em] text-stone-950">현금 할인 특가상품</h1>
      </div>
      {loading ? <p className="py-20 text-center text-sm text-stone-500">상품을 불러오는 중입니다.</p> : <>
        <nav aria-label="현금 할인 특가상품 카테고리" className="flex flex-wrap gap-x-7 gap-y-3 border-b border-stone-200 py-5">
          <button type="button" onClick={() => setSelectedCategory("전체")} className={`text-sm font-bold ${selectedCategory === "전체" ? "text-hajin-600" : "text-stone-500 hover:text-stone-900"}`}>전체 <span className="text-xs">{products.length}</span></button>
          {categories.map((category) => <button type="button" key={category.name} onClick={() => setSelectedCategory(category.name)} className={`text-sm font-bold ${selectedCategory === category.name ? "text-hajin-600" : "text-stone-500 hover:text-stone-900"}`}>{category.name} <span className="text-xs">{category.count}</span></button>)}
        </nav>
        <div className="flex items-end justify-between border-b border-stone-900 pb-4 pt-10">
          <h2 className="text-[26px] font-black tracking-[-.06em]">{selectedCategory}</h2>
          <p className="text-sm text-stone-500">총 <strong className="text-hajin-600">{visibleProducts.length}</strong>개</p>
        </div>
        {visibleProducts.length ? <div className="grid grid-cols-2 gap-x-3 gap-y-8 py-8 sm:grid-cols-3 sm:gap-x-5 sm:gap-y-10 lg:grid-cols-4">{visibleProducts.map((product) => <SearchProductCard key={product.id} product={product} />)}</div> : <div className="border-b border-stone-200 py-20 text-center"><p className="text-lg font-bold text-stone-800">등록된 현금 할인 특가상품이 없습니다.</p><p className="mt-3 text-sm text-stone-500">관리자에서 공개 상품을 선택하면 해당 상품의 실제 카테고리와 함께 표시됩니다.</p></div>}
      </>}
    </section>
  </main>;
}

function BenefitPage({ slug, liveStore }: { slug: string; liveStore: HajinStorefrontData | null }) {
  if (slug === "cash-special") return <CashSpecialPage liveStore={liveStore} />;
  if (slug === "dcs") return <SourceMenuPage source="dcs" liveStore={liveStore} />;
  if (slug === "night-special") return <SourceMenuPage source="night-special" liveStore={liveStore} />;
  if (slug === "display") return <SourceMenuPage source="display" liveStore={liveStore} />;
  if (slug === "customer-center") return <CustomerCenterPage />;
  if (slug === "member") return <MemberBenefitsPage />;
  if (slug === "internet-tv") return <InternetTvPage />;
  if (slug === "stores") return <InternetTvPage store />;
  if (slug === "land500") return <Land500Page />;
  if (slug === "reviews") return <ReviewEventPage />;
  // 원본 PC CSS에서 app 전용 버튼(evt-ani)은 display:none이다. 모바일용 PNG를
  // 본문 이미지로 렌더링하면 거대한 앱 아이콘으로 확대되므로 PC 화면에서는 제외한다.
  if (slug === "attendance") return <SourceImageLanding title="출석체크" images={["/nas/cdn/attach/marketing/2024/11/28/df055b3b-847a-437c-9a93-623555bef221.jpg"]} />;
  if (slug === "cleaning") return <SourceImageLanding title="선한청소" images={["/nas/cdn/attach/marketing/2026/02/03/6f7c4268-f1e5-48fd-a6da-14376e24049a.jpg", "/nas/cdn/attach/marketing/2026/02/04/874f74d7-93e8-4931-b083-aa6f3ced91cb.jpg"]} />;
  const page = benefitPageCopy[slug] || benefitPageCopy.member;
  const [checkedIn, setCheckedIn] = useState(() => window.localStorage.getItem(`hajin-attendance-${new Date().toISOString().slice(0, 10)}`) === "done");
  const [notice, setNotice] = useState("");
  const today = new Date();
  const calendar = Array.from({ length: 28 }, (_, index) => index + 1);
  const attend = () => {
    if (checkedIn) return;
    window.localStorage.setItem(`hajin-attendance-${new Date().toISOString().slice(0, 10)}`, "done");
    setCheckedIn(true);
    setNotice("오늘 출석이 완료되었습니다. 포인트 지급은 운영 정책에 따라 반영됩니다.");
  };
  const openSearch = (query = page.query) => go(`/search?q=${encodeURIComponent(query)}`);
  const themeCards = themeHalls.slice(0, 6);
  const promotions = heroSlides.slice(0, 6);
  return <main className="min-h-[720px] bg-stone-50 pb-20"><section className="bg-[linear-gradient(120deg,#0d3471,#1366dc_58%,#66a6ff)] text-white"><div className="mx-auto max-w-[1120px] px-5 py-16"><p className="text-xs font-black tracking-[.18em] text-white/70">{page.eyebrow}</p><h1 className="mt-3 text-4xl font-black tracking-[-.08em]">{page.title}</h1><p className="mt-4 max-w-[560px] text-sm leading-6 text-white/85">{page.description}</p></div></section><section className="mx-auto max-w-[1120px] px-5 py-10">{slug === "attendance" ? <article className="rounded-2xl bg-white p-6 shadow-sm"><div className="flex flex-wrap items-start justify-between gap-4"><div><h2 className="text-2xl font-black">오늘의 출석</h2><p className="mt-2 text-sm text-stone-500">하루 한 번 출석하고 진행 중인 회원 혜택을 확인하세요.</p></div><button onClick={attend} disabled={checkedIn} className="rounded-xl bg-hajin-600 px-6 py-3 text-sm font-bold text-white disabled:bg-stone-300">{checkedIn ? "오늘 출석 완료" : "오늘 출석하기"}</button></div><div className="mt-7 grid grid-cols-7 gap-2">{calendar.map((day) => <span key={day} className={`grid aspect-square place-items-center rounded-lg text-sm ${day === today.getDate() ? "bg-hajin-600 font-black text-white" : "bg-stone-100 text-stone-500"}`}>{day}</span>)}</div>{notice ? <p className="mt-5 rounded-lg bg-blue-50 px-4 py-3 text-sm font-bold text-hajin-700">{notice}</p> : null}</article> : slug === "themes" ? <section><h2 className="text-2xl font-black">테마별 추천</h2><div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{themeCards.map((card, index) => <button key={card.title} onClick={() => openSearch(index < 3 ? ["제습기", "선풍기", "선풍기"][index] : "인기상품")} className="overflow-hidden rounded-2xl bg-white text-left shadow-sm transition hover:-translate-y-1"><img src={themeHallImages[index]} alt="" className="h-44 w-full object-cover" /><div className="p-5"><h3 className="whitespace-pre-line text-xl font-black">{card.title}</h3><p className="mt-2 text-sm text-stone-500">{card.detail}</p><span className="mt-4 block text-sm font-bold text-hajin-600">관련 상품 보기 →</span></div></button>)}</div></section> : slug === "promotions" ? <section><h2 className="text-2xl font-black">진행 중인 기획전</h2><div className="mt-6 grid gap-5 md:grid-cols-2">{promotions.map((promotion) => <button key={promotion.title} onClick={() => openSearch(promotion.title.includes("에어컨") ? "에어컨" : promotion.title.includes("냉장") ? "냉장고" : "인기상품")} className="overflow-hidden rounded-2xl bg-white text-left shadow-sm transition hover:-translate-y-1"><img src={heroImageByTitle[promotion.title]} alt="" className="h-44 w-full object-cover" /><div className="p-5"><p className="text-xs font-bold text-hajin-600">{promotion.eyebrow}</p><h3 className="mt-1 text-xl font-black">{promotion.title}</h3><p className="mt-2 text-sm text-stone-500">{promotion.detail}</p></div></button>)}</div></section> : <section className="grid gap-5 md:grid-cols-[1.2fr_.8fr]"><article className="rounded-2xl bg-white p-7 shadow-sm"><h2 className="text-2xl font-black">{page.title} 안내</h2><p className="mt-4 text-sm leading-7 text-stone-600">{page.description}</p><button onClick={() => openSearch()} className="mt-8 rounded-xl bg-hajin-600 px-6 py-3 text-sm font-bold text-white">{page.action}</button></article><article className="rounded-2xl border border-blue-100 bg-blue-50 p-7"><h2 className="font-black text-hajin-700">이용 안내</h2><ul className="mt-4 space-y-3 text-sm leading-6 text-stone-600"><li>· 로그인한 회원은 마이페이지에서 보유 포인트와 주문 정보를 확인할 수 있습니다.</li><li>· 혜택의 적용 조건과 기간은 각 행사 안내를 기준으로 합니다.</li><li>· 상품 및 행사 문의는 우측 하단 실시간 상담으로 남겨주세요.</li></ul></article></section>}</section></main>;
}

function NightDealShelf() {
  return <section className="mx-auto mt-[60px] w-full max-w-[1280px]"><div className="h-[160px] bg-[linear-gradient(115deg,#32106c,#5420aa_55%,#32106c)] pt-8 text-center text-white"><p className="text-[25px] font-medium tracking-[-.07em]">매일 밤 9시에서 새벽 <strong className="font-black">5시까지</strong></p><h2 className="mt-[-2px] text-[50px] font-black tracking-[-.12em]">심야특가 <span className="text-[35px]">→</span></h2></div><div className="relative mx-auto -mt-8 grid w-[1240px] grid-cols-5 gap-[25px] rounded-t-[20px] bg-white px-5 pt-5">{eventShelfProducts.slice(0, 5).map((product) => <article key={product.image} className="w-[220px]"><button className="relative block w-full overflow-hidden bg-[#f5f5f5]"><img src={product.image} alt="" className="aspect-square w-full object-contain" /><span className="absolute left-0 top-0 bg-[#222] px-1 py-0.5 text-[11px] font-bold text-white">온라인 단독</span></button><button className="mt-4 line-clamp-2 w-full text-left text-sm leading-5 text-stone-800">{product.name}</button><p className="mt-2 text-xl font-black tracking-[-.06em]"><span className="mr-1 text-red-600">{product.discount}%</span>{money(product.price)}원</p><div className="mt-2 grid gap-1 text-xs"><span className="text-amber-500">● e.POINT 적립</span><span className="text-sky-600">▣ 무료배송</span></div></article>)}</div><button className="mx-auto mt-12 block h-[57px] w-[400px] border border-stone-300 text-sm font-bold">심야특가 더 보러가기</button></section>;
}

function BrandPromoBanner() {
  return <section className="mx-auto mt-10 max-w-[1280px] px-4 pb-10 sm:mt-20 sm:px-5 sm:pb-[58px] lg:px-0"><button className="block h-[86px] w-full overflow-hidden sm:h-[120px]"><img src="/assets/etland/729d21d9-73d5-4c7a-af27-0594a46cfe83.jpg" alt="에스틸로 브랜드 기획전" loading="lazy" className="size-full object-cover" /></button></section>;
}

function BrandPromoBannerLegacy() {
  return <section className="mx-auto max-w-[1280px] pb-[58px]"><button className="block h-[140px] w-full overflow-hidden"><img src="/assets/etland/729d21d9-73d5-4c7a-af27-0594a46cfe83.jpg" alt="에스틸로 브랜드 기획전" className="size-full object-cover" /></button></section>;
}

function EventProductShelf({ list }: { list: Product[] }) {
  return <section className="mx-auto max-w-[1280px] px-4 pb-12 sm:px-5 sm:pb-[70px] lg:px-0"><h2 className="text-center text-[23px] font-bold tracking-[-.08em] sm:text-[28px]">행사상품 놓치지 마세요</h2><div className="mt-6 grid grid-cols-2 gap-x-3 gap-y-8 sm:mt-7 sm:grid-cols-3 sm:gap-5 lg:grid-cols-6 lg:gap-[23px] lg:px-[5px]">{list.map((product) => { const benefits = product.benefits?.filter((benefit) => !benefit.startsWith("★★★★★")) || ["▣ 10,000원 쿠폰할인", "▰ 5,000원 카드혜택", "● e.POINT 적립"]; return <article key={product.id} className="min-w-0"><button onClick={() => go(`/products/${product.slug}`)} className="relative block w-full overflow-hidden bg-[#f5f5f5]"><ProductVisual product={product} showBadge={false} /><span className="absolute left-0 top-0 bg-[#222] px-1 py-0.5 text-[10px] font-bold text-white sm:text-[11px]">온라인 단독</span></button><span className="mt-3 inline-block border border-[#c1a7ff] bg-[#f9f6ff] px-1 py-0.5 text-[9px] font-bold text-[#724eff] sm:mt-4 sm:text-[10px]">MD PICK 한정특가</span><button onClick={() => go(`/products/${product.slug}`)} className="mt-1 block line-clamp-2 w-full text-left text-[13px] leading-5 sm:text-sm">{product.name}</button><p className="mt-2 text-[18px] font-black sm:text-xl"><span className="mr-1 text-red-600">{product.discount || 0}%</span>{money(product.price)}원</p><div className="mt-2 grid gap-1 text-[10px] sm:text-xs">{benefits.map((benefit) => <span key={benefit} className={benefit.startsWith("●") ? "text-amber-500" : benefit.startsWith("▰") ? "text-emerald-600" : benefit.includes("배송") ? "text-stone-500" : "text-sky-600"}>{benefit}</span>)}<span className="text-stone-500">▣ {product.delivery || "무료배송"}</span></div></article>; })}</div></section>;
}

function EventProductShelfLegacy() {
  return <section className="mx-auto max-w-[1280px] pb-[70px]"><h2 className="text-center text-[28px] font-bold tracking-[-.08em]">행사상품 놓치지 마세요</h2><div className="mt-7 grid grid-cols-6 gap-5">{eventShelfProducts.map((product) => <article key={product.image} className="min-w-0"><button className="relative block w-full overflow-hidden bg-[#f5f5f5]"><img src={product.image} alt="" className="aspect-square w-full object-contain" /><span className="absolute left-0 top-0 bg-[#222] px-1 py-0.5 text-[11px] font-bold text-white">온라인 단독</span></button><button className="mt-3 line-clamp-2 w-full text-left text-sm leading-5">{product.name}</button><p className="mt-2 text-lg font-black"><span className="mr-1 text-red-600">{product.discount}%</span>{money(product.price)}원</p><div className="mt-2 grid gap-1 text-xs text-sky-600"><span>▣ 10,000원 쿠폰할인</span><span className="text-amber-500">● e.POINT 적립</span><span className="text-stone-500">▣ 무료배송</span></div></article>)}</div></section>;
}

function SourceFourShelf({ title, list, sourceList = false }: { title: string; list: Product[]; sourceList?: boolean }) {
  return <section className="mx-auto max-w-[1280px] px-[5px] pb-[10px]"><div className={sourceList ? "flex items-end justify-between border-b border-stone-900 pb-4" : ""}><h2 className={sourceList ? "text-[24px] font-black tracking-[-.07em]" : "text-center text-[28px] font-bold tracking-[-.08em]"}>{title}</h2>{sourceList ? <span className="text-sm text-stone-500">총 <strong className="text-hajin-600">{list.length}</strong>개</span> : null}</div><div className="mt-7 grid grid-cols-2 gap-x-5 gap-y-10 sm:grid-cols-4">{list.map((product) => <article key={product.id}><button onClick={() => go(`/products/${product.slug}`)} className="block w-full bg-[#f5f5f5]"><ProductVisual product={product} /></button><button onClick={() => go(`/products/${product.slug}`)} className="mt-3 line-clamp-2 text-left text-sm leading-5">{product.name}</button><p className="mt-2 text-xl font-black"><span className="mr-1 text-red-600">{product.discount}%</span>{money(product.price)}원</p>{product.benefits?.some((benefit) => benefit.startsWith("★★★★★")) ? <p className="mt-2 text-sm text-amber-400">{product.benefits.find((benefit) => benefit.startsWith("★★★★★"))}</p> : null}<div className="mt-2 grid gap-1 text-xs">{product.benefits?.filter((benefit) => !benefit.startsWith("★★★★★")).map((benefit) => <span key={benefit} className={benefit.startsWith("●") ? "text-amber-500" : benefit.startsWith("▰") ? "text-emerald-600" : "text-sky-600"}>{benefit}</span>)}<span className="text-stone-500">▣ {product.delivery || "무료배송"}</span></div></article>)}</div></section>;
}

function SourceBanner({ image, alt }: { image: string; alt: string }) {
  return <section className="mx-auto mt-[60px] max-w-[1280px] pb-[60px]"><button className="block h-[140px] w-full overflow-hidden"><img src={image} alt={alt} className="size-full object-cover" /></button></section>;
}

function SourceSixShelf({ title, list }: { title: string; list: Product[] }) {
  return <section className="mx-auto max-w-[1280px] px-[5px] pb-[90px]"><h2 className="text-center text-[28px] font-bold tracking-[-.08em]">{title}</h2><div className="mt-7 grid grid-cols-6 gap-[23px]">{list.map((product) => <article key={product.id}><button onClick={() => go(`/products/${product.slug}`)} className="relative block w-full bg-[#f5f5f5]"><ProductVisual product={product} showBadge={false} /><span className="absolute left-0 top-0 bg-[#222] px-1 py-0.5 text-[11px] font-bold text-white">온라인 단독</span></button><button onClick={() => go(`/products/${product.slug}`)} className="mt-3 line-clamp-2 text-left text-sm leading-5">{product.name}</button><p className="mt-2 text-xl font-black"><span className="mr-1 text-red-600">{product.discount}%</span>{money(product.price)}원</p>{product.benefits?.some((benefit) => benefit.startsWith("★★★★★")) ? <p className="mt-2 text-sm text-amber-400">{product.benefits.find((benefit) => benefit.startsWith("★★★★★"))}</p> : null}<div className="mt-2 grid gap-1 text-xs">{product.benefits?.filter((benefit) => !benefit.startsWith("★★★★★")).slice(0, 4).map((benefit) => <span key={benefit} className={benefit.startsWith("●") ? "text-amber-500" : benefit.startsWith("▰") ? "text-emerald-600" : "text-sky-600"}>{benefit}</span>)}<span className="text-stone-500">▣ {product.delivery || "무료배송"}</span></div></article>)}</div></section>;
}

function TrendingStrip({ list }: { list: Product[] }) {
  return <section className="mx-auto max-w-[1280px] px-[5px] pb-[26px]"><h2 className="text-center text-[28px] font-bold tracking-[-.08em]">지금 가장 뜨는 상품</h2><div className="mt-7 grid grid-cols-6 gap-[23px]">{list.map((product) => <article key={product.id}><button onClick={() => go(`/products/${product.slug}`)} className="block w-full bg-[#f5f5f5]"><ProductVisual product={product} showBadge={false} /></button><button onClick={() => go(`/products/${product.slug}`)} className="mt-3 line-clamp-2 w-full text-left text-sm leading-5">{product.name}</button></article>)}</div></section>;
}

function HeroPanel({ item, active = false }: { item: readonly string[]; active?: boolean }) {
  return <button className={`relative h-[330px] w-full overflow-hidden rounded-2xl bg-gradient-to-br text-left text-white transition lg:h-[410px] ${item[3]} ${active ? "opacity-100" : "opacity-30"}`}><div className="absolute right-0 top-0 h-full w-1/2 bg-[radial-gradient(circle_at_50%_45%,rgba(255,255,255,.35),transparent_24%),radial-gradient(circle_at_75%_80%,rgba(255,255,255,.18),transparent_35%)]" /><div className="relative px-9 pt-11 lg:px-10 lg:pt-16"><p className="text-sm font-bold text-white/75">{item[0]}</p><h1 className="mt-2 text-4xl font-black tracking-[-.08em] lg:text-5xl">{item[1]}</h1><p className="mt-5 text-lg font-medium">{item[2]}</p></div><div className="absolute right-[12%] top-1/2 hidden size-44 -translate-y-1/2 rounded-full border border-white/25 bg-white/10 backdrop-blur lg:block"><div className="absolute inset-[17%] rounded-[27%] border-[9px] border-white/90 bg-slate-900 shadow-2xl" /></div></button>;
}

function MainCampaign({ eyebrow, title, detail, tone, symbol }: { eyebrow: string; title: string; detail: string; tone: string; symbol: string }) {
  return <section className="mx-auto max-w-[1280px] px-5 py-8"><button className={`relative flex min-h-[240px] w-full items-center overflow-hidden rounded-[28px] bg-gradient-to-r px-10 text-left text-white ${tone}`}><div className="relative z-10"><p className="text-xs font-bold tracking-[.16em] text-white/70">{eyebrow}</p><h2 className="mt-3 text-[32px] font-black tracking-[-.08em]">{title}</h2><p className="mt-3 text-sm font-medium text-white/90">{detail}</p><span className="mt-7 inline-block border-b border-white pb-1 text-sm font-bold">자세히 보기 →</span></div><span aria-hidden className="absolute right-[9%] top-1/2 grid size-44 -translate-y-1/2 place-items-center rounded-full border-[16px] border-white/35 bg-white/15 text-6xl font-black text-white/80 shadow-2xl">{symbol}</span><span aria-hidden className="absolute -right-20 -top-28 size-80 rounded-full border-[32px] border-white/10" /></button></section>;
}

function Promo({ icon, title, description }: { icon: string; title: string; description: string }) { return <div className="flex items-center gap-3 rounded-2xl bg-white px-4 py-3"><span className="grid size-10 place-items-center rounded-full bg-hajin-50 text-hajin-600">{icon}</span><p><strong className="block text-sm">{title}</strong><span className="text-xs text-stone-500">{description}</span></p></div>; }
function PromoCard({ accent, title, detail }: { accent: string; title: string; detail: string }) { return <button className={`relative overflow-hidden rounded-3xl ${accent} px-7 py-8 text-left`}><p className="text-sm font-bold text-hajin-600">HAJIN EVENT</p><h2 className="mt-1 text-2xl font-black tracking-tight">{title}</h2><p className="mt-2 text-sm text-stone-600">{detail}</p><span className="mt-7 inline-block text-sm font-bold underline">자세히 보기</span><span className="absolute -right-6 -top-8 text-9xl opacity-10">◉</span></button>; }
function BrandLineup({ brands }: { brands?: Array<{ id: string; name: string; slug: string; imageUrl: string; sort: number }> }) {
  const [open, setOpen] = useState(false);
  const allBrands = brands?.length ? [...brands].sort((left, right) => left.sort - right.sort) : brandDirectory.map((brand, index) => ({ id: `fallback-${index}`, name: brand.name, slug: brand.name, imageUrl: brand.image, sort: index }));
  const browse = (brand: { name: string }) => { setOpen(false); go(`/search?q=${encodeURIComponent(brand.name)}`); };
  const firstRow = allBrands.filter((_, index) => index % 2 === 0);
  const secondRow = allBrands.filter((_, index) => index % 2 === 1);
  const BrandTrack = ({ items, reverse = false }: { items: typeof allBrands; reverse?: boolean }) => {
    const looped = [...items, ...items];
    return <div className={`hajin-brand-track ${reverse ? "hajin-brand-track-reverse" : ""}`}>{looped.map((brand, index) => <button key={`${brand.id}-${index}`} onClick={() => browse(brand)} className="hajin-brand-chip group grid w-[88px] shrink-0 justify-items-center gap-2 text-[11px] font-medium text-stone-700 sm:w-[106px] sm:text-xs"><span className="grid size-[62px] place-items-center rounded-full border border-stone-300 bg-white p-3 transition group-hover:border-hajin-600 group-hover:shadow-sm sm:size-[76px]"><img src={brand.imageUrl} alt="" loading="lazy" className="max-h-[28px] max-w-full object-contain sm:max-h-[34px]" /></span><span className="w-full truncate">{brand.name}</span></button>)}</div>;
  };
  return <section className="border-y border-stone-100 bg-stone-50"><style>{`@keyframes hajin-brand-marquee-left{from{transform:translateX(0)}to{transform:translateX(-50%)}}@keyframes hajin-brand-marquee-right{from{transform:translateX(-50%)}to{transform:translateX(0)}}.hajin-brand-viewport{overflow:hidden;mask-image:linear-gradient(to right,transparent,black 5%,black 95%,transparent)}.hajin-brand-track{display:flex;width:max-content;gap:14px;animation:hajin-brand-marquee-left 30s linear infinite}.hajin-brand-track-reverse{animation-name:hajin-brand-marquee-right}.hajin-brand-viewport:hover .hajin-brand-track{animation-play-state:paused}@media (prefers-reduced-motion:reduce){.hajin-brand-track{animation:none}}`}</style><div className="mx-auto max-w-[1280px] px-5 py-10 sm:py-12"><div className="flex items-end justify-between"><div><h2 className="text-[25px] font-semibold tracking-tight sm:text-[28px]">브랜드관</h2><p className="mt-1 text-sm text-stone-500">입점 브랜드를 만나보세요.</p></div><button onClick={() => setOpen(true)} className="text-sm font-bold">전체보기 →</button></div><div className="mt-7 space-y-4"><div className="hajin-brand-viewport"><BrandTrack items={firstRow.length ? firstRow : allBrands} /></div><div className="hajin-brand-viewport"><BrandTrack items={secondRow.length ? secondRow : allBrands} reverse /></div></div></div>{open ? <Layer onClose={() => setOpen(false)} className="grid place-items-center p-3 sm:p-5"><section role="dialog" aria-modal="true" aria-label="입점 브랜드 전체보기" className="max-h-[82dvh] w-[calc(100vw-24px)] max-w-[960px] overflow-y-auto bg-white shadow-2xl sm:h-[630px]"><header className="relative mx-4 flex h-[64px] items-center justify-center border-b border-stone-800 sm:mx-[30px] sm:h-[70px]"><h2 className="text-[20px] font-black tracking-[-.06em]">입점 브랜드</h2><button onClick={() => setOpen(false)} className="absolute right-0 top-[15px] text-[31px] font-thin leading-7 outline-none sm:top-[18px]" aria-label="브랜드 레이어 닫기">×</button></header><div className="mx-4 grid grid-cols-3 sm:mx-[50px] sm:grid-cols-5">{allBrands.map((brand) => <button key={brand.id} onClick={() => browse(brand)} className="grid h-[88px] place-items-center border-b border-stone-100 hover:bg-stone-50 sm:h-[100px]"><img src={brand.imageUrl} alt={brand.name} loading="lazy" className="max-h-[35px] max-w-[86px] object-contain sm:max-w-[150px]" /></button>)}</div></section></Layer> : null}</section>;
}
function ProductShelf({ title, subtitle, list, onAdd }: { title: string; subtitle: string; list: Product[]; onAdd: (product: Product) => void }) { return <section className="mx-auto max-w-[1280px] px-5 py-10"><div className="mb-6 flex items-end justify-between"><div><h2 className="text-2xl font-black tracking-tight">{title}</h2><p className="mt-1 text-sm text-stone-500">{subtitle}</p></div><button className="text-sm font-bold text-stone-600">전체보기 →</button></div><div className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-4"><>{list.map((product) => <ProductCard key={product.id} product={product} onAdd={onAdd} />)}</></div></section>; }

function StorefrontHome({ data, onAdd }: { data: HajinStorefrontData | null; onAdd: (product: Product) => void }) {
  if (!data) return <main className="mx-auto grid min-h-[520px] max-w-[1280px] place-items-center px-5 text-center"><div><h1 className="text-2xl font-black">상품을 불러오는 중입니다.</h1><p className="mt-3 text-sm text-stone-500">관리자에 등록된 상품과 홈 진열 정보를 확인하고 있습니다.</p></div></main>;
  const sections = data.sections.filter((section) => section.products.length > 0);
  return <main>
    {data.heroSlides.length ? <DualHero slides={data.heroSlides} /> : <section className="bg-gradient-to-r from-hajin-800 to-hajin-500 py-20 text-white"><div className="mx-auto max-w-[1280px] px-5"><p className="text-sm font-bold tracking-[.18em] text-white/70">ETLAND ONLINE</p><h1 className="mt-3 text-4xl font-black">전자랜드 온라인점의 상품을 만나보세요.</h1><p className="mt-4 text-sm text-white/85">메인 배너는 관리자 홈 진열에서 등록할 수 있습니다.</p></div></section>}
    <section className="mx-auto grid max-w-[1280px] grid-cols-5 gap-2 px-5 py-7 sm:grid-cols-10"><>{data.categories.map((category) => <button key={category.slug} onClick={() => go(`/category/${category.slug}`)} className="group grid justify-items-center gap-2 py-2 text-xs font-medium"><span className="grid size-12 place-items-center rounded-2xl bg-stone-100 text-xl text-stone-600 transition group-hover:bg-hajin-100 group-hover:text-hajin-600">{category.icon}</span><span className="text-center leading-4">{category.name}</span></button>)}</></section>
    {sections.length ? sections.map((section) => <ProductShelf key={section.id} title={section.title || "추천 상품"} subtitle={section.subtitle || "관리자에서 구성한 상품 진열입니다."} list={section.products} onAdd={onAdd} />) : <ProductShelf title="전체 상품" subtitle="관리자에 등록되어 판매 중인 상품입니다." list={data.products} onAdd={onAdd} />}
  </main>;
}

function ListingLegacy({ title, list, onAdd }: { title: string; list: Product[]; onAdd: (product: Product) => void }) {
  const [filterOpen, setFilterOpen] = useState<string | null>("브랜드");
  const [sort, setSort] = useState<string | null>(null);
  const kitchen = title.includes("냉장고") || title.includes("주방");
  const categoryLabel = kitchen ? "냉장고/주방가전" : "TV/영상가전";
  const categoryChildren = kitchen ? ["냉장고/냉동고", "밥솥(IH압력/일반)", "전자레인지/오븐", "가스/전기레인지", "식기세척기/살균건조기", "정수기", "음식물처리기", "믹서/쥬서/원액기", "커피머신", "토스터기", "전기포트", "식품조리기", "전기그릴/팬", "소형주방가전"] : ["TV", "리퍼TV"];
  const filters: Array<[string, string[]]> = kitchen ? [["브랜드", ["삼성전자 (1)", "캐리어 (4)", "메디하임 (3)"]], ["가격", ["전체", "20만원 이하", "20~50만원", "50만원 이상"]], ["냉장고 품목", ["일반형 냉장고", "쇼케이스 냉장고", "소형 냉장고", "4도어 냉장고"]]] : [["브랜드", ["와이드뷰 (32)", "프리즘코리아 (12)", "LG전자 (6)", "메디하임 (5)"]], ["가격", ["전체", "10만원 이하", "10~30만원", "30만원 이상"]], ["화면 크기", ["32형 이하", "40~50형", "55~65형", "70형 이상"]]];
  return <main className="mx-auto grid max-w-[1280px] gap-x-[60px] px-[5px] pb-7 pt-[18px] lg:grid-cols-[220px_1fr]"><aside className="hidden pt-[18px] lg:block"><h2 className="border-b border-stone-700 pb-5 text-xl font-black">{categoryLabel}</h2>{categoryChildren.map((label) => <button key={label} className="flex w-full items-center justify-between border-b border-stone-300 py-5 text-left font-bold">{label}<span>＋</span></button>)}{!kitchen && filters.map(([name, options]) => <section key={name} className="border-b border-stone-400 py-4"><button onClick={() => setFilterOpen(filterOpen === name ? null : name)} className="flex w-full items-center justify-between text-left font-bold">{name}<span>{filterOpen === name ? "−" : "＋"}</span></button>{filterOpen === name ? <div className="grid gap-3 pt-5 text-sm">{options.map((option) => <label key={option} className="flex cursor-pointer items-center gap-2"><input type="checkbox" className="size-4 accent-stone-900" />{option}</label>)}{name === "브랜드" ? <button className="mt-2 border border-stone-300 py-3 text-sm">브랜드 더보기</button> : null}</div> : null}</section>)}</aside><section><p className="text-sm text-stone-500">홈 &gt; {categoryLabel} &gt; <strong className="text-stone-800">{title}</strong></p><div className="-mt-2 flex items-end gap-3 border-b border-stone-500 pb-3"><h1 className="text-[30px] font-black tracking-[-.06em]">{title}</h1><span className="pb-1 text-sm text-stone-400"><strong className="text-rose-600">{kitchen ? 257 : list.length * 8 + 5}</strong>개의 상품이 있습니다.</span></div><section className="mt-[88px]"><h2 className="text-xl font-black">다른 고객님들이 많이 본 상품</h2><div className="mt-5 grid grid-cols-2 gap-5 md:grid-cols-4">{list.slice(0, 4).map((product) => <article key={product.id}><button onClick={() => go(`/products/${product.slug}`)} className="block w-full"><ProductVisual product={product} /></button><button onClick={() => go(`/products/${product.slug}`)} className="mt-3 line-clamp-2 text-left text-sm leading-5">{product.name}</button><p className="mt-2 text-xl font-black"><span className="mr-1 text-rose-600">{product.discount || 0}%</span>{money(product.price)}<small className="ml-0.5 text-sm">원</small></p><BenefitLines product={product} /></article>)}</div></section><div className="mt-12 flex flex-wrap items-center gap-x-3 gap-y-3 border-b border-stone-300 pb-4 text-sm"><strong>{sort}</strong><span className="text-stone-300">●</span>{["누적판매순", "높은 조회순", "리뷰 많은순", "낮은가격순", "높은가격순", "등록일순"].map((label) => <button key={label} onClick={() => setSort(label)} className={sort === label ? "font-bold" : "text-stone-400 hover:text-stone-900"}>{label}</button>)}<button className="ml-auto border border-stone-300 px-3 py-2 text-xs">40개씩 보기⌄</button></div><div className="grid grid-cols-2 gap-x-5 gap-y-10 py-8 sm:grid-cols-3 lg:grid-cols-4">{list.map((product) => <ProductCard key={product.id} product={product} onAdd={onAdd} />)}</div></section></main>;
}

function Listing({ title, categorySlug, subCategory, list, attentionList = list, loading = false, onAdd }: { title: string; categorySlug: string; subCategory?: string; list: Product[]; attentionList?: Product[]; loading?: boolean; onAdd: (product: Product) => void }) {
  const [openFilters, setOpenFilters] = useState<string[]>(["브랜드", "가격"]);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState<string | null>(null);
  const [sort, setSort] = useState<"낮은가격순" | "높은가격순" | null>(null);
  const [catalogPage, setCatalogPage] = useState(1);
  const [categoryTransitioning, setCategoryTransitioning] = useState(false);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const kitchen = categorySlug === "kitchen";
  const category = staticCategoryTree.find((item) => item.slug === categorySlug);
  const categoryLabel = category?.name || title;
  const categoryChildren = category?.children.map((item) => item.name) || [];
  const categoryDetail = category?.children.find((item) => item.name === categoryOpen)?.children.map((item) => item.name) || [];
  const activeCategoryParent = category?.children.find((item) => item.name === subCategory || item.children.some((child) => child.name === subCategory))?.name || null;
  const selectCategory = (label: string) => {
    setMobileFilterOpen(false);
    go(`/category/${categorySlug}?sub=${encodeURIComponent(label)}`);
  };
  const categoryFacetNames = categoryNameSetFromProducts(list);
  const brandOptions = Array.from(new Set(list.filter((product) => isDisplayBrandFacet(product, categoryFacetNames)).map((product) => product.brand).filter(Boolean)))
    .map((brand) => `${brand} (${list.filter((product) => product.brand === brand).length})`)
    .sort((left, right) => Number(right.match(/\((\d+)\)$/)?.[1] || 0) - Number(left.match(/\((\d+)\)$/)?.[1] || 0) || left.localeCompare(right, "ko"))
    .slice(0, 18);
  // 공개몰 필터는 DB에서 검증 가능한 값만 노출한다. 용량/도어/에너지효율 등
  // 상세 스펙은 현재 구조화 칼럼이 없어 상품명 패턴 추정이므로 숨긴다.
  const filters: Array<[string, string[]]> = [["브랜드", brandOptions], ["가격", []]];
  const displayFilters = filters.filter(([name, options]) => name !== "브랜드" || options.length > 0);
  const isFilterOpen = (name: string) => openFilters.includes(name);
  const toggleFilter = (name: string) => setOpenFilters((items) => items.includes(name) ? items.filter((item) => item !== name) : [...items, name]);
  const toggleBrand = (brand: string) => setSelectedBrands((items) => items.includes(brand) ? items.filter((item) => item !== brand) : [...items, brand]);
  const toggleFeature = (feature: string) => setSelectedFeatures((items) => items.includes(feature) ? items.filter((item) => item !== feature) : [...items, feature]);
  const minimum = Number(minPrice.replaceAll(",", "")) || 0;
  const maximum = Number(maxPrice.replaceAll(",", "")) || Number.MAX_SAFE_INTEGER;
  const featureMatches = (product: Product, feature: string) => {
    if (feature === "32형 이하") return /32(?:인치|형)/.test(product.name);
    if (feature === "40~50형") return /(?:40|42|43|49|50)(?:인치|형)/.test(product.name);
    if (feature === "55~65형") return /(?:55|60|65)(?:인치|형)/.test(product.name);
    if (feature === "70형 이상") return /(?:70|75|76|85)(?:인치|형)/.test(product.name);
    if (feature === "쇼케이스 냉장고") return product.name.includes("쇼케이스");
    if (feature === "소형 냉장고") return /미니|49리터|85리터|94L|64L/.test(product.name);
    if (/^[1-4]등급$/.test(feature)) return product.name.includes(feature);
    if (/^[1-4]$/.test(feature)) return product.name.includes(`${feature}도어`);
    if (/^[1-8]00리터대$/.test(feature)) return new RegExp(`${feature[0]}\\d{2}(?:리터|L)`).test(product.name);
    if (feature === "100리터 미만") return /(?:[1-9]\d)(?:리터|L)/.test(product.name);
    if (feature === "홈바포함") return product.name.includes("홈바");
    if (feature === "홈바미포함") return !product.name.includes("홈바");
    if (feature === "LG물류") return product.brand.includes("LG") || product.name.includes("LG물류");
    if (feature === "삼성물류") return product.brand.includes("삼성") || product.name.includes("삼성물류");
    if (feature === "전자랜드물류") return !product.brand.includes("LG") && !product.brand.includes("삼성");
    if (feature === "상냉장 하냉동") return /일반형|2도어/.test(product.name);
    if (feature === "좌냉동 우냉장") return /양문형/.test(product.name);
    if (feature === "정수기포함") return product.name.includes("정수기");
    if (feature === "정수기미포함") return !product.name.includes("정수기");
    if (feature === "양문형 냉장고") return product.name.includes("양문형");
    if (feature === "냉동고") return product.name.includes("냉동고");
    if (feature === "뚜껑형 김치냉장고") return /뚜껑형.*김치|김치.*뚜껑형/.test(product.name);
    if (feature === "스탠드형 김치냉장고") return /스탠드형.*김치|김치.*스탠드형/.test(product.name);
    return categorySlug === "tv" ? true : /일반형|냉장고/.test(product.name) && !product.name.includes("쇼케이스");
  };
  const visibleList = list.filter((product) => (selectedBrands.length === 0 || selectedBrands.some((brand) => product.brand === brand)) && (selectedFeatures.length === 0 || selectedFeatures.some((feature) => featureMatches(product, feature))) && product.price >= minimum && product.price <= maximum).slice().sort((left, right) => sort === "낮은가격순" ? left.price - right.price : sort === "높은가격순" ? right.price - left.price : 0);
  const pageSize = 20;
  const totalPages = Math.max(1, Math.ceil(visibleList.length / pageSize));
  const activePage = Math.min(catalogPage, totalPages);
  const pagedList = visibleList.slice((activePage - 1) * pageSize, activePage * pageSize);
  const changeCatalogPage = (nextPage: number) => {
    const target = Math.min(Math.max(1, nextPage), totalPages);
    setCatalogPage(target);
    window.requestAnimationFrame(() => document.getElementById("category-product-list")?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };
  useEffect(() => { setCatalogPage(1); }, [categorySlug, title, sort, selectedBrands, selectedFeatures, minPrice, maxPrice]);
  useEffect(() => { setCategoryOpen(activeCategoryParent); }, [activeCategoryParent]);
  useEffect(() => {
    setSelectedBrands([]);
    setSelectedFeatures([]);
    setMinPrice("");
    setMaxPrice("");
    setSort(null);
  }, [categorySlug, title]);
  useEffect(() => {
    setCategoryTransitioning(true);
    const timer = window.setTimeout(() => setCategoryTransitioning(false), 1000);
    return () => window.clearTimeout(timer);
  }, [categorySlug, title, subCategory]);
  if (!list.length && loading) return <main className="relative mx-auto grid max-w-[1280px] grid-cols-[minmax(0,1fr)] gap-x-[60px] px-4 pb-7 pt-3 sm:px-5 lg:grid-cols-[220px_1fr] lg:px-0 lg:pt-[18px]"><aside className="hidden pt-[50px] lg:block"><h2 className="border-b border-stone-700 pb-4 text-xl font-black">{categoryLabel}</h2><div className="mt-4 grid gap-3">{Array.from({ length: 8 }, (_, index) => <span key={index} className="h-10 animate-pulse rounded bg-stone-100" />)}</div></aside><section className="min-w-0"><div className="mt-3 border-b border-stone-500 pb-3 sm:mt-7"><h1 className="text-[25px] font-black tracking-[-.06em] sm:text-[30px]">{title}</h1></div><div className="mt-12"><ListingSkeletonGrid count={8} className="grid-cols-2 sm:grid-cols-3 lg:grid-cols-4" /></div></section></main>;
  if (!list.length) return <main className="mx-auto grid min-h-[520px] max-w-[1280px] place-items-center px-5 text-center"><div><h1 className="text-2xl font-black">현재 등록된 상품이 없습니다.</h1><p className="mt-3 text-sm text-stone-500">관리자에서 상품을 등록하고 공개하면 이 카테고리에 노출됩니다.</p><button onClick={() => go("/")} className="mt-6 rounded-xl bg-hajin-600 px-5 py-3 text-sm font-bold text-white">홈으로 돌아가기</button></div></main>;
  return <main className="relative mx-auto grid max-w-[1280px] grid-cols-[minmax(0,1fr)] gap-x-[60px] px-4 pb-7 pt-3 sm:px-5 lg:grid-cols-[220px_1fr] lg:px-0 lg:pt-[18px]"><p className="absolute left-0 top-[25px] z-10 hidden text-sm text-stone-500 lg:block">홈 &gt; {categoryLabel} &gt; <strong className="text-stone-800">{title}</strong></p><span aria-hidden className="absolute left-[280px] top-[18px] z-10 hidden h-5 w-[410px] bg-white lg:block" />
    <aside className="hidden pt-[50px] lg:block">
      <h2 className="border-b border-stone-700 pb-4 text-xl font-black">{categoryLabel}</h2>
      {categoryChildren.map((label) => {
        const item = category?.children.find((child) => child.name === label);
        const details = item?.children.map((child) => child.name) || [];
        const active = subCategory === label || activeCategoryParent === label;
        return <div key={label} className="border-b border-stone-300"><button onClick={() => details.length ? setCategoryOpen(categoryOpen === label ? null : label) : selectCategory(label)} className={`flex w-full items-center justify-between py-4 text-left font-bold transition ${active ? "text-hajin-600" : "hover:text-hajin-600"}`}>{label}<span>{details.length ? categoryOpen === label ? "−" : "＋" : "›"}</span></button>{categoryOpen === label && details.length ? <div className="grid gap-2 pb-4 text-sm text-stone-600">{details.map((detail) => <button key={detail} onClick={() => selectCategory(detail)} className={`text-left transition hover:text-hajin-600 ${subCategory === detail ? "font-bold text-hajin-600" : ""}`}>{detail}</button>)}</div> : null}</div>;
      })}
      <div className="mt-6">
        {displayFilters.map(([name, options]) => <section key={name} className="border-b border-stone-400 py-4">
          <button onClick={() => toggleFilter(name)} className="flex w-full items-center justify-between text-left font-bold">{name}<span>{isFilterOpen(name) ? "−" : "＋"}</span></button>
          {isFilterOpen(name) ? <div className="grid gap-3 pt-5 text-sm">
            {name === "가격" ? <><label className="flex cursor-pointer items-center gap-2"><input checked={!minPrice && !maxPrice} onChange={() => { setMinPrice(""); setMaxPrice(""); }} name="price-all" type="radio" className="size-4 accent-stone-900" />전체</label><div className="flex items-center gap-2"><input value={minPrice} onChange={(event) => setMinPrice(event.target.value.replace(/[^0-9]/g, ""))} aria-label="최소 가격" className="w-[82px] border border-stone-300 px-2 py-2" inputMode="numeric" /><span>~</span><input value={maxPrice} onChange={(event) => setMaxPrice(event.target.value.replace(/[^0-9]/g, ""))} aria-label="최대 가격" className="w-[82px] border border-stone-300 px-2 py-2" inputMode="numeric" /><button type="button" aria-label="가격 검색" className="grid size-8 place-items-center border border-stone-400 text-lg">⌕</button></div></> : options.map((option) => {
              const brand = option.replace(/\s*\(\d+\)$/, "");
              const isBrand = name === "브랜드";
              return <label key={option} className="flex cursor-pointer items-center gap-2"><input checked={isBrand ? selectedBrands.includes(brand) : selectedFeatures.includes(option)} onChange={() => isBrand ? toggleBrand(brand) : toggleFeature(option)} type="checkbox" className="size-4 accent-stone-900" />{option}</label>;
            })}
            {name === "브랜드" ? <button className="mt-2 border border-stone-300 py-3 text-sm">브랜드 더보기</button> : null}
          </div> : null}
        </section>)}
      </div>
    </aside>
    <div className="lg:hidden">
      <button onClick={() => setMobileFilterOpen((open) => !open)} className="mt-2 flex w-full items-center justify-between rounded-lg border border-stone-300 bg-white px-4 py-3 text-sm font-bold shadow-sm">상세 필터 <span>{mobileFilterOpen ? "−" : "＋"}</span></button>
      {mobileFilterOpen ? <div className="mt-2 rounded-xl border border-stone-200 bg-stone-50 px-4 py-3">{displayFilters.map(([name, options]) => <section key={name} className="py-3"><h2 className="text-sm font-bold">{name}</h2>{name === "가격" ? <div className="mt-3 flex items-center gap-2"><input value={minPrice} onChange={(event) => setMinPrice(event.target.value.replace(/[^0-9]/g, ""))} aria-label="최소 가격" inputMode="numeric" placeholder="최소" className="min-w-0 flex-1 rounded-md border border-stone-300 px-3 py-2 text-sm" /><span>~</span><input value={maxPrice} onChange={(event) => setMaxPrice(event.target.value.replace(/[^0-9]/g, ""))} aria-label="최대 가격" inputMode="numeric" placeholder="최대" className="min-w-0 flex-1 rounded-md border border-stone-300 px-3 py-2 text-sm" /></div> : <div className="mt-3 flex max-h-[118px] flex-wrap gap-x-4 gap-y-3 overflow-hidden">{options.map((option) => { const brand = option.replace(/\s*\(\d+\)$/, ""); const isBrand = name === "브랜드"; return <label key={option} className="flex max-w-full items-center gap-2 text-sm"><input checked={isBrand ? selectedBrands.includes(brand) : selectedFeatures.includes(option)} onChange={() => isBrand ? toggleBrand(brand) : toggleFeature(option)} type="checkbox" className="size-4 shrink-0 accent-stone-900" /><span className="truncate">{option}</span></label>; })}</div>}</section>)}</div> : null}
    </div>
    <section className="min-w-0"><p className="hidden text-sm text-stone-500 lg:block">홈 &gt; {categoryLabel} &gt; <strong className="text-stone-800">{title}</strong></p><div className="mt-3 flex flex-col items-start gap-1 border-b border-stone-500 pb-3 sm:mt-7 sm:flex-row sm:items-end sm:gap-3"><h1 className="text-[25px] font-black tracking-[-.06em] sm:text-[30px]">{title}</h1><span className="pb-0 text-sm text-stone-400 sm:pb-1"><strong className="text-rose-600">{visibleList.length}</strong>개의 상품</span></div>
      <section className="mt-[56px] hidden md:block"><h2 className="text-xl font-black">다른 고객님들이 많이 본 상품</h2>{categoryTransitioning ? <ListingSkeletonGrid count={4} className="mt-4 grid-cols-4" /> : <div className="mt-4 grid grid-cols-2 gap-5 md:grid-cols-4">{attentionList.slice(0, 4).map((product) => <article key={product.id}><button onClick={() => go(`/products/${product.slug}`)} className="block w-full"><ProductVisual product={product} /></button><button onClick={() => go(`/products/${product.slug}`)} className="mt-3 line-clamp-2 text-left text-sm leading-5">{product.name}</button><p className="mt-2 text-xl font-black">{product.discount ? <span className="mr-1 text-rose-600">{product.discount}%</span> : null}{money(product.price)}<small className="ml-0.5 text-sm">원</small></p>{product.benefits?.length ? <BenefitLines product={product} /> : null}</article>)}</div>}</section>
      <div className="mt-7 flex items-center gap-x-3 overflow-x-auto border-b border-stone-300 pb-3 text-sm [scrollbar-width:none] md:mt-[80px] [&::-webkit-scrollbar]:hidden"><strong className="shrink-0">가격 정렬</strong><button type="button" onClick={() => setSort("낮은가격순")} className={`shrink-0 ${sort === "낮은가격순" ? "font-bold text-stone-900" : "text-stone-400 hover:text-stone-900"}`}>낮은가격순</button><span className="shrink-0 text-stone-300">|</span><button type="button" onClick={() => setSort("높은가격순")} className={`shrink-0 ${sort === "높은가격순" ? "font-bold text-stone-900" : "text-stone-400 hover:text-stone-900"}`}>높은가격순</button>{sort ? <><span className="shrink-0 text-stone-300">|</span><button type="button" onClick={() => setSort(null)} className="shrink-0 text-xs text-stone-500 underline underline-offset-4">정렬 해제</button></> : null}</div>
      <div id="category-product-list" className="scroll-mt-32 py-6 sm:py-8">{categoryTransitioning ? <ListingSkeletonGrid count={8} className="grid-cols-2 sm:grid-cols-3 lg:grid-cols-4" /> : <div className="grid grid-cols-2 gap-x-3 gap-y-8 sm:grid-cols-3 sm:gap-x-5 sm:gap-y-10 lg:grid-cols-4">{visibleList.length ? pagedList.map((product) => <ProductCard key={product.id} product={product} onAdd={onAdd} />) : <p className="col-span-full py-16 text-center text-sm text-stone-400">선택한 조건에 맞는 상품이 없습니다.</p>}</div>}</div>
      {!categoryTransitioning && visibleList.length ? <CategoryListingTail page={activePage} totalPages={totalPages} onPageChange={changeCatalogPage} /> : null}
    </section>
  </main>;
}

function ListingSkeletonGrid({ count = 8, className = "" }: { count?: number; className?: string }) {
  return <div aria-label="상품을 불러오는 중" className={`grid gap-x-3 gap-y-8 sm:gap-x-5 sm:gap-y-10 ${className}`}>{Array.from({ length: count }, (_, index) => <article key={index} className="animate-pulse"><div className="aspect-square rounded-t-xl bg-stone-100" /><div className="mt-4 h-4 rounded bg-stone-100" /><div className="mt-2 h-4 w-4/5 rounded bg-stone-100" /><div className="mt-4 h-6 w-2/3 rounded bg-stone-100" /><div className="mt-3 space-y-2"><span className="block h-3 w-1/2 rounded bg-stone-100" /><span className="block h-3 w-2/5 rounded bg-stone-100" /></div></article>)}</div>;
}

function CategoryListingTail({ page, totalPages, onPageChange }: { page: number; totalPages: number; onPageChange: (page: number) => void }) {
  const popularBrands = [
    { label: "LG전자", directoryName: "LG전자" },
    { label: "캐리어", directoryName: "Carrier" },
    { label: "하이얼", directoryName: "Haier" },
    { label: "미닉스", directoryName: "minix" },
    { label: "삼성전자", directoryName: "SAMSUNG" },
  ];
  const groupStart = Math.floor((page - 1) / 10) * 10 + 1;
  const pageNumbers = Array.from({ length: Math.min(10, totalPages - groupStart + 1) }, (_, index) => groupStart + index);
  return <section className="pb-14 pt-10 sm:pb-[103px] sm:pt-[60px]">
    {totalPages > 1 ? <nav aria-label="상품 목록 페이지" className="flex h-8 items-center justify-center gap-1 text-xs text-stone-500"><button type="button" onClick={() => onPageChange(1)} disabled={page === 1} className="grid size-6 place-items-center border border-stone-300 disabled:cursor-not-allowed disabled:opacity-35" aria-label="첫 페이지"><svg viewBox="0 0 20 20" className="size-3" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="m12 4-6 6 6 6M17 4l-6 6 6 6" /></svg></button><button type="button" onClick={() => onPageChange(Math.max(1, groupStart - 10))} disabled={groupStart === 1} className="grid size-6 place-items-center border border-stone-300 disabled:cursor-not-allowed disabled:opacity-35" aria-label="이전 페이지 그룹"><svg viewBox="0 0 20 20" className="size-3" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="m12.5 4-6 6 6 6" /></svg></button>{pageNumbers.map((number) => <button type="button" key={number} onClick={() => onPageChange(number)} aria-current={number === page ? "page" : undefined} className={`grid size-6 place-items-center border ${number === page ? "border-stone-900 bg-stone-900 font-bold text-white" : "border-stone-300 hover:bg-stone-50"}`}>{number}</button>)}<button type="button" onClick={() => onPageChange(Math.min(totalPages, groupStart + 10))} disabled={groupStart + 10 > totalPages} className="grid size-6 place-items-center border border-stone-300 disabled:cursor-not-allowed disabled:opacity-35" aria-label="다음 페이지 그룹"><svg viewBox="0 0 20 20" className="size-3" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="m7.5 4 6 6-6 6" /></svg></button><button type="button" onClick={() => onPageChange(totalPages)} disabled={page === totalPages} className="grid size-6 place-items-center border border-stone-300 disabled:cursor-not-allowed disabled:opacity-35" aria-label="마지막 페이지"><svg viewBox="0 0 20 20" className="size-3" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="m3 4 6 6-6 6M8 4l6 6-6 6" /></svg></button></nav> : null}
    <div className="mt-10 sm:mt-[68px]"><h2 className="text-lg font-black tracking-[-.05em]">인기 브랜드</h2><div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">{popularBrands.map(({ label, directoryName }) => { const brand = brandDirectory.find((item) => item.name === directoryName); return <button key={label} onClick={() => go(`/search?q=${encodeURIComponent(label)}`)} className="grid h-[96px] place-items-center rounded-xl bg-stone-50 p-4 transition hover:bg-stone-100 sm:h-[112px] sm:p-5"><img src={brand?.image} alt={label} loading="lazy" className="max-h-10 max-w-[120px] object-contain" /><span className="text-xs font-medium text-stone-600">{label}</span></button>; })}</div></div>
  </section>;
}

function compareSoldOutLast(left: Product, right: Product) {
  return Number(Boolean(left.soldOut)) - Number(Boolean(right.soldOut));
}

function SearchResultsLegacy({ query, list }: { query: string; list: Product[] }) {
  const [sort, setSort] = useState("전자랜드 인기순");
  const items = list.length ? list : products.slice(0, 4);
  const categories = [["에어컨/계절가전", 283], ["생활용품", 72], ["주방용품", 11], ["컴퓨터/게임기/주변기기", 3], ["가구/여행/패션", 2], ["골프/스포츠/레저", 1], ["냉풍기/이동형 에어컨", 1], ["문구/사무용품", 1], ["뷰티", 1], ["선한청소", 1]];
  const brands = ["LG전자(주)(99)", "셀러하우스(76)", "LG(48)", "삼성전자(42)", "LG전자(32)", "캐리어(24)"];
  const sortedItems = items.slice().sort(compareSoldOutLast);
  return <main className="relative mx-auto max-w-[1280px] px-0 pb-12 pt-0 lg:left-[45px]"><div className="flex items-center justify-end border-b border-stone-800 pb-12"><h1 className="shrink-0 text-[28px] font-black tracking-[-.07em]">검색 상품 <span>{query ? 376 : 0}건</span></h1></div><div className="grid gap-x-[60px] pt-4 lg:grid-cols-[220px_1fr]"><aside><h2 className="text-lg font-black">카테고리</h2><div className="mt-5 grid gap-3 text-sm font-bold">{categories.map(([name, count]) => <button key={name} className="text-left">{name} ({count})</button>)}</div><section className="mt-8 border-y border-stone-400 py-5"><button className="flex w-full items-center justify-between font-bold">브랜드 <span>⌃</span></button><div className="mt-5 grid gap-3 text-sm">{brands.map((brand) => <label key={brand} className="flex items-center gap-2"><input type="checkbox" className="size-4 accent-stone-900" /> {brand}</label>)}<button className="mt-2 border border-stone-300 py-3">브랜드 더보기</button></div></section><section className="border-b border-stone-400 py-5"><button className="flex w-full items-center justify-between font-bold">혜택 <span>⌃</span></button></section></aside><section><div className="flex flex-wrap items-center gap-x-3 gap-y-3 border-b border-stone-200 pb-4 text-sm"><strong>{sort}</strong><span className="text-stone-300">●</span>{["누적판매순", "높은 조회순", "리뷰 많은순", "낮은가격순", "높은가격순", "등록일순"].map((label) => <button key={label} onClick={() => setSort(label)} className={sort === label ? "font-bold" : "text-stone-400"}>{label}</button>)}<button className="ml-auto border border-stone-300 px-3 py-2 text-xs">40개씩 보기⌄</button></div><h2 className="mt-6 text-[26px] font-black tracking-[-.06em]">일반상품</h2>{query ? <div className="mt-[47px] grid grid-cols-2 gap-x-5 gap-y-10 lg:grid-cols-4">{sortedItems.slice(0, 4).map((product) => <SearchProductCard key={product.id} product={product} />)}</div> : <div className="mt-10 border-y border-stone-200 py-20 text-center text-stone-400">검색어를 입력해 주세요.</div>}</section></div></main>;
}

function SearchResultsLive({ query, list }: { query: string; list: Product[] }) {
  const [sort, setSort] = useState<"PRICE_ASC" | "PRICE_DESC" | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [benefitOpen, setBenefitOpen] = useState(false);
  const [selectedBenefits, setSelectedBenefits] = useState<string[]>([]);
  const [searchTransitioning, setSearchTransitioning] = useState(false);

  useEffect(() => {
    setSelectedCategory(null);
    setSelectedBrands([]);
    setSelectedBenefits([]);
  }, [query]);
  useEffect(() => {
    setSearchTransitioning(true);
    const timer = window.setTimeout(() => setSearchTransitioning(false), 1000);
    return () => window.clearTimeout(timer);
  }, [query, selectedCategory, selectedBrands, selectedBenefits, sort]);

  const toggleBrand = (brand: string) => setSelectedBrands((current) => current.includes(brand) ? current.filter((item) => item !== brand) : [...current, brand]);
  const toggleBenefit = (benefit: string) => setSelectedBenefits((current) => current.includes(benefit) ? current.filter((item) => item !== benefit) : [...current, benefit]);
  const matchesBenefits = (product: Product) => selectedBenefits.every((benefit) => benefit === "무료배송" ? (product.delivery || "무료배송") === "무료배송" : product.benefits?.some((item) => item.includes(benefit)));
  const benefitFilteredList = list.filter(matchesBenefits);
  const allCategoryFacetNames = categoryNameSetFromProducts(benefitFilteredList);
  const brandFilteredList = benefitFilteredList.filter((product) => selectedBrands.length === 0 || selectedBrands.some((brand) => product.brand === brand));
  const categoryCounts: Array<[string, number]> = [];
  const categoryIndex = new Map<string, number>();
  brandFilteredList.forEach((product) => {
    new Set(product.categoryNames || []).forEach((name) => {
      if (!name) return;
      categoryIndex.set(name, (categoryIndex.get(name) || 0) + 1);
    });
  });
  categoryIndex.forEach((count, name) => categoryCounts.push([name, count]));
  categoryCounts.sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0], "ko"));

  const categoryFilteredList = benefitFilteredList.filter((product) => !selectedCategory || new Set(product.categoryNames || []).has(selectedCategory));
  const brandCounts: Array<[string, number]> = [];
  const brandIndex = new Map<string, number>();
  categoryFilteredList.forEach((product) => {
    if (!isDisplayBrandFacet(product, allCategoryFacetNames)) return;
    if (product.brand) brandIndex.set(product.brand, (brandIndex.get(product.brand) || 0) + 1);
  });
  brandIndex.forEach((count, brand) => brandCounts.push([brand, count]));
  brandCounts.sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0], "ko"));

  const visibleList = categoryFilteredList
    .filter((product) => selectedBrands.length === 0 || selectedBrands.some((brand) => product.brand === brand))
    .filter(matchesBenefits)
    .slice()
    .sort((left, right) => compareSoldOutLast(left, right) || (sort === "PRICE_ASC" ? left.price - right.price : sort === "PRICE_DESC" ? right.price - left.price : 0));

  const categoryLabel = selectedCategory || "일반상품";
  return <main className="relative mx-auto max-w-[1280px] px-4 pb-12 pt-0 sm:px-5 lg:left-[45px] lg:px-0">
    <div className="flex flex-col items-end gap-4 border-b border-stone-800 pb-5 sm:flex-row sm:items-center sm:justify-end sm:pb-12">
      <h1 className="shrink-0 text-[23px] font-black tracking-[-.07em] sm:text-[28px]">검색 상품 <span>{visibleList.length}건</span></h1>
    </div>
    <div className="grid gap-x-[60px] pt-4 lg:grid-cols-[220px_1fr]">
      <aside className="rounded-xl bg-stone-50 p-4 lg:rounded-none lg:bg-transparent lg:p-0">
        <h2 className="text-lg font-black">카테고리</h2>
        <div className="mt-4 grid grid-cols-2 gap-1 text-sm font-bold sm:grid-cols-3 lg:mt-5 lg:grid-cols-1">
          <button onClick={() => setSelectedCategory(null)} className={`rounded px-2 py-2 text-left transition ${!selectedCategory ? "bg-stone-900 text-white" : "hover:bg-stone-100"}`}>전체 ({brandFilteredList.length})</button>
          {categoryCounts.length ? categoryCounts.map(([name, count]) => <button key={name} onClick={() => setSelectedCategory((current) => current === name ? null : name)} className={`rounded px-2 py-2 text-left transition ${selectedCategory === name ? "bg-stone-900 text-white" : "hover:bg-stone-100"}`} aria-pressed={selectedCategory === name}>{name} ({count})</button>) : <p className="px-2 py-2 text-stone-400">일치하는 카테고리가 없습니다.</p>}
        </div>
        {brandCounts.length ? <section className="mt-5 border-y border-stone-400 py-4 lg:mt-8 lg:py-5">
          <button className="flex w-full items-center justify-between font-bold">브랜드 <span>⌃</span></button>
          <div className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-3 lg:mt-5 lg:grid-cols-1">{brandCounts.map(([brand, count]) => <label key={brand} className="flex min-w-0 cursor-pointer items-center gap-2 truncate"><input checked={selectedBrands.includes(brand)} onChange={() => toggleBrand(brand)} type="checkbox" className="size-4 shrink-0 accent-stone-900" /> <span className="truncate">{brand} ({count})</span></label>)}{brandCounts.length > 6 ? <button className="mt-2 border border-stone-300 py-3 text-sm">브랜드 더보기</button> : null}</div>
        </section> : null}
        <section className="border-b border-stone-400 py-4 lg:py-5">
          <button onClick={() => setBenefitOpen((open) => !open)} className="flex w-full items-center justify-between font-bold">혜택 <span>{benefitOpen ? "⌃" : "⌄"}</span></button>
          {benefitOpen ? <div className="mt-5 grid gap-3 text-sm">{["쿠폰할인", "카드혜택", "무료배송"].map((benefit) => <label key={benefit} className="flex cursor-pointer items-center gap-2"><input checked={selectedBenefits.includes(benefit)} onChange={() => toggleBenefit(benefit)} type="checkbox" className="size-4 accent-stone-900" />{benefit}</label>)}</div> : null}
        </section>
      </aside>
      <section>
        <div className="flex flex-wrap items-center gap-y-2 border-b border-stone-200 pb-4 text-sm">
          <span className="mr-4 font-bold text-stone-700">가격 정렬</span>
          <button onClick={() => setSort((current) => current === "PRICE_ASC" ? null : "PRICE_ASC")} className={`px-4 ${sort === "PRICE_ASC" ? "font-bold text-stone-950" : "text-stone-400 hover:text-stone-900"}`}>낮은가격순</button>
          <span aria-hidden className="h-4 border-l border-stone-300" />
          <button onClick={() => setSort((current) => current === "PRICE_DESC" ? null : "PRICE_DESC")} className={`px-4 ${sort === "PRICE_DESC" ? "font-bold text-stone-950" : "text-stone-400 hover:text-stone-900"}`}>높은가격순</button>
          {sort ? <><span aria-hidden className="h-4 border-l border-stone-300" /><button onClick={() => setSort(null)} className="px-4 font-medium text-stone-500 hover:text-stone-950">필터 해제</button></> : null}
        </div>
        <h2 className="mt-6 text-[23px] font-black tracking-[-.06em] sm:text-[26px]">{categoryLabel} <span className="ml-2 text-base font-medium text-stone-500">{visibleList.length}개</span></h2>
        {query ? searchTransitioning ? <ListingSkeletonGrid count={8} className="mt-8 grid-cols-2 sm:mt-[78px] lg:grid-cols-4" /> : visibleList.length ? <div className="mt-8 grid grid-cols-2 gap-x-3 gap-y-8 sm:mt-[78px] sm:gap-x-5 sm:gap-y-10 lg:grid-cols-4">{visibleList.map((product) => <SearchProductCard key={product.id} product={product} />)}</div> : <div className="mt-10 border-y border-stone-200 py-20 text-center text-stone-400"><strong className="block text-stone-700">선택한 조건에 맞는 상품이 없습니다.</strong><p className="mt-2 text-sm">다른 카테고리 또는 필터를 선택해 주세요.</p></div> : <div className="mt-10 border-y border-stone-200 py-20 text-center text-stone-400">검색어를 입력해 주세요.</div>}
      </section>
    </div>
  </main>;
}

function SearchResults({ query, list }: { query: string; list: Product[] }) {
  const [sort, setSort] = useState("전자랜드 인기순");
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [benefitOpen, setBenefitOpen] = useState(false);
  const [selectedBenefits, setSelectedBenefits] = useState<string[]>([]);
  const categoryCounts = list.length ? Array.from(new Set(list.flatMap((product) => product.categoryNames || []))).map((name) => [name, list.filter((product) => product.categoryNames?.includes(name)).length]) : [];
  const brandCounts = Array.from(new Set(list.map((product) => product.brand))).map((brand) => [brand, list.filter((product) => product.brand === brand).length]);
  const resultCount = list.length;
  const toggleBrand = (brand: string) => setSelectedBrands((current) => current.includes(brand) ? current.filter((item) => item !== brand) : [...current, brand]);
  const toggleBenefit = (benefit: string) => setSelectedBenefits((current) => current.includes(benefit) ? current.filter((item) => item !== benefit) : [...current, benefit]);
  const matchesBenefits = (product: Product) => selectedBenefits.every((benefit) => benefit === "무료배송" ? (product.delivery || "무료배송") === "무료배송" : product.benefits?.some((item) => item.includes(benefit)));
  const visibleList = list.filter((product) => (selectedBrands.length === 0 || selectedBrands.some((brand) => product.brand.includes(brand))) && matchesBenefits(product)).slice().sort((left, right) => compareSoldOutLast(left, right) || (sort === "낮은가격순" ? left.price - right.price : sort === "높은가격순" ? right.price - left.price : 0));
  return <main className="relative mx-auto max-w-[1280px] px-0 pb-12 pt-0 lg:left-[45px]"><div className="flex items-center justify-end border-b border-stone-800 pb-12"><h1 className="shrink-0 text-[28px] font-black tracking-[-.07em]">검색 상품 <span>{resultCount}건</span></h1></div><div className="grid gap-x-[60px] pt-4 lg:grid-cols-[220px_1fr]"><aside><h2 className="text-lg font-black">카테고리</h2><div className="mt-5 grid gap-3 text-sm font-bold">{categoryCounts.length ? categoryCounts.map(([name, count]) => <button key={name} className="text-left">{name} ({count})</button>) : <p className="text-stone-400">일치하는 카테고리가 없습니다.</p>}</div><section className="mt-8 border-y border-stone-400 py-5"><button className="flex w-full items-center justify-between font-bold">브랜드 <span>⌃</span></button><div className="mt-5 grid gap-3 text-sm">{brandCounts.map(([brand, count]) => <label key={brand} className="flex items-center gap-2"><input checked={selectedBrands.includes(String(brand))} onChange={() => toggleBrand(String(brand))} type="checkbox" className="size-4 accent-stone-900" /> {brand} ({count})</label>)}<button className="mt-2 border border-stone-300 py-3 text-sm">브랜드 더보기</button></div></section><section className="border-b border-stone-400 py-5"><button onClick={() => setBenefitOpen((open) => !open)} className="flex w-full items-center justify-between font-bold">혜택 <span>{benefitOpen ? "⌃" : "⌄"}</span></button>{benefitOpen ? <div className="mt-5 grid gap-3 text-sm">{["쿠폰할인", "카드혜택", "무료배송"].map((benefit) => <label key={benefit} className="flex cursor-pointer items-center gap-2"><input checked={selectedBenefits.includes(benefit)} onChange={() => toggleBenefit(benefit)} type="checkbox" className="size-4 accent-stone-900" />{benefit}</label>)}</div> : null}</section></aside><section><div className="flex flex-wrap items-center gap-x-3 gap-y-3 border-b border-stone-200 pb-4 text-sm"><strong>{sort}</strong><span className="text-stone-300">●</span>{["누적판매순", "높은 조회순", "리뷰 많은순", "낮은가격순", "높은가격순", "등록일순"].map((label) => <button key={label} onClick={() => setSort(label)} className={sort === label ? "font-bold" : "text-stone-400 hover:text-stone-900"}>{label}</button>)}<button className="ml-auto border border-stone-300 px-3 py-2 text-xs">40개씩 보기⌄</button></div><h2 className="mt-6 text-[26px] font-black tracking-[-.06em]">일반상품</h2>{query ? visibleList.length ? <div className="mt-[78px] grid grid-cols-2 gap-x-5 gap-y-10 lg:grid-cols-4">{visibleList.map((product) => <SearchProductCard key={product.id} product={product} />)}</div> : <div className="mt-10 border-y border-stone-200 py-20 text-center text-stone-400"><strong className="block text-stone-700">‘{query}’ 검색 결과가 없습니다.</strong><p className="mt-2 text-sm">검색어를 다시 확인해 주세요.</p></div> : <div className="mt-10 border-y border-stone-200 py-20 text-center text-stone-400">검색어를 입력해 주세요.</div>}</section></div></main>;
}

function SearchProductCardLegacy({ product }: { product: Product }) { return <article><button onClick={() => go(`/products/${product.slug}`)} className="block w-full text-left"><ProductVisual product={product} tall /></button><button onClick={() => go(`/products/${product.slug}`)} className="mt-3 line-clamp-2 text-left text-sm leading-5">{product.name}</button><p className="mt-2 text-xl font-black"><span className="mr-1 text-rose-600">{product.discount || 0}%</span>{money(product.price)}<small className="ml-0.5 text-sm">원</small></p><p className="mt-2 text-sm text-amber-400">★★★★★ <span className="text-stone-500">4 (200)</span></p><div className="mt-2 grid gap-1 text-xs text-sky-600"><span>▣ 30,000원 쿠폰할인</span><span>▤ 25,000원 카드혜택</span><span className="text-orange-500">● 12,000P 적립</span><span className="text-stone-500">▣ 무료배송</span></div></article>; }

function SearchProductCard({ product }: { product: Product }) {
  return <article className="min-w-0"><button onClick={() => go(`/products/${product.slug}`)} className="block w-full text-left"><ProductVisual product={product} tall /></button><button onClick={() => go(`/products/${product.slug}`)} className="mt-2.5 line-clamp-2 text-left text-[13px] font-medium leading-[1.45] text-stone-800 hover:underline sm:mt-3 sm:text-sm sm:leading-5">{product.name}</button><p className="mt-2 flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 font-black"><span className="text-[16px] text-rose-600 sm:text-xl">{product.discount || 0}%</span><span className="text-[18px] tracking-tight sm:text-xl">{money(product.price)}원</span></p>{product.listPrice ? <p className="mt-0.5 text-xs text-stone-400 line-through">{money(product.listPrice)}원</p> : null}{product.benefits?.some((benefit) => benefit.startsWith("★★★★★")) ? <p className="mt-2 line-clamp-1 text-[11px] text-amber-400 sm:text-sm">{product.benefits.find((benefit) => benefit.startsWith("★★★★★"))}</p> : null}<div className="mt-2 grid gap-1 text-[11px] leading-[1.35] sm:text-xs">{product.benefits?.filter((benefit) => !benefit.startsWith("★★★★★")).slice(0, 3).map((benefit) => <span key={benefit} className={`line-clamp-1 ${benefit.startsWith("●") ? "text-orange-500" : benefit.startsWith("▣") ? "text-sky-600" : "text-stone-600"}`}>{benefit}</span>)}<span className="line-clamp-1 text-stone-500">▣ {product.delivery || "무료배송"}</span></div></article>;
}

function ProductPageLegacy({ product, onAdd }: { product: Product; onAdd: (product: Product) => void }) {
  const [quantity, setQuantity] = useState(1);
  const [optionOpen, setOptionOpen] = useState(false);
  const purchasePrice = product.salePrice || product.price;
  const price = purchasePrice * quantity;
  const related = products.filter((item) => item.id !== product.id).slice(0, 4);
  return <main className="mx-auto max-w-[1130px] px-0 pb-7 pt-[7px]">
    <div className="flex items-start justify-between"><div><p className="text-xs text-stone-500">홈 &gt; {product.brand} &gt; {product.name}</p><h1 className="mt-7 text-[24px] font-black tracking-[-.06em]">{product.name}</h1><span className="mt-2 inline-block bg-stone-100 px-2 py-1 text-sm text-stone-500">상품번호 {product.slug.toUpperCase()}</span></div><button className="mt-9 grid size-8 place-items-center rounded-full border border-stone-300 text-sm" aria-label="공유">↗</button></div>
    <div className="mt-[30px] grid gap-[100px] lg:grid-cols-[500px_1fr]"><ProductGallery product={product} /><section className="relative border-t-0 pt-6 before:absolute before:-top-8 before:left-0 before:right-0 before:border-t before:border-stone-950"><div className="grid grid-cols-[110px_1fr] gap-y-4 text-sm"><dt>할인가</dt><dd><strong className="text-lg">{money(product.salePrice || product.price)}원</strong>{product.listPrice ? <span className="ml-3 text-stone-400 line-through">{money(product.listPrice)}원</span> : null}</dd><dt className="text-rose-600">최대혜택가</dt><dd className="flex items-center gap-3"><strong className="text-[27px] text-rose-600">{product.discount || 0}%</strong><strong className="text-[27px] tracking-[-.05em]">{money(product.price)}원</strong><button className="ml-auto text-xs text-rose-600 underline">혜택 모두보기 &gt;</button></dd><dt>적립</dt><dd>최대 e.POINT 적립 <strong className="text-violet-600">3,952P</strong></dd><dt>배송비</dt><dd>{product.delivery || "무료배송"}</dd></div><div className="mt-7 border-y border-stone-200 py-8"><button className="mr-12 text-sm">카드혜택</button><button className="text-sm">카드사별 무이자 혜택 &gt;</button></div><div className="mt-5 flex h-16 items-center justify-center bg-gradient-to-r from-fuchsia-600 to-violet-500 text-lg font-bold text-white">ⓔ <span className="ml-4">APP에서 구매하면 1% 추가 적립</span></div><button onClick={() => setOptionOpen(!optionOpen)} className="mt-7 flex w-full items-center justify-between border border-stone-300 px-4 py-3 text-left text-sm">[필수] 옵션 <span>⌄</span></button>{optionOpen ? <div className="border border-t-0 border-stone-300 bg-stone-50 p-4"><button onClick={() => setOptionOpen(false)} className="flex w-full justify-between text-sm">기본 구성 <span>선택</span></button></div> : null}<div className="mt-8 flex items-center justify-between"><span className="font-bold">총 상품금액</span><div className="flex items-center gap-4"><button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="border px-2">−</button><span>{quantity}</span><button onClick={() => setQuantity(quantity + 1)} className="border px-2">＋</button><strong className="text-[28px] tracking-[-.05em]">{money(price)}원</strong></div></div><div className="mt-5 flex gap-0">{product.soldOut ? <><button disabled className="flex-1 cursor-not-allowed bg-stone-400 py-5 font-bold text-white">SOLD OUT</button><button className="flex-1 bg-black py-5 font-bold text-white">재입고 알림 신청</button></> : <><button onClick={() => { for (let index = 0; index < quantity; index += 1) onAdd(product); }} className="flex-1 bg-stone-700 py-5 font-bold text-white">장바구니</button><button onClick={() => { for (let index = 0; index < quantity; index += 1) onAdd(product); go("/order"); }} className="flex-1 bg-black py-5 font-bold text-white">바로구매</button></>}</div></section></div>
    <section className="mt-[86px]"><h2 className="text-[22px] font-black tracking-[-.05em]">이런 상품은 어때요?</h2><div className="mt-5 grid grid-cols-2 gap-5 sm:grid-cols-4">{related.map((item) => <ProductCard key={item.id} product={item} onAdd={onAdd} />)}</div></section>
    <ProductDetailTabs product={product} />
  </main>;
}

function ProductPage({
  product,
  relatedProducts,
  onAdd,
  onBuy,
}: {
  product: Product;
  relatedProducts: Product[];
  onAdd: (product: Product) => void;
  onBuy: (items: Product[]) => void;
}) {
  const [quantity, setQuantity] = useState(1);
  const [optionOpen, setOptionOpen] = useState(false);
  const [benefitOpen, setBenefitOpen] = useState(false);
  const [cartLayer, setCartLayer] = useState(false);
  const [restockOpen, setRestockOpen] = useState(false);
  const [restockComplete, setRestockComplete] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [optionNotice, setOptionNotice] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const productCategory = product.categoryNames?.[0] || "상품";
  const point =
    product.benefits
      ?.find((benefit) => benefit.includes("P 적립"))
      ?.replace("● ", "") || "3,952P";
  const price = (product.salePrice || product.price) * quantity;
  const isMarketplaceTv = product.slug === "b0405475";
  const isMedihiemReference = product.slug === "b0405505";
  const related = relatedProducts.filter((item) => item.id !== product.id).slice(0, 4);
  const optionLabel = product.optionName
    ? `[필수] ${product.optionName}`
    : "[필수] 옵션";
  const productOptions = product.options?.length ? product.options : ["단품"];
  const addToCart = () => {
    if (product.optionName && !selectedOption) {
      setOptionNotice(true);
      setOptionOpen(true);
      return false;
    }
    for (let index = 0; index < quantity; index += 1) onAdd(product);
    setCartLayer(true);
    return true;
  };
  const buyNow = () => {
    if (product.optionName && !selectedOption) {
      setOptionNotice(true);
      setOptionOpen(true);
      return;
    }
    const items = Array.from({ length: quantity }, () => product);
    onBuy(items);
    go("/order");
  };
  const copyProductUrl = async () => {
    try {
      await navigator.clipboard?.writeText(window.location.href);
    } catch {
      // Clipboard access can be unavailable on a non-secure preview origin.
    }
    setShareOpen(true);
    window.setTimeout(() => setShareOpen(false), 1800);
  };
  return (
    <main className={`relative mx-auto max-w-[1140px] px-4 pb-7 pt-[7px] sm:px-5 lg:px-0 ${isMedihiemReference ? "" : "lg:left-[5px]"}`}>
      <div className="relative flex items-start justify-between gap-4">
        <div>
          <p className="mb-4 text-xs text-stone-500 sm:absolute sm:right-0 sm:top-[8px]">
            홈 &gt; {productCategory} &gt; {product.name}
          </p>
          {product.badge || product.reviewCount ? (
            <p className="mt-8 flex items-center gap-2 text-xs">
              <span className="bg-hajin-600 px-2 py-1 text-white">
                {product.badge || "상품"}
              </span>
              {product.reviewCount ? (
                <span className="font-bold text-hajin-600">★ 5</span>
              ) : null}
              {product.reviewCount ? (
                <span className="text-stone-500">
                  리뷰 {product.reviewCount}건
                </span>
              ) : null}
            </p>
          ) : null}
          <h1
            className={`${product.badge || product.reviewCount ? "mt-3" : "mt-2 sm:mt-[53px]"} break-keep text-[21px] font-black tracking-[-.06em] sm:text-[24px]`}
          >
            {product.name}
          </h1>
          <span className="mt-1 inline-block bg-stone-100 px-2 py-1 text-sm text-stone-500">
            상품번호 {product.slug.toUpperCase()}
          </span>
        </div>
        <button
          onClick={copyProductUrl}
          className="mt-1 grid size-8 shrink-0 place-items-center overflow-hidden rounded-full border border-stone-300 text-sm sm:mt-[57px]"
          aria-label="공유"
        >
          <span aria-hidden className="relative block h-3 w-4"><span className="absolute left-0 top-[3px] h-2 w-[9px] -rotate-45 rounded-[3px] border border-stone-700" /><span className="absolute right-0 top-0 h-2 w-[9px] -rotate-45 rounded-[3px] border border-stone-700" /></span>
        </button>
      </div>
      <div className="mt-7 grid gap-10 sm:mt-[30px] lg:gap-[100px] lg:grid-cols-[500px_1fr]">
        <ProductGallery product={product} />
        <section className="relative pt-[28px] before:absolute before:left-0 before:right-0 before:top-0 before:border-t before:border-stone-950">
          <dl className="grid grid-cols-[82px_minmax(0,1fr)] gap-y-4 text-[13px] sm:grid-cols-[110px_1fr] sm:text-sm">
            <dt>할인가</dt>
            <dd>
              <strong className="text-lg">
                {money(product.salePrice || product.price)}원
              </strong>
              {product.listPrice ? (
                <span className="ml-3 text-stone-400 line-through">
                  {money(product.listPrice)}원
                </span>
              ) : null}
            </dd>
            <dt className="text-rose-600">최대혜택가</dt>
            <dd className="flex flex-wrap items-center gap-2 sm:gap-3">
              <strong className="text-[23px] text-rose-600 sm:text-[27px]">
                {product.discount || 0}%
              </strong>
              <strong className="text-[23px] tracking-[-.05em] sm:text-[27px]">
                {money(product.price)}원
              </strong>
              <button
                onClick={() => setBenefitOpen(true)}
                className="ml-auto text-xs text-rose-600 underline"
              >
                혜택 모두보기 &gt;
              </button>
            </dd>
            <dt>적립</dt>
            <dd>
              최대 e.POINT 적립{" "}
              <strong className="text-violet-600">{point}</strong>
              <button
                onClick={() => setBenefitOpen(true)}
                className="ml-4 text-xs text-rose-600 underline"
              >
                자세히보기
              </button>
            </dd>
            <dt>배송비</dt>
            <dd>{product.delivery || "무료배송"}</dd>
          </dl>
          <div className="mt-6 border-y border-stone-200 py-8">
            <button onClick={() => setBenefitOpen(true)} className="mr-12 text-sm">카드혜택</button>
            <button onClick={() => setBenefitOpen(true)} className="text-sm">카드사별 무이자 혜택 &gt;</button>
          </div>
          <button
            onClick={() => setOptionOpen((value) => !value)}
            className="mt-7 flex w-full items-center justify-between border border-stone-300 px-4 py-3 text-left text-sm"
          >
            {selectedOption || optionLabel}
            <span>⌄</span>
          </button>
          {optionOpen ? (
            <div className="border border-t-0 border-stone-300 bg-white">
              {productOptions.map((option) => (
                <button
                  key={option}
                  onClick={() => {
                    setSelectedOption(option);
                    setOptionOpen(false);
                    setOptionNotice(false);
                  }}
                  className="flex h-[38px] w-full items-center border-b border-stone-100 px-4 text-left text-sm last:border-b-0 hover:bg-stone-50"
                >
                  <span>{option}</span>
                  {selectedOption === option ? <span className="ml-auto text-xs text-hajin-600">✓</span> : null}
                </button>
              ))}
            </div>
          ) : null}
          {optionNotice ? (
            <p className="mt-2 text-xs font-bold text-rose-600">
              필수 옵션을 선택해 주세요.
            </p>
          ) : null}
          {selectedOption ? (
            <section className="mt-3 border border-stone-200 bg-stone-50 p-4">
              <div className="flex items-start justify-between gap-4 text-sm">
                <strong>{selectedOption}</strong>
                <button onClick={() => { setSelectedOption(null); setQuantity(1); }} aria-label="선택 옵션 삭제" className="-mt-1 text-xl leading-none text-stone-400">×</button>
              </div>
              <div className="mt-4 flex items-center justify-between">
                <div className="flex border border-stone-300 bg-white">
                  <button onClick={() => setQuantity(Math.max(1, quantity - 1))} disabled={quantity === 1} className="px-3 py-1 disabled:text-stone-300">−</button>
                  <span className="border-x border-stone-300 px-3 py-1 text-sm">{quantity}</span>
                  <button onClick={() => setQuantity(quantity + 1)} className="px-3 py-1">＋</button>
                </div>
                <strong className="text-lg">{money(price)}원</strong>
              </div>
            </section>
          ) : null}
          <div className="mt-8 flex items-center justify-between">
            <span className="font-bold">총 상품금액</span>
            <strong className="text-[28px] tracking-[-.05em]">
              {money(price)}원
            </strong>
          </div>
          <div className="mt-5 flex gap-0">
            {product.soldOut ? (
              <>
                <button
                  disabled
                  className="flex-1 cursor-not-allowed bg-stone-400 py-5 font-bold text-white"
                >
                  SOLD OUT
                </button>
                <button onClick={() => { setRestockComplete(false); setRestockOpen(true); }} className="flex-1 bg-black py-5 font-bold text-white">
                  재입고 알림 신청
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={buyNow}
                  className="flex-1 bg-black py-5 font-bold text-white"
                >
                  구매하기
                </button>
                <button
                  onClick={addToCart}
                  className="flex-1 border border-stone-900 bg-white py-5 font-bold text-stone-900"
                >
                  장바구니
                </button>
              </>
            )}
          </div>
        </section>
      </div>
      {isMarketplaceTv ? <MarketplaceRelatedPlan /> : isMedihiemReference ? <MedihiemRecommendedProducts products={medihiemRecommended} /> : <section className="mt-[86px]"><h2 className="text-[22px] font-black tracking-[-.05em]">이런 상품은 어때요?</h2><div className="mt-5 grid grid-cols-2 gap-5 sm:grid-cols-4">{related.map((item) => <ProductCard key={item.id} product={item} onAdd={onAdd} />)}</div></section>}
      <ProductDetailTabs product={product} />
      {cartLayer ? (
        <Layer
          onClose={() => setCartLayer(false)}
          className="grid place-items-center p-5"
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-label="장바구니 담기 완료"
            className="w-full max-w-[420px] bg-white p-7 text-center shadow-2xl"
          >
            <p className="text-2xl text-hajin-600">✓</p>
            <h2 className="mt-3 text-xl font-black">장바구니에 담았습니다.</h2>
            <p className="mt-3 line-clamp-2 text-sm text-stone-500">
              {product.name}
            </p>
            <div className="mt-7 grid grid-cols-2 gap-2">
              <button
                onClick={() => setCartLayer(false)}
                className="border border-stone-300 py-3 text-sm font-bold"
              >
                계속 쇼핑
              </button>
              <button
                onClick={() => go("/cart")}
                className="bg-stone-900 py-3 text-sm font-bold text-white"
              >
                장바구니 보기
              </button>
            </div>
          </section>
        </Layer>
      ) : null}
      {benefitOpen ? (
        <BenefitLayer product={product} onClose={() => setBenefitOpen(false)} />
      ) : null}
      {restockOpen ? (
        <Layer onClose={() => setRestockOpen(false)} className="grid place-items-center p-5">
          <form onSubmit={(event) => { event.preventDefault(); setRestockComplete(true); }} role="dialog" aria-modal="true" aria-label="재입고 알림 신청" className="w-full max-w-[480px] bg-white p-7 shadow-2xl">
            <div className="flex items-center justify-between border-b border-stone-200 pb-4"><h2 className="text-xl font-black">재입고 알림 신청</h2><button type="button" onClick={() => setRestockOpen(false)} aria-label="재입고 알림 닫기" className="text-2xl text-stone-500">×</button></div>
            {restockComplete ? <div className="py-12 text-center"><p className="text-lg font-bold">재입고 알림이 신청되었습니다.</p><p className="mt-3 text-sm text-stone-500">상품 입고 시 입력하신 연락처로 알려드리겠습니다.</p><button type="button" onClick={() => setRestockOpen(false)} className="mt-7 bg-stone-900 px-12 py-3 text-sm font-bold text-white">확인</button></div> : <><p className="mt-5 line-clamp-2 text-sm leading-6 text-stone-600">{product.name}</p><label className="mt-6 block text-sm font-bold">휴대폰 번호<input required inputMode="tel" placeholder="숫자만 입력" className="mt-2 h-12 w-full border border-stone-300 px-4 text-sm outline-none focus:border-stone-900" /></label><label className="mt-4 flex items-start gap-2 text-xs leading-5 text-stone-500"><input required type="checkbox" className="mt-0.5 size-4 accent-stone-900" />재입고 알림을 위한 개인정보 수집 및 이용에 동의합니다.</label><button className="mt-7 h-12 w-full bg-stone-900 text-sm font-bold text-white">알림 신청</button></>}
          </form>
        </Layer>
      ) : null}
      {shareOpen ? <p role="status" className="fixed bottom-8 left-1/2 z-50 -translate-x-1/2 bg-stone-900 px-5 py-3 text-sm font-bold text-white shadow-xl">URL이 복사되었습니다.</p> : null}
    </main>
  );
}

function MedihiemRecommendedProducts({ products }: { products: Product[] }) {
  return (
    <section className="mt-[88px] pb-[90px]">
      <h2 className="text-[22px] font-bold leading-[29px] tracking-[-.05em]">
        이런 상품은 어때요?
      </h2>
      <div className="mt-5 grid grid-cols-2 gap-5 lg:grid-cols-5">
        {products.map((item) => (
          <button
            key={item.id}
            onClick={() => go(`/products/${item.slug}`)}
            className="block min-w-0 text-left"
          >
            <div className="aspect-square overflow-hidden bg-[#f5f5f5]">
              <img
                src={item.image}
                alt=""
                className="size-full object-contain"
              />
            </div>
            {item.badge ? (
              <span className="mt-[15px] inline-block bg-[#222] px-2 py-[3px] text-[12px] leading-[15.6px] text-white">
                {item.badge}
              </span>
            ) : (
              <span className="mt-[15px] block h-[21.6px]" aria-hidden />
            )}
            <span className="mt-[10px] block h-[44px] overflow-hidden text-[15px] leading-[22px] text-[#222]">
              {item.name}
            </span>
            <span className="mt-[7px] flex h-[26px] items-center text-[#222]">
              <strong className="text-[20px] font-semibold leading-[26px] text-rose-600">
                {item.discount}%
              </strong>
              <strong className="ml-[5px] text-[20px] font-semibold leading-[26px]">
                {money(item.price)}
              </strong>
              <span className="ml-px mt-[5px] text-[15px] leading-[19.5px]">원</span>
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}

function MarketplaceRelatedPlan() {
  return <section className="relative -mt-[41px] w-[500px]"><h2 className="text-[17px] font-black tracking-[-.05em]">관련 기획전</h2><button onClick={() => go("/search?q=LG%EC%A0%84%EC%9E%90")} className="relative mt-7 block h-[230px] w-full overflow-hidden bg-[linear-gradient(135deg,#fb89c2,#fb7ab0_45%,#f492c5)] text-left text-white"><span className="absolute -right-4 -top-12 size-[270px] rounded-full border-[30px] border-white/20" /><span className="absolute right-[105px] top-[55px] grid size-[92px] place-items-center rounded-[22px] bg-white/90 text-3xl text-pink-400 shadow-xl">⌂</span><span className="absolute left-9 top-[81px] text-[27px] font-black tracking-[-.08em]">LG전자 다품목</span><span className="absolute bottom-8 left-9 text-sm font-bold">함께 구매하면 더 커지는 혜택</span></button></section>;
}

function BenefitLayer({
  product,
  onClose,
}: {
  product: Product;
  onClose: () => void;
}) {
  if (product.slug === "b0405505") {
    return (
      <Layer onClose={onClose} className="overflow-y-auto px-5 pb-5 pt-5" overlayClass="bg-black/60">
        <section role="dialog" aria-modal="true" aria-label="할인 혜택 모두보기" className="mx-auto min-h-[1255px] w-full max-w-[590px] bg-white">
          <header className="flex h-[66px] items-center justify-between px-[30px]">
            <h2 className="mx-auto text-[20px] font-bold tracking-[-.05em]">할인 · 혜택 모두보기</h2>
            <button onClick={onClose} aria-label="혜택 창 닫기" className="-mr-1 text-[18px] leading-none text-stone-900">×</button>
          </header>
          <div className="px-[30px] text-[14px] leading-5 text-stone-900">
            <section className="flex h-[64px] items-center justify-between rounded-[5px] border-2 border-cyan-500 px-[30px]">
              <strong>정상가</strong><strong>{money(product.listPrice || 502_200)}원</strong>
            </section>
            <section className="mt-4 overflow-hidden rounded-[5px] border-2 border-[#377bff]">
              <div className="flex h-[60px] items-center justify-between px-[30px]"><strong>즉시할인</strong><strong>167,400원</strong></div>
              <div className="mx-[30px] border-t border-stone-300" />
              <div className="h-[96px] px-[30px] pt-[18px]"><strong className="block">쿠폰할인</strong><p className="mt-[16px]">·&nbsp; <strong>5% 할인쿠폰</strong><span className="ml-5 font-bold">16,740원</span></p></div>
              <div className="flex h-[66px] items-center justify-between bg-[#edf3ff] px-[30px] text-[20px] font-bold text-[#1260e8]"><span>할인가</span><span>318,060원</span></div>
            </section>
            <section className="mt-4 overflow-hidden rounded-[5px] border-2 border-violet-600">
              <div className="flex h-[54px] items-center justify-between px-[30px]"><strong>e.POINT 최대적립 <span className="ml-1 inline-grid size-[17px] place-items-center rounded-full border border-stone-400 text-[11px] text-stone-500">i</span></strong><strong>6,362P</strong></div>
              <div className="space-y-[10px] px-[30px] pb-[23px] text-[13px]"><p className="flex justify-between"><span>· 기본적립</span><strong>3,181P</strong></p><p className="flex justify-between"><span>· <mark className="bg-violet-700 px-1 text-white">앱 구매시</mark> 추가적립 <em className="ml-1 border border-red-500 px-1 not-italic text-red-500">추천</em></span><strong>3,181P</strong></p></div>
              <div className="mx-[30px] border-t border-stone-300" />
              <div className="h-[96px] px-[30px] pt-[18px]"><strong className="block">카드혜택</strong><p className="mt-[17px]">· 하나카드 즉시할인(일반결제) <strong className="ml-4">15,000원</strong></p></div>
              <div className="flex h-[66px] items-center justify-between bg-violet-50 px-[30px] text-[20px] font-bold text-violet-700"><span>최대 혜택가</span><span>296,698원</span></div>
            </section>
            <section className="pt-4 text-[12px] leading-[19px] text-[#596575]">
              <strong className="block text-[14px] text-stone-900">하나카드 즉시할인 유의사항 (ID별 최초 1회)</strong>
              <p className="mt-1">· ※ 본 혜택은 이벤트 기간 내 ID당 1회만 적용 가능합니다.</p><p>· ※ 첫 주문 결제 시 혜택이 적용되며, 이후 추가 주문 시에는 혜택 적용이 불가합니다.</p><p>· ※ 주문은 전체 취소하신 경우 혜택 기회는 복구되나, 부분 취소 시에는 재적용이 어려울 수 있습니다.</p>
              <p className="mt-4">· -이벤트 기간 : 7/1~7/31일</p><p>· * 예산조기소진시 사전 안내없이 종료될수 있습니다.</p>
              <p className="mt-4">하나앱카드로 10만원 이상 결제 시 금액대별 즉시할인</p><p>· * 10만원 이상 결제 시 5천원 / 30만원 이상 1.5만원 / 50만원 이상 2.5만원 / 70만원 이상 3.5만원 / 100만원 이상 5만원 / 150만원 이상 7.5만원 / 200만원 이상 10만원 / 250만원 이상 12.5만원 즉시할인</p><p>· * 적용제외대상: 법인/선불/기프트/하나BC 등 일부카드 및 간편결제수단, 전자랜드페이 결제건 제외</p><p>· * 비회원/기업회원 결제건 제외</p><p>· * 최종결제금액 기준으로 혜택이 적용</p><p>· * 예산 소진 시 별도 공지없이 종료될 수 있음</p><p>· * 즉시할인 적용 시 결제금액은 카드사 승인금액으로 확인가능</p><p>· * 주문취소 또는 부분취소로 인해 할인 조건에 부합하지 않을 경우, 적용된 할인금액은 환불금액에서 차감됩니다.</p>
            </section>
          </div>
        </section>
      </Layer>
    );
  }
  const coupon =
    product.benefits?.find((benefit) => benefit.includes("쿠폰")) ||
    "쿠폰 할인";
  const point = product.benefits?.find((benefit) => benefit.includes("P 적립"))?.replace("● ", "") || "3,952P";
  return (
    <Layer onClose={onClose} className="overflow-y-auto px-5 pb-5 pt-5" overlayClass="bg-black/60">
      <section
        role="dialog"
        aria-modal="true"
        aria-label="할인 혜택 모두보기"
        className="mx-auto w-full max-w-[590px] bg-white"
      >
        <header className="flex items-center justify-between border-b border-stone-200 bg-white px-8 py-6">
          <h2 className="text-[22px] font-black tracking-[-.05em]">할인 · 혜택 모두보기</h2>
          <button
            onClick={onClose}
            className="text-2xl text-stone-500"
            aria-label="혜택 창 닫기"
          >
            ×
          </button>
        </header>
        <div className="space-y-0 px-8 pb-8 text-sm">
          <div className="flex justify-between border-b border-stone-200 py-5">
            <strong>정상가</strong>
            <span>
              {product.listPrice
                ? money(product.listPrice)
                : money(product.salePrice || product.price)}
              원
            </span>
          </div>
          <div className="border-b border-stone-200 py-5">
            <p className="flex justify-between"><strong>즉시할인</strong><span>{money((product.listPrice || product.salePrice || product.price) - (product.salePrice || product.price))}원</span></p>
            <p className="mt-4 flex justify-between">
              <strong>쿠폰할인</strong>
              <span className="text-emerald-600">
                {coupon.replace("▣ ", "")}
              </span>
            </p>
            <p className="mt-5 flex justify-between font-bold">
              <span>할인가</span>
              <span>{money(product.salePrice || product.price)}원</span>
            </p>
          </div>
          <div className="border-b border-stone-200 py-5">
            <p className="flex items-center justify-between"><strong>e.POINT 최대적립</strong><strong className="text-lg text-violet-600">{point}</strong></p>
            <div className="mt-4 grid grid-cols-2 gap-3 text-xs"><p className="border-r border-stone-200"><strong className="block text-sm text-stone-900">기본적립</strong><span className="mt-1 block text-violet-600">3,181P</span></p><p><strong className="block text-sm text-stone-900">앱 구매시 추가적립</strong><span className="mt-1 block text-violet-600">3,181P</span></p></div>
          </div>
          <div className="border-b border-stone-200 py-5">
            <p className="flex justify-between"><strong>카드혜택</strong><span className="font-bold text-rose-600">하나카드 즉시할인 15,000원</span></p>
            <p className="flex justify-between">
              <strong className="text-rose-600">최대혜택가</strong>
              <strong className="text-lg">{money(product.price)}원</strong>
            </p>
            <p className="mt-4 text-xs leading-6 text-stone-500">
              하나카드 즉시할인 유의사항: 이벤트 기간 내 ID당 최초 1회 적용됩니다. 최종 카드결제금액과 결제 조건에 따라 혜택이 달라질 수 있습니다.
            </p>
            <ul className="mt-4 space-y-2 text-xs leading-6 text-stone-500">
              <li>※ 본 혜택은 이벤트 기간 내 ID당 1회만 적용 가능합니다.</li>
              <li>※ 첫 주문 결제 시 혜택이 적용되며, 이후 추가 주문 시에는 혜택 적용이 불가합니다.</li>
              <li>※ 주문을 전체 취소하신 경우 혜택 기회는 복구되나, 부분 취소 시에는 재적용이 어려울 수 있습니다.</li>
              <li>- 이벤트 기간 : 7/1~7/31일, 예산 조기 소진 시 사전 안내 없이 종료될 수 있습니다.</li>
              <li>하나앱카드로 10만원 이상 결제 시 금액대별 즉시할인이 적용됩니다.</li>
              <li>* 적용 제외 대상: 법인·선불·기프트·하나BC 등 일부 카드 및 간편결제수단, 전자랜드페이 결제건 제외</li>
              <li>* 비회원·기업회원 결제건 제외 / 최종 카드결제금액 기준으로 혜택 적용</li>
              <li>* 주문취소 또는 부분취소로 할인 조건에 부합하지 않을 경우 적용된 할인금액은 환불금액에서 차감됩니다.</li>
            </ul>
          </div>
          <section className="border-b border-stone-200 pb-12 pt-5 text-xs leading-6 text-stone-500"><strong className="block text-sm text-stone-900">e.POINT 적립 안내</strong><p className="mt-3">구매확정 시 자동 적립됩니다. 쿠폰·포인트 사용액은 최종결제금액에서 제외되며, 최종 적립액은 주문서에서 다시 확인해 주세요.</p><ul className="mt-4 space-y-2"><li>· e.POINT는 주문 상태가 구매확정으로 변경된 후 적립됩니다.</li><li>· 주문 취소 또는 반품 시 해당 주문으로 적립된 포인트는 회수될 수 있습니다.</li><li>· 적립 예정 포인트와 실제 적립 포인트는 결제수단·쿠폰 사용 여부에 따라 달라질 수 있습니다.</li><li>· 포인트 사용 가능 여부와 유효기간은 마이페이지에서 확인할 수 있습니다.</li><li>· 이벤트성 추가 적립은 행사 조건을 충족한 주문에 한해 적용됩니다.</li><li>· 회원 등급과 서비스 정책 변경에 따라 적립 기준이 변경될 수 있습니다.</li></ul></section>
          <button
            onClick={onClose}
            className="mt-7 w-full bg-stone-900 py-4 font-bold text-white"
          >
            확인
          </button>
        </div>
      </section>
    </Layer>
  );
}

function ShareLayer({ product, onClose }: { product: Product; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const copyLink = async () => {
    try {
      await navigator.clipboard?.writeText(window.location.href);
    } catch {
      // Clipboard access can be unavailable on a non-secure preview origin.
    }
    setCopied(true);
  };
  return (
    <Layer onClose={onClose} className="grid place-items-center p-5">
      <section role="dialog" aria-modal="true" aria-label="상품 공유" className="w-full max-w-[420px] bg-white">
        <header className="flex items-center justify-between border-b border-stone-200 px-7 py-5">
          <h2 className="text-xl font-black">상품 공유</h2>
          <button onClick={onClose} className="text-2xl text-stone-500" aria-label="공유 창 닫기">×</button>
        </header>
        <div className="p-7">
          <p className="line-clamp-2 text-sm leading-6 text-stone-600">{product.name}</p>
          <div className="mt-6 grid grid-cols-3 gap-3 text-center text-xs font-bold">
            <button className="grid h-20 place-items-center rounded-full bg-[#fee500] text-lg text-stone-900" aria-label="카카오톡으로 공유">●<span className="text-xs">카카오톡</span></button>
            <button className="grid h-20 place-items-center rounded-full bg-[#03c75a] text-lg text-white" aria-label="네이버로 공유">N<span className="text-xs">네이버</span></button>
            <button onClick={copyLink} className="grid h-20 place-items-center rounded-full bg-stone-800 text-lg text-white" aria-label="링크 복사">↗<span className="text-xs">링크복사</span></button>
          </div>
          {copied ? <p className="mt-5 text-center text-sm font-bold text-emerald-600">링크를 복사했습니다.</p> : null}
        </div>
      </section>
    </Layer>
  );
}

function ProductGallery({ product }: { product: Product }) {
  const [zoom, setZoom] = useState(false);
  const marketplaceTv = product.slug === "b0405475";
  const supportsZoom = product.slug !== "b0405505";
  const images = product.galleryImages?.length
    ? product.galleryImages
    : product.image
      ? [product.image]
      : [];
  const [activeIndex, setActiveIndex] = useState(0);
  const activeImage = images[activeIndex];
  const move = (amount: number) =>
    setActiveIndex((index) => (index + amount + images.length) % images.length);
  return (
    <div>
      <div className="relative border border-stone-200">
        <button
          onClick={() => supportsZoom && !product.soldOut && setZoom(true)}
          className="relative block w-full text-left"
          aria-label={`${product.name} 이미지 크게 보기`}
        >
          <ProductVisual
            product={product}
            image={activeImage}
            showBadge={false}
            large
          />
          {product.soldOut || marketplaceTv ? (
            <span className="absolute left-0 top-0 grid w-[175px] overflow-hidden text-center text-sm font-bold text-white">
              <span className="bg-[#242424] py-2.5">5,000원 하나카드</span>
              <span className="bg-[#3457ed] py-2.5">{marketplaceTv ? "10,100" : "10,400"} 쿠폰할인</span>
            </span>
          ) : supportsZoom ? (
            <span className="absolute bottom-4 right-4 grid size-10 place-items-center rounded-full bg-white/90 text-lg shadow">
              ⌕
            </span>
          ) : null}
        </button>
        {images.length > 1 ? (
          <>
            <button
              onClick={() => move(-1)}
              className={`absolute left-0 top-1/2 grid size-[50px] -translate-y-1/2 place-items-center text-4xl font-thin ${marketplaceTv ? "bg-black/10 text-black/40" : "bg-stone-200/90"}`}
              aria-label="이전 상품 이미지"
            >
              ‹
            </button>
            <button
              onClick={() => move(1)}
              className={`absolute right-0 top-1/2 grid size-[50px] -translate-y-1/2 place-items-center text-4xl font-thin ${marketplaceTv ? "bg-black/10 text-black/40" : "bg-stone-200/90"}`}
              aria-label="다음 상품 이미지"
            >
              ›
            </button>
            <span className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-stone-900/80 px-3 py-1 text-sm text-white">
              {activeIndex + 1} / {images.length}
            </span>
          </>
        ) : null}
      </div>
      {zoom && supportsZoom ? (
        <Layer
          onClose={() => setZoom(false)}
          className="grid place-items-center bg-black/70 p-5"
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-label="상품 이미지 크게 보기"
            className="relative w-full max-w-3xl bg-white p-5"
          >
            <button
              onClick={() => setZoom(false)}
              className="absolute right-4 top-3 z-10 text-2xl text-stone-500"
              aria-label="이미지 확대 닫기"
            >
              ×
            </button>
            <ProductVisual
              product={product}
              image={activeImage}
              showBadge={false}
              large
            />
            <p className="mt-4 text-center text-sm font-bold">{product.name}</p>
          </section>
        </Layer>
      ) : null}
    </div>
  );
}

function EtlandBedDetail() {
  const images = [
    "gentice_top.jpg",
    "gentice_delivery_bn.jpg",
    "bianco_bed_01.jpg",
    "bianco_bed_02.jpg",
    "bianco_bed_03.jpg",
    "bianco_bed_04.jpg",
    "bianco_bed_05.jpg",
    "bianco_bed_06.jpg",
    "bianco_bed_07.jpg",
    "bianco_bed_08.jpg",
    "bianco_bed_09.jpg",
    "bottom_1.jpg",
    "bottom_2_3puNlow.jpg",
    "bottom_3.jpg",
    "bottom_4.jpg",
  ];
  return (
    <section className="mt-14 border-t border-stone-200">
      <div className="sticky top-[114px] z-20 grid grid-cols-4 border-b border-stone-200 bg-white">
        {["상품정보", "구매정보", "리뷰 (0)", "Q&A (0)"].map((label, index) => (
          <button
            key={label}
            className={`border-b-2 py-4 text-sm font-bold ${index === 0 ? "border-hajin-600 text-hajin-600" : "border-transparent text-stone-400"}`}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="mx-auto max-w-[1000px] py-12">
        <div className="border border-stone-200 bg-stone-50 p-6 text-sm leading-6 text-stone-600">
          <strong className="mr-10 text-stone-900">사칭 주의 안내</strong>
          <span>
            공식 쇼핑몰 외 판매자의 현금 거래 요구는 거부하고 고객센터로 신고해
            주세요.
          </span>
        </div>
        <div className="mt-8 text-center">
          {images.map((image) => (
            <img
              key={image}
              src={`/assets/etland/gentice/${image}`}
              alt="젠티스 비앙코 저상형침대 상세 정보"
              className="mx-auto block max-w-full"
              loading="lazy"
            />
          ))}
        </div>
        <section className="mt-10 border-t border-stone-300 pt-8 text-sm leading-7 text-stone-600">
          <h3 className="text-lg font-black text-stone-900">꼭 확인해주세요</h3>
          <p className="mt-3">
            제품 이미지 및 특장점은 광고적 표현을 포함할 수 있으며 실제 제품과
            차이가 있을 수 있습니다. 제품 외관과 사양은 예고 없이 변경될 수
            있습니다.
          </p>
        </section>
      </div>
    </section>
  );
}

function CarrierShowcaseDetail({ product }: { product: Product }) {
  const [tab, setTab] = useState<"info" | "buy" | "review" | "qna">("info");
  const [writer, setWriter] = useState<"review" | "qna" | null>(null);
  const tabs: Array<[typeof tab, string]> = [
    ["info", "상품설명"],
    ["buy", "구매정보"],
    ["review", "리뷰 (0)"],
    ["qna", "Q&A (0)"],
  ];
  return (
    <section className="mt-14 border-t border-stone-200">
      <div className="sticky top-[114px] z-20 grid grid-cols-4 border-b border-stone-200 bg-white">
        {tabs.map(([key, label]) => (
          <button
            key={key}
            onClick={() => {
              setTab(key);
              window.requestAnimationFrame(() => scrollToProductDetailSection(key, 122));
            }}
            className={`border-b-2 py-4 text-sm font-bold ${tab === key ? "border-hajin-600 text-hajin-600" : "border-transparent text-stone-400"}`}
          >
            {label}
          </button>
        ))}
      </div>
      <section id={productDetailSectionId("info")} className="scroll-mt-32">
        <div className="mx-auto max-w-[1000px] py-12">
          <div className="border border-stone-200 bg-stone-50 p-6 text-sm leading-6 text-stone-600">
            <strong className="mr-10 text-stone-900">사칭 주의 안내</strong>
            <span>
              공식 쇼핑몰 외 판매자의 현금 거래 요구는 거부하고 고객센터로
              신고해 주세요.
            </span>
          </div>
          <div className="mt-8 text-center">
            <img
              src="/assets/etland/carrier_service_notice.jpg"
              alt="캐리어 서비스 안내"
              className="mx-auto block max-w-full"
              loading="lazy"
            />
            <img
              src="/assets/etland/carrier_cvdr64bem1_detail.jpg"
              alt="캐리어 미니 쇼케이스 CVDR64BEM1 상세 정보"
              className="mx-auto block max-w-full"
              loading="lazy"
            />
          </div>
          <section className="mt-10 border-t border-stone-300 pt-8 text-sm leading-7 text-stone-600">
            <h3 className="text-lg font-black text-stone-900">
              꼭 확인해주세요
            </h3>
            <p className="mt-3">
              제품 이미지 및 특장점에는 광고적 표현이 포함될 수 있으며 실제
              제품과 차이가 있을 수 있습니다. 제품 사양은 예고 없이 변경될 수
              있습니다.
            </p>
          </section>
        </div>
      </section>
      <section id={productDetailSectionId("buy")} className="scroll-mt-32">
        <div className="mx-auto max-w-[1000px] py-12">
          <h3 className="text-xl font-black">상품정보제공고시</h3>
          <table className="mt-6 w-full border-collapse text-left text-sm">
            <tbody>
              {[
                ["품명 및 모델명", "캐리어 미니 쇼케이스 CVDR64BEM1"],
                ["용량", "64L"],
                ["제조자", "캐리어"],
                ["배송", "무료배송"],
                ["A/S 책임자", "캐리어 고객센터"],
              ].map(([name, value]) => (
                <tr key={name} className="border border-stone-200">
                  <th className="w-[240px] bg-stone-50 p-4">{name}</th>
                  <td className="p-4">{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section id={productDetailSectionId("review")} className="scroll-mt-32">
        <SourceReviewSection count={0} product={product} onWrite={() => setWriter("review")} />
      </section>
      <section id={productDetailSectionId("qna")} className="scroll-mt-32">
        <SourceQnaSection count={0} product={product} onWrite={() => setWriter("qna")} />
      </section>
      {writer ? <ProductFeedbackModal product={product} type={writer} onClose={() => setWriter(null)} /> : null}
    </section>
  );
}

function CarrierSlimReferenceDetail({ product }: { product: Product }) {
  const [tab, setTab] = useState<"info" | "buy" | "review" | "qna">("info");
  const [writer, setWriter] = useState<"qna" | null>(null);
  const tabs: Array<[typeof tab, string]> = [
    ["info", "상품설명"],
    ["buy", "구매정보"],
    ["review", "리뷰 6"],
    ["qna", "Q&A 6"],
  ];
  const rows = [
    ["품명 및 모델명", "KRD-T182WEH1"],
    ["KC 인증정보", "SU071531-19004"],
    ["정격전압, 소비전력", "220V / 60Hz, 29.3kWh/월"],
    ["동일모델의 출시년월", "2021년 8월"],
    ["제조사(수입자)", "캐리어"],
    ["제조국", "중국"],
    ["크기, 용량, 형태", "W500 x D580 x H1400mm, 182L, 일반냉장고"],
    ["에너지소비효율등급", "3등급"],
    ["A/S 책임자와 전화번호", "씨알케이 주식회사 1588-71000"],
  ];
  return (
    <section className="mt-14 border-t border-stone-200">
      <div className="sticky top-[114px] z-20 grid grid-cols-4 border-b border-stone-200 bg-white">
        {tabs.map(([key, label]) => (
          <button
            key={key}
            onClick={() => {
              setTab(key);
              window.requestAnimationFrame(() => scrollToProductDetailSection(key, 122));
            }}
            className={`border-b-2 py-4 text-sm font-bold ${tab === key ? "border-hajin-600 text-hajin-600" : "border-transparent text-stone-400"}`}
          >
            {label}
          </button>
        ))}
      </div>
      <section id={productDetailSectionId("info")} className="scroll-mt-32">
        <div className="mx-auto max-w-[1000px] py-12">
          <div className="border border-stone-200 bg-stone-50 p-6 text-sm leading-6 text-stone-600">
            <strong className="mr-10 text-stone-900">사칭 주의 안내</strong>
            <span>
              현재 공식 사이트 외 판매자가 현금 거래를 요구하면 거부하고
              고객센터로 신고해 주세요.
            </span>
          </div>
          <section className="mt-8 grid gap-10 border-y border-stone-200 py-10 md:grid-cols-[340px_1fr]">
            <ProductVisual product={product} large />
            <div>
              <p className="text-xs font-bold text-hajin-600">
                캐리어 클라윈드
              </p>
              <h3 className="mt-2 text-2xl font-black tracking-[-.05em]">
                슬림한 182L 2도어 냉장고
              </h3>
              <p className="mt-5 text-sm leading-7 text-stone-600">
                상단 냉동실과 하단 냉장실이 분리된 상냉동·하냉장 구조입니다.
                계란보관함과 야채실을 기본으로 갖춰 식재료를 용도별로 나누어
                보관하기 편리합니다.
              </p>
              <dl className="mt-6 grid grid-cols-[100px_1fr] gap-y-3 border-t border-stone-200 pt-5 text-sm">
                <dt className="text-stone-500">총용량</dt>
                <dd className="font-bold">182L</dd>
                <dt className="text-stone-500">크기</dt>
                <dd>W500 x D580 x H1400mm</dd>
                <dt className="text-stone-500">색상</dt>
                <dd>화이트</dd>
              </dl>
            </div>
          </section>
          <section className="border-b border-stone-200 pb-10 text-sm leading-7 text-stone-600">
            <h3 className="text-lg font-black text-stone-900">
              꼭 확인해주세요
            </h3>
            <p className="mt-3">
              제품 이미지와 특장점에는 광고적 표현이 포함될 수 있으며 실제
              제품과 차이가 있을 수 있습니다. 제품 외관과 사양은 예고 없이
              변경될 수 있습니다.
            </p>
          </section>
        </div>
      </section>
      <section id={productDetailSectionId("buy")} className="scroll-mt-32">
        <div className="mx-auto max-w-[1000px] py-12">
          <h3 className="text-xl font-black">상품정보제공고시</h3>
          <table className="mt-6 w-full border-collapse text-left text-sm">
            <tbody>
              {rows.map(([name, value]) => (
                <tr key={name} className="border border-stone-200">
                  <th className="w-[270px] bg-stone-50 p-4">{name}</th>
                  <td className="p-4">{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section id={productDetailSectionId("review")} className="scroll-mt-32">
        <div className="mx-auto max-w-[1000px] py-12">
          <div className="flex items-end gap-5 border-b border-stone-200 pb-6">
            <strong className="text-4xl">5.0</strong>
            <span className="text-amber-400">★★★★★</span>
            <span className="text-sm text-stone-500">리뷰 6건</span>
          </div>
          <article className="border-b border-stone-200 py-6 text-sm">
            <div className="flex justify-between">
              <strong>정*강</strong>
              <span className="text-amber-400">★★★★★</span>
            </div>
            <p className="mt-3 leading-6 text-stone-600">
              너무 만족합니다. 설치와 배송 안내도 친절했습니다.
            </p>
          </article>
        </div>
      </section>
      <section id={productDetailSectionId("qna")} className="scroll-mt-32">
        <EmptyPanel
          title="상품 문의를 확인해 주세요."
          detail="상품 관련 문의는 고객센터 또는 판매자 문의를 이용해 주세요."
          action="상품 문의하기"
          onAction={() => setWriter("qna")}
        />
      </section>
      {writer ? <ProductFeedbackModal product={product} type={writer} onClose={() => setWriter(null)} /> : null}
    </section>
  );
}

type ProductDetailSectionKey = "info" | "buy" | "review" | "qna";

function productDetailSectionId(key: ProductDetailSectionKey) {
  return `product-detail-${key}`;
}

function scrollToProductDetailSection(key: ProductDetailSectionKey, offset = 120) {
  const target = document.getElementById(productDetailSectionId(key));
  if (!target) return;
  const top = target.getBoundingClientRect().top + window.scrollY - offset;
  window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
}

function SourceProductDetail({ product }: { product: Product }) {
  const [tab, setTab] = useState<"info" | "buy" | "review" | "qna">("info");
  const [writer, setWriter] = useState<"review" | "qna" | null>(null);
  const [feedbackCounts, setFeedbackCounts] = useState({ review: 0, qna: 0 });
  const tabBarRef = useRef<HTMLDivElement>(null);
  const isMarketplaceTv = product.slug === "b0405475";
  const isMedihiemReference = product.slug === "b0405505";
  const reloadFeedbackCounts = useCallback(() => {
    let active = true;
    Promise.all([
      loadHajinProductReviews(product.slug).catch(() => []),
      loadHajinProductInquiries(product.slug).catch(() => []),
    ]).then(([reviews, inquiries]) => {
      if (active) setFeedbackCounts({ review: reviews.length, qna: inquiries.length });
    });
    return () => { active = false; };
  }, [product.slug]);

  useEffect(() => reloadFeedbackCounts(), [reloadFeedbackCounts]);
  useEffect(() => {
    const listener = (event: Event) => {
      const detail = (event as CustomEvent<{ slug?: string }>).detail;
      if (!detail?.slug || detail.slug === product.slug) reloadFeedbackCounts();
    };
    window.addEventListener("hajin:product-review-refresh", listener);
    window.addEventListener("hajin:product-qna-refresh", listener);
    return () => {
      window.removeEventListener("hajin:product-review-refresh", listener);
      window.removeEventListener("hajin:product-qna-refresh", listener);
    };
  }, [product.slug, reloadFeedbackCounts]);

  const reviewCount = isMarketplaceTv || isMedihiemReference ? (product.reviewCount || 0) : feedbackCounts.review;
  const questionCount = isMarketplaceTv || isMedihiemReference ? (product.questionCount || 0) : feedbackCounts.qna;
  const tabs: Array<[typeof tab, string]> = [
    ["info", "상품설명"],
    ["buy", "구매정보"],
    ["review", `리뷰${reviewCount ? ` ${reviewCount}` : isMarketplaceTv || isMedihiemReference ? " 0" : " (0)"}`],
    ["qna", `Q&A${questionCount ? ` ${questionCount}` : isMarketplaceTv || isMedihiemReference ? " 0" : " (0)"}`],
  ];
  const model =
    product.name.match(/[A-Z]{2,}[A-Z0-9-]*/)?.[0] ||
    product.slug.toUpperCase();
  const rows = product.specifications || [
    ["품명 및 모델명", model],
    ["제조자", product.brand],
    ["배송", product.delivery || "무료배송"],
    ["상품번호", product.slug.toUpperCase()],
    ["A/S 책임자", `${product.brand} 고객센터`],
  ];
  const relatedProducts = getSourceDetailRelatedProducts(product);
  const selectTab = (key: typeof tab) => {
    setTab(key);
    window.requestAnimationFrame(() => scrollToProductDetailSection(key, isMedihiemReference ? 178 : 122));
  };
  return (
    <section className={`${isMedihiemReference ? "mt-0" : "mt-14"} border-t border-stone-200`}>
      <div ref={tabBarRef} className={`sticky top-[70px] z-20 grid grid-cols-4 border-b border-stone-200 bg-white ${isMarketplaceTv ? "border-t border-stone-300" : ""}`}>
        {tabs.map(([key, label]) => (
          <button
            key={key}
            onClick={() => selectTab(key)}
            className={`border-b-2 py-5 text-sm font-bold ${isMarketplaceTv ? "border-r border-stone-300 last:border-r-0" : ""} ${tab === key ? isMarketplaceTv ? "relative -mb-px border border-stone-900 border-b-white text-stone-900" : "border-hajin-600 text-hajin-600" : "border-transparent text-stone-400"}`}
          >
            {isMarketplaceTv && (key === "review" || key === "qna") ? <>{key === "review" ? "리뷰" : "Q&A"} <span className="text-rose-600">0</span></> : label}
          </button>
        ))}
      </div>
      <section id={productDetailSectionId("info")} className="scroll-mt-32">
        <div className={`mx-auto ${isMarketplaceTv || isMedihiemReference ? "max-w-[1140px] py-0" : "max-w-[1000px] py-12"}`}>
          {!isMedihiemReference ? <div className={isMarketplaceTv ? "flex min-h-[103px] items-center bg-[#f3f3f3] px-40 text-sm leading-6 text-stone-700" : "border border-stone-200 bg-stone-50 p-6 text-sm leading-6 text-stone-600"}>
            <strong className={isMarketplaceTv ? "mr-16 shrink-0 text-[22px] font-black text-red-600" : "mr-10 text-stone-900"}>{isMarketplaceTv ? "⚠ 사칭 주의 안내" : "사칭 주의 안내"}</strong>
            <span>{isMarketplaceTv ? <>- 현재 공식 사이트 외 다른 사이트는 운영하고 있지 않습니다.<br />- 판매자의 현금 거래 요구 시, 거부하고 <strong className="text-red-600">즉시 고객센터로 신고해주세요.</strong></> : <>공식 쇼핑몰 외 판매자의 현금 거래 요구는 거부하고 고객센터로 신고해 주세요.</>}</span>
          </div> : null}
          <div className={isMarketplaceTv ? "mt-[53px] text-center" : isMedihiemReference ? "mt-[286px] text-center" : "mt-8 text-center"}>
            {product.detailImages?.map((image, index) => (
              <img
                key={image}
                src={image}
                alt={`${product.name} 상세 정보 ${index + 1}`}
                className={`mx-auto block max-w-full ${isMedihiemReference && index === 0 ? "w-full" : ""} ${isMedihiemReference && index > 0 ? "mt-[18px]" : ""} ${isMarketplaceTv && index === 1 ? "mt-9" : ""}`}
                loading="eager"
              />
            ))}
          </div>
          {isMedihiemReference ? <MedihiemReferenceTail product={product} /> : <section className="mt-10 border-t border-stone-300 pt-8 text-sm leading-7 text-stone-600">
            <h3 className="text-lg font-black text-stone-900">
              꼭 확인해주세요
            </h3>
            <p className="mt-3">
              제품 이미지와 특장점은 제조사 제공 정보를 기준으로 합니다. 실제
              제품의 외관과 사양은 예고 없이 변경될 수 있습니다.
            </p>
          </section>}
        </div>
      </section>
      <section id={productDetailSectionId("buy")} className="scroll-mt-32">
        <div className={`mx-auto ${isMarketplaceTv ? "max-w-[1130px] py-0" : "max-w-[1000px] py-12"}`}>
          {isMarketplaceTv ? (
            <>
              <div className="mt-[80px] flex min-h-[120px] items-center bg-[#f3f3f3] px-40 text-sm leading-6 text-stone-700">
                <strong className="mr-16 shrink-0 text-[22px] font-black text-red-600">⚠ 사칭 주의 안내</strong>
                <span>- 현재 공식 사이트 외 다른 사이트는 운영하고 있지 않습니다.<br />- 판매자의 현금 거래 요구 시, 거부하고 <strong className="text-red-600">즉시 고객센터로 신고해주세요.</strong></span>
              </div>
              <img src="/assets/etland/details/b0405475-store-guide.jpg" alt="입점업체 상품안내" className="mx-auto mt-[58px] block w-[1140px] max-w-full" />
            </>
          ) : null}
          <h3 className={isMarketplaceTv ? "mt-16 text-xl font-black" : "text-xl font-black"}>상품정보제공고시</h3>
          <table className="mt-6 w-full border-collapse text-left text-sm">
            <tbody>
              {rows.map(([name, value]) => (
                <tr key={name} className="border border-stone-200">
                  <th className={`bg-stone-50 p-4 ${isMarketplaceTv ? "w-[280px]" : "w-[240px]"}`}>{name}</th>
                  <td className="p-4 leading-6">{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section id={productDetailSectionId("review")} className="scroll-mt-32">
      {
        isMarketplaceTv ? (
          <MarketplaceReviewPanel product={product} />
        ) : (
          <SourceReviewSection count={reviewCount} product={product} onWrite={() => setWriter("review")} />
        )
      }
      </section>
      <section id={productDetailSectionId("qna")} className="scroll-mt-32">
      {
        isMarketplaceTv ? (
          <MarketplaceQuestionPanel product={product} />
        ) : (
          <SourceQnaSection count={questionCount} product={product} onWrite={() => setWriter("qna")} />
        )
      }
      </section>
      {!isMarketplaceTv && relatedProducts.length ? <SourceRelatedShelf products={relatedProducts} /> : null}
      {writer ? <ProductFeedbackModal product={product} type={writer} onClose={() => setWriter(null)} onSaved={() => {
        window.dispatchEvent(new CustomEvent(writer === "review" ? "hajin:product-review-refresh" : "hajin:product-qna-refresh", { detail: { slug: product.slug } }));
      }} /> : null}
    </section>
  );
}

function getSourceDetailRelatedProducts(product: Product) {
  const candidates = [
    ...catalogProducts,
    ...products,
    ...kitchenProducts,
    ...refrigeratorCategoryPageSnapshot,
    ...refrigeratorSearchSnapshot,
    ...tvCategorySnapshot,
  ];
  const unique = new Map<string, Product>();
  candidates.forEach((item) => {
    if (item.slug !== product.slug && !unique.has(item.slug)) unique.set(item.slug, item);
  });
  const productCategories = new Set(product.categoryNames || []);
  const related = [...unique.values()].filter((item) => {
    if (item.brand && product.brand && item.brand === product.brand) return true;
    return (item.categoryNames || []).some((name) => productCategories.has(name));
  });
  return related.slice(0, 4);
}

function maskedReviewName(name?: string | null) {
  const clean = (name || "구매자").trim();
  if (!clean) return "구*자";
  if (clean.length === 1) return `${clean}*`;
  if (clean.length === 2) return `${clean[0]}*`;
  return `${clean[0]}${"*".repeat(Math.max(1, clean.length - 2))}${clean[clean.length - 1]}`;
}

function SourceReviewSection({ count, product, onWrite }: { count: number; product: Product; onWrite: () => void }) {
  const [rows, setRows] = useState<HajinProductReview[] | null>(null);
  const [editingReview, setEditingReview] = useState<HajinProductReview | null>(null);
  const [actionError, setActionError] = useState("");
  const reload = useCallback(() => {
    let active = true;
    loadHajinProductReviews(product.slug)
      .then((items) => { if (active) setRows(items); })
      .catch(() => { if (active) setRows([]); });
    return () => { active = false; };
  }, [product.slug]);

  useEffect(() => reload(), [reload]);
  useEffect(() => {
    const listener = (event: Event) => {
      const detail = (event as CustomEvent<{ slug?: string }>).detail;
      if (!detail?.slug || detail.slug === product.slug) reload();
    };
    window.addEventListener("hajin:product-review-refresh", listener);
    return () => window.removeEventListener("hajin:product-review-refresh", listener);
  }, [product.slug, reload]);

  const items = rows ?? [];
  const displayCount = rows === null ? count : items.length;
  const mediaItems = items.flatMap((item) => (item.imageUrls || []).map((url) => ({ url, review: item }))).slice(0, 8);
  const visibleRows = items;
  const deleteReview = async (item: HajinProductReview) => {
    if (!window.confirm("작성한 리뷰를 삭제할까요?")) return;
    setActionError("");
    try {
      await deleteHajinProductReview(product.slug, item.id);
      setRows((current) => (current || []).filter((row) => row.id !== item.id));
      window.dispatchEvent(new CustomEvent("hajin:product-review-refresh", { detail: { slug: product.slug } }));
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "리뷰 삭제에 실패했습니다.");
    }
  };
  return (
    <div className="mx-auto max-w-[1000px] py-10">
      <div className="flex items-center justify-between border-b-2 border-stone-900 pb-3">
        <h3 className="text-lg font-black">리뷰 <span className="ml-1 text-hajin-600">{displayCount}</span></h3>
        <button type="button" onClick={onWrite} className="rounded border border-stone-300 px-4 py-2 text-xs font-bold transition hover:border-stone-900 hover:text-stone-900">리뷰 작성하기</button>
      </div>
      {rows === null ? (
        <div className="grid gap-3 py-6">
          <div className="h-28 animate-pulse rounded bg-stone-100" />
          <div className="h-20 animate-pulse rounded bg-stone-100" />
        </div>
      ) : visibleRows.length ? (
        <>
          {actionError ? <p className="mt-4 rounded-xl bg-rose-50 px-4 py-3 text-sm font-bold text-rose-600">{actionError}</p> : null}
          {mediaItems.length ? (
            <section className="py-8">
              <h4 className="text-base font-black">포토&amp;동영상 리뷰</h4>
              <div className="mt-4 grid grid-cols-4 gap-2 sm:grid-cols-8">
                {mediaItems.map(({ url, review: mediaReview }, index) => {
                  const isVideo = /\.(mp4|webm|mov)$/i.test(url);
                  return <div key={`${url}-${index}`} className="relative aspect-square overflow-hidden rounded bg-stone-100">
                    {isVideo ? <video src={url} className="h-full w-full object-cover" muted playsInline /> : <img src={url} alt={`${mediaReview.authorName} 리뷰 첨부`} className="h-full w-full object-cover" loading="lazy" />}
                    {index === 7 && mediaItems.length >= 8 ? <span className="absolute inset-0 grid place-items-center bg-black/45 text-sm font-black text-white">+ 더보기</span> : null}
                  </div>;
                })}
              </div>
            </section>
          ) : null}
          <div className="flex items-center justify-between border-b-2 border-stone-900 pb-3 pt-4">
            <h4 className="text-base font-black">전체 <span className="text-stone-400">({displayCount}건)</span></h4>
            <label className="flex items-center gap-2 text-xs font-bold text-stone-700"><input type="checkbox" readOnly checked={mediaItems.length > 0} className="size-4" /> 사진/동영상</label>
          </div>
          <div className="divide-y divide-stone-200">
            {visibleRows.map((item) => {
              const rating = Math.max(1, Math.min(5, Number(item.rating) || 5));
              return (
              <article className="py-6 text-sm" key={item.id}>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center gap-1 font-bold text-hajin-600">
                    <span aria-label={`${rating}점`}>{"★".repeat(rating)}<span className="text-stone-200">{"★".repeat(5 - rating)}</span></span>
                    <span className="text-sm text-stone-900">{rating}</span>
                  </span>
                  <strong className="text-stone-800/75 blur-[0.25px]">{maskedReviewName(item.authorName)}</strong>
                  <span className="text-xs text-stone-400">{qnaDate(item.createdAt)}</span>
                </div>
                <p className="mt-2 text-xs text-stone-500">주문 상품 : {product.name}</p>
                {item.imageUrls?.length ? <div className="mt-4 flex flex-wrap gap-2">{item.imageUrls.slice(0, 4).map((url, index) => /\.(mp4|webm|mov)$/i.test(url) ? <video key={url} src={url} controls className="h-28 w-28 rounded object-cover" /> : <img key={url} src={url} alt={`리뷰 첨부 ${index + 1}`} className="h-28 w-28 rounded object-cover" loading="lazy" />)}</div> : null}
                {item.title ? <h5 className="mt-4 font-black">{item.title}</h5> : null}
                <p className="mt-3 whitespace-pre-line leading-6 text-stone-700">{item.content}</p>
                <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-stone-200 px-3 py-1 text-xs text-stone-500">👍 0 <span>도움 됐어요!</span></div>
                {item.canManage ? (
                  <div className="mt-4 flex gap-2">
                    <button type="button" onClick={() => setEditingReview(item)} className="rounded border border-stone-300 px-3 py-1.5 text-xs font-bold transition hover:border-stone-900">수정</button>
                    <button type="button" onClick={() => void deleteReview(item)} className="rounded border border-rose-200 px-3 py-1.5 text-xs font-bold text-rose-600 transition hover:bg-rose-50">삭제</button>
                  </div>
                ) : null}
              </article>
              );
            })}
          </div>
        </>
      ) : (
        <div className="grid min-h-[160px] place-items-center border-b border-stone-200 text-center">
          <div>
            <p className="text-sm font-bold text-stone-800">아직 작성된 리뷰가 없습니다.</p>
            <p className="mt-1 text-xs text-stone-400">첫 번째 리뷰를 남겨주세요.</p>
            <button type="button" onClick={onWrite} className="mt-4 rounded border border-stone-300 px-4 py-2 text-xs font-bold transition hover:border-stone-900 hover:text-stone-900">리뷰 작성하기</button>
          </div>
        </div>
      )}
      {editingReview ? (
        <ReviewEditModal
          product={product}
          review={editingReview}
          onClose={() => setEditingReview(null)}
          onSaved={(review) => {
            setRows((current) => (current || []).map((item) => item.id === review.id ? review : item));
            setEditingReview(null);
            window.dispatchEvent(new CustomEvent("hajin:product-review-refresh", { detail: { slug: product.slug } }));
          }}
        />
      ) : null}
    </div>
  );
}

const qnaDate = (value?: string | null) => value ? new Date(value).toLocaleDateString("ko-KR", { year: "numeric", month: "2-digit", day: "2-digit" }) : "-";

function SourceQnaSection({ count, product, onWrite }: { count: number; product: Product; onWrite: () => void }) {
  const [rows, setRows] = useState<HajinProductInquiry[] | null>(null);
  const [unlockTarget, setUnlockTarget] = useState<HajinProductInquiry | null>(null);
  const [editingInquiry, setEditingInquiry] = useState<HajinProductInquiry | null>(null);
  const [unlockPassword, setUnlockPassword] = useState("");
  const [unlockError, setUnlockError] = useState("");
  const [actionError, setActionError] = useState("");
  const [unlocking, setUnlocking] = useState(false);

  const reload = useCallback(() => {
    let active = true;
    loadHajinProductInquiries(product.slug)
      .then((items) => { if (active) setRows(items); })
      .catch(() => { if (active) setRows([]); });
    return () => { active = false; };
  }, [product.slug]);

  useEffect(() => reload(), [reload]);
  useEffect(() => {
    const listener = (event: Event) => {
      const detail = (event as CustomEvent<{ slug?: string }>).detail;
      if (!detail?.slug || detail.slug === product.slug) reload();
    };
    window.addEventListener("hajin:product-inquiry-refresh", listener);
    return () => window.removeEventListener("hajin:product-inquiry-refresh", listener);
  }, [product.slug, reload]);

  const items = rows ?? [];
  const displayCount = rows === null ? count : items.length;

  const openInquiry = (item: HajinProductInquiry) => {
    if (item.isLocked) {
      setUnlockTarget(item);
      setUnlockPassword("");
      setUnlockError("");
    }
  };

  const submitUnlock = async (event: FormEvent) => {
    event.preventDefault();
    if (!unlockTarget || unlocking) return;
    setUnlocking(true);
    setUnlockError("");
    try {
      const unlocked = await unlockHajinProductInquiry(product.slug, unlockTarget.id, unlockPassword);
      setRows((current) => (current || []).map((item) => item.id === unlocked.id ? unlocked : item));
      setUnlockTarget(null);
      setUnlockPassword("");
    } catch (error) {
      setUnlockError(error instanceof Error ? error.message : "비밀번호 확인에 실패했습니다.");
    } finally {
      setUnlocking(false);
    }
  };

  const deleteInquiry = async (item: HajinProductInquiry) => {
    if (!window.confirm("작성한 상품 문의를 삭제할까요?")) return;
    setActionError("");
    try {
      await deleteHajinProductInquiry(product.slug, item.id);
      setRows((current) => (current || []).filter((row) => row.id !== item.id));
      window.dispatchEvent(new CustomEvent("hajin:product-inquiry-refresh", { detail: { slug: product.slug } }));
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "상품 문의 삭제에 실패했습니다.");
    }
  };

  return (
    <div className="mx-auto max-w-[1000px] pb-10">
      <div className="flex items-center justify-between border-b-2 border-stone-900 pb-3">
        <h3 className="text-lg font-black">Q&amp;A <span className="ml-1 text-hajin-600">{displayCount}</span></h3>
        <button type="button" onClick={onWrite} className="rounded bg-[#ff5050] px-5 py-2 text-xs font-black text-white transition hover:bg-[#e23f3f]">상품문의</button>
      </div>
      <p className="border-b border-stone-200 py-4 text-xs text-stone-500">
        ※ 상품 관련 문의사항이 아닌 반품/교환 관련 문의는 고객센터 1:1 문의를 이용해주세요.
      </p>
      {actionError ? <p className="mt-4 rounded-xl bg-rose-50 px-4 py-3 text-sm font-bold text-rose-600">{actionError}</p> : null}
      {rows === null ? (
        <div className="grid gap-3 border-b border-stone-200 py-5">
          {[0, 1].map((item) => <div key={item} className="h-12 animate-pulse rounded bg-stone-100" />)}
        </div>
      ) : items.length ? (
        <div className="divide-y divide-stone-200 border-b border-stone-200">
          {items.map((item) => (
            <article key={item.id} className="py-4 text-sm">
              <button type="button" onClick={() => openInquiry(item)} className="grid w-full grid-cols-[86px_minmax(0,1fr)_110px] items-center gap-3 text-left max-sm:grid-cols-1 max-sm:gap-1">
                <span className={`font-black ${item.status === "ANSWERED" ? "text-hajin-600" : "text-stone-500"}`}>{item.status === "ANSWERED" ? "답변완료" : "답변대기"}</span>
                <span className="min-w-0">
                  <strong className="line-clamp-1 text-stone-900">{item.isSecret ? "🔒 " : ""}{item.subject}</strong>
                  <em className="mt-1 block text-xs not-italic text-stone-400">{item.authorName} · {qnaDate(item.createdAt)}</em>
                </span>
                <span className="justify-self-end text-xs font-bold text-stone-400 max-sm:justify-self-start">{item.isLocked ? "비밀번호 확인" : "열람 가능"}</span>
              </button>
              {!item.isLocked ? (
                <div className="mt-4 grid gap-3 leading-6 text-stone-700">
                  <div className="rounded-2xl bg-stone-50 p-4">
                    {item.secretNotice ? <p className="mb-2 text-xs font-black text-hajin-600">{item.secretNotice}</p> : null}
                    <p className="whitespace-pre-wrap">{item.content}</p>
                  </div>
                  {item.answer ? (
                    <div className="ml-8 rounded-2xl border border-hajin-100 bg-white p-4 shadow-sm max-sm:ml-4">
                      <div className="flex items-center gap-2">
                        <span className="grid size-7 place-items-center rounded-full bg-hajin-600 text-xs font-black text-white">A</span>
                        <div>
                          <strong className="block text-hajin-700">관리자 답변이 등록되었습니다.</strong>
                          <time className="text-xs text-stone-400">{qnaDate(item.answeredAt)}</time>
                        </div>
                      </div>
                      <p className="mt-3 whitespace-pre-wrap text-stone-700">{item.answer}</p>
                    </div>
                  ) : null}
                  {item.canManage ? (
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setEditingInquiry(item)} className="rounded border border-stone-300 px-3 py-1.5 text-xs font-bold transition hover:border-stone-900">수정</button>
                      <button type="button" onClick={() => void deleteInquiry(item)} className="rounded border border-rose-200 px-3 py-1.5 text-xs font-bold text-rose-600 transition hover:bg-rose-50">삭제</button>
                    </div>
                  ) : null}
                </div>
              ) : item.hasAnswer ? (
                <p className="mt-3 rounded-2xl bg-hajin-50 px-4 py-3 text-sm font-bold text-hajin-700">
                  관리자 답변이 등록되었습니다. 비밀번호 확인 후 답변 내용을 볼 수 있습니다.
                </p>
              ) : null}
              {item.canManage && item.isLocked ? (
                <div className="mt-3 flex gap-2">
                  <button type="button" onClick={() => openInquiry(item)} className="rounded border border-stone-300 px-3 py-1.5 text-xs font-bold transition hover:border-stone-900">비밀번호 확인 후 수정</button>
                  <button type="button" onClick={() => void deleteInquiry(item)} className="rounded border border-rose-200 px-3 py-1.5 text-xs font-bold text-rose-600 transition hover:bg-rose-50">삭제</button>
                </div>
              ) : null}
            </article>
          ))}
        </div>
      ) : (
        <div className="grid min-h-[105px] place-items-center border-b border-stone-200 text-center text-sm text-stone-400">
          문의한 상품Q&amp;A가 없습니다.
        </div>
      )}
      {unlockTarget ? (
        <Layer onClose={() => setUnlockTarget(null)} className="grid place-items-center p-4" initialFocusSelector="[data-unlock-password]">
          <form onSubmit={submitUnlock} role="dialog" aria-modal="true" aria-label="비공개 문의 비밀번호 확인" className="w-full max-w-[420px] rounded-[24px] bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-black uppercase tracking-[.16em] text-hajin-600">Private Q&amp;A</p>
                <h4 className="mt-1 text-xl font-black">비공개 문의 확인</h4>
                <p className="mt-2 line-clamp-2 text-sm text-stone-500">{unlockTarget.subject}</p>
              </div>
              <button type="button" onClick={() => setUnlockTarget(null)} className="text-2xl text-stone-400">×</button>
            </div>
            <label className="mt-5 grid gap-2 text-sm font-bold">비밀번호
              <input data-unlock-password autoFocus type="password" value={unlockPassword} onChange={(event) => setUnlockPassword(event.target.value)} placeholder="문의 등록 시 입력한 비밀번호" className="h-12 rounded-xl border border-stone-200 px-4 outline-none focus:border-hajin-600" />
            </label>
            {unlockError ? <p className="mt-3 rounded-xl bg-rose-50 px-4 py-3 text-sm font-bold text-rose-600">{unlockError}</p> : null}
            <div className="mt-5 grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setUnlockTarget(null)} className="rounded-2xl border border-stone-200 py-3 text-sm font-bold">취소</button>
              <button disabled={unlocking} className="rounded-2xl bg-stone-900 py-3 text-sm font-bold text-white disabled:bg-stone-300">{unlocking ? "확인 중..." : "확인"}</button>
            </div>
          </form>
        </Layer>
      ) : null}
      {editingInquiry ? (
        <InquiryEditModal
          product={product}
          inquiry={editingInquiry}
          onClose={() => setEditingInquiry(null)}
          onSaved={(inquiry) => {
            setRows((current) => (current || []).map((item) => item.id === inquiry.id ? inquiry : item));
            setEditingInquiry(null);
            window.dispatchEvent(new CustomEvent("hajin:product-inquiry-refresh", { detail: { slug: product.slug } }));
          }}
        />
      ) : null}
    </div>
  );
}

function SourceRelatedShelf({ products: relatedProducts }: { products: Product[] }) {
  return (
    <section className="mx-auto max-w-[1000px] pb-16">
      <h3 className="border-b-2 border-stone-900 pb-3 text-lg font-black">같은 브랜드의 다른 상품</h3>
      <div className="mt-6 grid grid-cols-2 gap-x-6 gap-y-8 sm:grid-cols-4">
        {relatedProducts.map((item) => (
          <ProductCard key={item.slug} product={item} onAdd={() => undefined} />
        ))}
      </div>
    </section>
  );
}

function MedihiemReferenceTail({ product }: { product: Product }) {
  const specs = product.specifications || [];
  const shipping = [
    {
      group: "배송안내",
      rows: [
        [
          "배송지역",
          "전국 (단, 배송이 어려운 일부 도서/산간지역은 제외될 수 있습니다.)",
        ],
        [
          "배송비",
          "- 기본배송비는 5,000원입니다.\n- 결제 금액 기준 3만 원 이상 구입하시면 무료로 배송해 드립니다.\n- 일부 설치형 상품은 추가 설치 비용이 발생할 수 있습니다.",
        ],
        [
          "배송기간",
          "- 결제 완료일로부터 3~14일(영업일 기준)이 소요됩니다.\n- 도서/산간지역은 배송기간이 더 길어질 수 있습니다.",
        ],
        [
          "설치 상품 배송",
          "- 대형 가전은 배송 당일 기사와 방문일자 및 시간을 협의할 수 있습니다.\n- 연휴 및 공휴일 설치 가능 여부는 메이커별로 다를 수 있습니다.",
        ],
      ],
    },
    {
      group: "교환/반품안내",
      rows: [
        [
          "교환/반품이 가능한 경우",
          "① 제조사 A/S 기사로부터 불량 판정을 받았을 경우\n② 단순 변심은 제품 수령 후 7일 이내 신청할 수 있습니다.",
        ],
        [
          "교환/반품이 불가능한 경우",
          "다음 각 내용에 해당하는 경우에는 교환/반품이 불가능합니다.\n① 제품을 설치 또는 사용해 상품 가치가 훼손된 경우\n⑥ 대형 가전 배송 및 설치 후 단순 변심으로 인한 교환/반품인 경우",
        ],
      ],
    },
    {
      group: "기타",
      rows: [
        [
          "환불안내",
          "- 카드결제 환불은 취소/반품 완료 후 카드사 처리까지 4~5일(영업일 기준) 소요됩니다.\n- 현금결제 환불은 요청 계좌로 환불됩니다.",
        ],
        [
          "보상안내",
          "상품의 교환/반품 및 상품 결함 등의 보상은 소비자피해보상규정에 따라 보상 받을 수 있습니다.",
        ],
      ],
    },
  ];
  const related: Product[] = [
    { ...product, id: "mediheim-related-175", slug: "b0405505", name: "메디하임 166리터 컴프레셔3년무상AS 냉장고 MHR-175GR 화이트/실버 소형 미니 원룸 사무실 가정용", price: 296_698, discount: 41, image: "/assets/etland/B0405505_0_320.jpg" },
    { ...product, id: "mediheim-related-202", slug: "b0405506", name: "메디하임 냉동고 방문설치 MHR-202SG 화이트 가정용 사무실 소형 서랍형 미니 업소용 스탠드", price: 352_558, discount: 40, image: "/assets/etland/B0405506_0_320.jpg" },
    { ...product, id: "mediheim-related-230", slug: "b0405473", name: "메디하임 203리터 컴프레셔3년무상AS 냉장고 MHR-230GR 화이트/실버 사무실 소형 미니 원룸", price: 315_318, discount: 41, image: "/assets/etland/B0405473_0_320.jpg" },
    { ...product, id: "mediheim-related-50", slug: "b0405472", name: "메디하임 49리터 컴프레셔3년무상AS 냉장고 MHR-50G 화이트/실버 사무실 소형 원룸 미니", price: 148_516, discount: 40, image: "/assets/etland/B0405472_0_320.jpg" },
  ];
  return (
    <>
      <section className="mx-auto mt-[120px] w-[940px] max-w-full bg-stone-100 px-10 py-4 text-sm leading-6 text-stone-700">
        <h3 className="text-base font-black text-stone-900">꼭 확인해주세요</h3>
        <ol className="mt-3 grid gap-1">
          <li>① 제품 이미지와 특장점은 제조사 제공 정보를 기준으로 합니다.</li>
          <li>
            ② 모니터 및 촬영 이미지에 따라 실제 색상과 차이가 있을 수 있습니다.
          </li>
          <li>
            ③ 제품의 사양은 사용 환경에 따라 일부 차이가 있을 수 있습니다.
          </li>
        </ol>
      </section>
      <section
        id="product-buy"
        className="mx-auto mt-[277px] w-[1140px] max-w-full"
      >
        <h3 className="border-b-2 border-stone-800 pb-3 text-lg font-black">
          상품정보제공고시
        </h3>
        <p className="mt-7 text-xs text-stone-500">
          전자상거래 등에서의 상품정보제공 고시에 따라 작성되었습니다.
        </p>
        <table className="mt-8 w-full border-collapse text-left text-sm">
          <tbody>
            {specs.map(([name, value]) => (
              <tr key={name} className="border border-stone-200">
                <th className="w-[240px] bg-stone-50 px-5 py-4 font-medium text-stone-600">
                  {name}
                </th>
                <td className="px-5 py-4 leading-5 text-stone-700">{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      <section className="mx-auto mt-[92px] w-[1140px] max-w-full">
        <h3 className="border-b-2 border-stone-800 pb-3 text-lg font-black">
          배송/교환/반품/환불 안내
        </h3>
        <table className="w-full border-collapse text-left text-xs">
          <tbody>
            {shipping.flatMap(({ group, rows }) =>
              rows.map(([name, value], index) => (
                <tr
                  key={`${group}-${name}`}
                  className="border-b border-stone-200"
                >
                  <>
                    {index === 0 ? (
                      <th
                        rowSpan={rows.length}
                        className="w-[180px] border-r border-stone-200 bg-stone-50 px-4 text-center font-bold"
                      >
                        {group}
                      </th>
                    ) : null}
                  </>
                  <th className="w-[160px] border-r border-stone-200 bg-stone-50 px-5 py-8 font-medium text-stone-600">
                    {name}
                  </th>
                  <td className="whitespace-pre-line px-5 py-8 leading-6 text-stone-600">
                    {value}
                  </td>
                </tr>
              )),
            )}
          </tbody>
        </table>
      </section>
      <section
        id="product-review"
        className="mx-auto mt-[347px] w-[1140px] max-w-full"
      >
        <h3 className="border-b-2 border-stone-800 pb-3 text-lg font-black">
          리뷰
        </h3>
        <div className="grid h-[300px] place-items-center border-b border-stone-200 text-xs text-stone-400">
          아직 작성된 리뷰가 없습니다.
        </div>
      </section>
      <section id="product-qna" className="mx-auto mt-16 w-[1140px] max-w-full">
        <div className="flex items-end justify-between border-b-2 border-stone-800 pb-3">
          <h3 className="text-lg font-black">Q&amp;A</h3>
          <button
            onClick={() => go("/account")}
            className="bg-rose-500 px-5 py-2 text-xs font-bold text-white"
          >
            상품문의
          </button>
        </div>
        <div className="relative h-[165px] border-b border-stone-200 text-xs text-stone-400">
          <p className="absolute left-0 top-7 text-left">※ 상품 관련 문의사항이 아닌 반품/교환관련 등의 문의는 고객센터 1:1 문의를 이용해주세요.</p>
          <p className="absolute inset-x-0 top-[108px] text-center">등록된 상품문의가 없습니다.</p>
        </div>
      </section>
      <section className="mx-auto mt-20 w-[1140px] max-w-full pb-12">
        <h3 className="text-lg font-black">메디하임의 다른 상품</h3>
        <div className="mt-5 grid grid-cols-2 gap-5 sm:grid-cols-4">
          {related.map((item) => (
            <ProductCard key={item.id} product={item} onAdd={() => undefined} />
          ))}
        </div>
      </section>
    </>
  );
}

function MarketplaceBrandShelf({ product }: { product: Product }) {
  const items: Product[] = [
    product,
    {
      ...product,
      id: "tv-related-black-32",
      slug: "b0405475-black",
      name: "메디하임 구글 32인치 FHD TV 정품패널 1등급 티비 LE-321HSGA 블랙 스마트TV",
    },
    {
      ...product,
      id: "tv-related-white-40",
      slug: "b0405475-white-40",
      name: "메디하임 구글 40인치 FHD TV 정품패널 1등급 티비 LE-401FSGA 화이트 스마트TV",
      price: 242_646,
    },
    {
      ...product,
      id: "tv-related-black-40",
      slug: "b0405475-black-40",
      name: "메디하임 구글 40인치 FHD TV 정품패널 1등급 티비 LE-401FSGA 블랙 스마트TV",
      price: 242_646,
    },
  ];
  return (
    <section className="mx-auto mt-[90px] max-w-[1130px]">
      <h3 className="text-[20px] font-black tracking-[-.06em]">
        메디하임의 다른 상품
      </h3>
      <div className="mt-5 grid grid-cols-4 gap-5">
        {items.map((item) => (
          <article key={item.id} className="min-w-0">
            <button
              onClick={() => go(`/products/${item.slug}`)}
              className="block aspect-[1.14] w-full overflow-hidden rounded-[10px] bg-stone-100"
            >
              <img
                src={item.galleryImages?.[0] || item.image}
                alt=""
                className="size-full object-cover"
              />
            </button>
            <button
              onClick={() => go(`/products/${item.slug}`)}
              className="mt-3 line-clamp-2 text-left text-sm leading-5 tracking-[-.04em]"
            >
              {item.name}
            </button>
            <p className="mt-2 text-[19px] font-black tracking-[-.05em]">
              <span className="mr-1 text-red-500">9%</span>
              {money(item.price)}
              <small className="text-xs">원</small>
            </p>
          </article>
        ))}
      </div>
    </section>
  );
}

function MarketplaceInquiryButton({ product }: { product: Product }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="bg-[#ff5151] px-7 py-3 text-sm font-bold text-white"
      >
        상품문의
      </button>
      {open ? <ProductFeedbackModal product={product} type="qna" onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function MarketplaceReviewPanel({ product }: { product: Product }) {
  const [writer, setWriter] = useState<"review" | null>(null);
  return (
    <>
      <SourceReviewSection count={0} product={product} onWrite={() => setWriter("review")} />
      {writer ? <ProductFeedbackModal product={product} type={writer} onClose={() => setWriter(null)} /> : null}
    </>
  );
}

function MarketplaceQuestionPanel({ product }: { product: Product }) {
  const [writer, setWriter] = useState<"qna" | null>(null);
  return (
    <>
      <SourceQnaSection count={0} product={product} onWrite={() => setWriter("qna")} />
      <MarketplaceBrandShelf product={product} />
      {writer ? <ProductFeedbackModal product={product} type={writer} onClose={() => setWriter(null)} /> : null}
    </>
  );
}

function ReferenceReviewPanel({
  count,
  product,
  onWrite,
}: {
  count: number;
  product: Product;
  onWrite?: () => void;
}) {
  const review = product.reviewPreview || {
    author: "구매자",
    date: "",
    text: "상품을 만족스럽게 사용하고 있습니다.",
  };
  return (
    <div className="mx-auto max-w-[1000px] py-12">
      <div className="flex flex-wrap items-end gap-5 border-b border-stone-200 pb-6">
        <strong className="text-4xl">5.0</strong>
        <span className="text-amber-400">★★★★★</span>
        <span className="text-sm text-stone-500">리뷰 {count}건</span>
        {onWrite ? <button type="button" onClick={onWrite} className="ml-auto rounded-xl border border-stone-300 px-4 py-2 text-sm font-bold transition hover:border-hajin-600 hover:text-hajin-600">리뷰 작성하기</button> : null}
      </div>
      <article className="border-b border-stone-200 py-6 text-sm">
        <div className="flex justify-between">
          <strong>{review.author}</strong>
          <span className="text-stone-400">{review.date}</span>
          <span className="text-amber-400">★★★★★</span>
        </div>
        <p className="mt-2 text-xs text-stone-500">
          주문 상품 : {product.name}
        </p>
        <p className="mt-3 whitespace-pre-line leading-6 text-stone-700">
          {review.text}
        </p>
      </article>
    </div>
  );
}

function ReferenceQuestionPanel({
  count,
  product,
  onWrite,
}: {
  count: number;
  product: Product;
  onWrite?: () => void;
}) {
  return (
    <div className="mx-auto max-w-[1000px] py-12">
      <p className="border-b border-stone-200 pb-4 text-sm text-stone-500">
        상품 관련 문의사항이 아닌 반품/교환 관련 문의는 고객센터 1:1 문의를
        이용해 주세요.
      </p>
      <div className="border-b border-stone-200 py-5 text-sm">
        <span className="mr-4 font-bold text-hajin-600">답변완료</span>비공개
        글입니다.
        <span className="float-right text-stone-400">
          {product.questionDate || ""}
        </span>
      </div>
      <p className="mt-5 text-right text-sm text-stone-500">
        총 {count}건의 문의
      </p>
      {onWrite ? <p className="mt-5 text-right"><button type="button" onClick={onWrite} className="rounded-xl border border-stone-300 px-4 py-2 text-sm font-bold transition hover:border-hajin-600 hover:text-hajin-600">상품 문의하기</button></p> : null}
    </div>
  );
}

function FallbackProductReferenceDetail({ product }: { product: Product }) {
  const [tab, setTab] = useState<"info" | "buy" | "review" | "qna">("info");
  const [writer, setWriter] = useState<"review" | "qna" | null>(null);
  const tabs: Array<[typeof tab, string]> = [
    ["info", "상품설명"],
    ["buy", "구매정보"],
    ["review", "리뷰 (0)"],
    ["qna", "Q&A (0)"],
  ];
  const model =
    product.name.match(/[A-Z]{2,}[A-Z0-9-]*/)?.[0] ||
    product.slug.toUpperCase();
  const rows = [
    ["품명 및 모델명", model],
    ["제조자", product.brand],
    ["배송", product.delivery || "무료배송"],
    ["상품번호", product.slug.toUpperCase()],
    ["A/S 책임자와 전화번호", `${product.brand} 고객센터`],
  ];
  return (
    <section className="mt-14 border-t border-stone-200">
      <div className="sticky top-[114px] z-20 grid grid-cols-4 border-b border-stone-200 bg-white">
        {tabs.map(([key, label]) => (
          <button
            key={key}
            onClick={() => {
              setTab(key);
              window.requestAnimationFrame(() => scrollToProductDetailSection(key, 122));
            }}
            className={`border-b-2 py-4 text-sm font-bold ${tab === key ? "border-hajin-600 text-hajin-600" : "border-transparent text-stone-400"}`}
          >
            {label}
          </button>
        ))}
      </div>
      <section id={productDetailSectionId("info")} className="scroll-mt-32">
        <div className="mx-auto max-w-[1000px] py-12">
          <div className="border border-stone-200 bg-stone-50 p-6 text-sm leading-6 text-stone-600">
            <strong className="mr-10 text-stone-900">사칭 주의 안내</strong>
            <span>
              공식 쇼핑몰 외 판매자의 현금 거래 요구는 거부하고 고객센터로
              신고해 주세요.
            </span>
          </div>
          <section className="mt-8 grid gap-10 border-y border-stone-200 py-10 md:grid-cols-[340px_1fr]">
            <ProductVisual product={product} large />
            <div>
              <p className="text-xs font-bold text-hajin-600">
                {product.brand}
              </p>
              <h3 className="mt-2 text-2xl font-black tracking-[-.05em]">
                {product.name}
              </h3>
              <dl className="mt-7 grid grid-cols-[110px_1fr] gap-y-3 border-t border-stone-200 pt-5 text-sm">
                <dt className="text-stone-500">판매가</dt>
                <dd className="font-bold">
                  {money(product.salePrice || product.price)}원
                </dd>
                <dt className="text-stone-500">최대혜택가</dt>
                <dd className="font-bold text-rose-600">
                  {money(product.price)}원
                </dd>
                <dt className="text-stone-500">배송</dt>
                <dd>{product.delivery || "무료배송"}</dd>
                <dt className="text-stone-500">상품번호</dt>
                <dd>{product.slug.toUpperCase()}</dd>
              </dl>
            </div>
          </section>
          <section className="border-b border-stone-200 pb-10 text-sm leading-7 text-stone-600">
            <h3 className="text-lg font-black text-stone-900">
              꼭 확인해주세요
            </h3>
            <p className="mt-3">
              상품 이미지와 제품 정보는 판매자가 제공한 정보를 기준으로 합니다.
              실제 제품의 외관과 사양은 예고 없이 변경될 수 있습니다.
            </p>
          </section>
        </div>
      </section>
      <section id={productDetailSectionId("buy")} className="scroll-mt-32">
        <div className="mx-auto max-w-[1000px] py-12">
          <h3 className="text-xl font-black">상품정보제공고시</h3>
          <table className="mt-6 w-full border-collapse text-left text-sm">
            <tbody>
              {rows.map(([name, value]) => (
                <tr key={name} className="border border-stone-200">
                  <th className="w-[270px] bg-stone-50 p-4">{name}</th>
                  <td className="p-4">{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section id={productDetailSectionId("review")} className="scroll-mt-32">
        <SourceReviewSection count={0} product={product} onWrite={() => setWriter("review")} />
      </section>
      <section id={productDetailSectionId("qna")} className="scroll-mt-32">
        <SourceQnaSection count={0} product={product} onWrite={() => setWriter("qna")} />
      </section>
      {writer ? <ProductFeedbackModal product={product} type={writer} onClose={() => setWriter(null)} /> : null}
    </section>
  );
}

function ProductDetailTabs({ product }: { product: Product }) {
  if (product.slug === "b0000599") return <EtlandBedDetail />;
  if (product.slug === "b0405423") return <CarrierShowcaseDetail product={product} />;
  if (product.detailImages?.length)
    return <SourceProductDetail product={product} />;
  if (product.slug === "s6296247")
    return <CarrierSlimReferenceDetail product={product} />;
  return <FallbackProductReferenceDetail product={product} />;
}

function EmptyPanel({
  title,
  detail,
  action,
  onAction,
}: {
  title: string;
  detail: string;
  action: string;
  onAction?: () => void;
}) {
  return (
    <div className="py-16 text-center">
      <p className="font-bold">{title}</p>
      <p className="mt-2 text-sm text-stone-500">{detail}</p>
      <button type="button" onClick={onAction} className="mt-5 rounded-xl border border-stone-300 px-4 py-2 text-sm font-bold transition hover:border-hajin-600 hover:text-hajin-600">
        {action}
      </button>
    </div>
  );
}

function ReviewEditModal({ product, review, onClose, onSaved }: { product: Product; review: HajinProductReview; onClose: () => void; onSaved: (review: HajinProductReview) => void }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [media, setMedia] = useState<string[]>(review.imageUrls || []);
  const [uploadingMedia, setUploadingMedia] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      const result = await updateHajinProductReview(product.slug, review.id, {
        rating: Number(form.get("rating") || review.rating || 5),
        title: String(form.get("title") || ""),
        content: String(form.get("content") || ""),
        imageUrls: media,
      }) as { review?: HajinProductReview };
      if (!result.review) throw new Error("리뷰 수정 결과를 확인할 수 없습니다.");
      onSaved(result.review);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "리뷰 수정에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const uploadMedia = async (files: FileList | null) => {
    const selected = Array.from(files || []).slice(0, Math.max(0, 6 - media.length));
    if (!selected.length) return;
    setUploadingMedia(true);
    setError("");
    try {
      const urls: string[] = [];
      for (const file of selected) urls.push(await uploadHajinProductReviewMedia(file));
      setMedia((current) => [...current, ...urls].slice(0, 6));
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : "첨부 파일 업로드에 실패했습니다.");
    } finally {
      setUploadingMedia(false);
    }
  };

  return (
    <Layer onClose={onClose} className="grid place-items-center p-4">
      <form onSubmit={submit} role="dialog" aria-modal="true" aria-label="리뷰 수정" className="w-full max-w-[560px] overflow-hidden rounded-[28px] bg-white shadow-2xl">
        <header className="flex items-start justify-between border-b border-stone-100 px-6 py-5">
          <div>
            <p className="text-xs font-black uppercase tracking-[.16em] text-hajin-600">Edit Review</p>
            <h2 className="mt-1 text-2xl font-black">리뷰 수정</h2>
            <p className="mt-2 line-clamp-2 text-sm text-stone-500">{product.name}</p>
          </div>
          <button type="button" onClick={onClose} className="grid size-9 place-items-center rounded-full text-2xl text-stone-400 transition hover:bg-stone-100 hover:text-stone-900" aria-label="리뷰 수정 닫기">×</button>
        </header>
        <div className="grid gap-4 p-6">
          <label className="grid gap-2 text-sm font-bold">평점
            <select name="rating" defaultValue={String(review.rating || 5)} className="h-12 rounded-xl border border-stone-200 px-4 font-medium outline-none focus:border-hajin-600">
              {[5, 4, 3, 2, 1].map((score) => <option key={score} value={score}>{score}점</option>)}
            </select>
          </label>
          <label className="grid gap-2 text-sm font-bold">제목
            <input name="title" defaultValue={review.title || ""} maxLength={120} placeholder="리뷰 제목을 입력해 주세요." className="h-12 rounded-xl border border-stone-200 px-4 font-medium outline-none focus:border-hajin-600" />
          </label>
          <div className="grid gap-2 text-sm font-bold">사진/동영상 첨부
            <label className="grid min-h-[86px] cursor-pointer place-items-center rounded-2xl border border-dashed border-stone-300 bg-stone-50 px-4 py-5 text-center transition hover:border-hajin-600 hover:bg-hajin-50">
              <input type="file" accept="image/*,video/mp4,video/webm,video/quicktime" multiple className="sr-only" onChange={(event) => void uploadMedia(event.currentTarget.files)} />
              <span className="text-sm font-black text-stone-800">{uploadingMedia ? "업로드 중..." : "사진/동영상 추가"}</span>
              <span className="mt-1 text-xs font-medium text-stone-500">최대 6개까지 관리할 수 있습니다.</span>
            </label>
            {media.length ? <div className="grid grid-cols-6 gap-2 max-sm:grid-cols-3">{media.map((url) => {
              const isVideo = /\.(mp4|webm|mov)$/i.test(url);
              return <div key={url} className="relative aspect-square overflow-hidden rounded-xl bg-stone-100">
                {isVideo ? <video src={url} className="h-full w-full object-cover" muted playsInline /> : <img src={url} alt="리뷰 첨부 미리보기" className="h-full w-full object-cover" />}
                <button type="button" onClick={() => setMedia((current) => current.filter((item) => item !== url))} className="absolute right-1 top-1 grid size-6 place-items-center rounded-full bg-black/65 text-xs text-white">×</button>
              </div>;
            })}</div> : null}
          </div>
          <label className="grid gap-2 text-sm font-bold">내용
            <textarea required name="content" defaultValue={review.content} minLength={5} maxLength={3000} className="h-36 resize-none rounded-xl border border-stone-200 p-4 font-medium leading-6 outline-none focus:border-hajin-600" />
          </label>
          {error ? <p className="rounded-xl bg-rose-50 px-4 py-3 text-sm font-bold text-rose-600">{error}</p> : null}
          <div className="grid grid-cols-2 gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-2xl border border-stone-200 py-4 text-sm font-bold">취소</button>
            <button disabled={saving} className="rounded-2xl bg-hajin-600 py-4 text-sm font-bold text-white disabled:cursor-not-allowed disabled:bg-stone-300">{saving ? "저장 중..." : "수정 저장"}</button>
          </div>
        </div>
      </form>
    </Layer>
  );
}

function InquiryEditModal({ product, inquiry, onClose, onSaved }: { product: Product; inquiry: HajinProductInquiry; onClose: () => void; onSaved: (inquiry: HajinProductInquiry) => void }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      const result = await updateHajinProductInquiry(product.slug, inquiry.id, {
        subject: String(form.get("subject") || "상품 문의"),
        content: String(form.get("content") || ""),
        isSecret: true,
        password: String(form.get("password") || ""),
      }) as { inquiry?: HajinProductInquiry };
      if (!result.inquiry) throw new Error("문의 수정 결과를 확인할 수 없습니다.");
      onSaved(result.inquiry);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "상품 문의 수정에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layer onClose={onClose} className="grid place-items-center p-4">
      <form onSubmit={submit} role="dialog" aria-modal="true" aria-label="상품 문의 수정" className="w-full max-w-[520px] overflow-hidden rounded-[28px] bg-white shadow-2xl">
        <header className="flex items-start justify-between border-b border-stone-100 px-6 py-5">
          <div>
            <p className="text-xs font-black uppercase tracking-[.16em] text-hajin-600">Edit Q&amp;A</p>
            <h2 className="mt-1 text-2xl font-black">상품 문의 수정</h2>
            <p className="mt-2 line-clamp-2 text-sm text-stone-500">{product.name}</p>
          </div>
          <button type="button" onClick={onClose} className="grid size-9 place-items-center rounded-full text-2xl text-stone-400 transition hover:bg-stone-100 hover:text-stone-900" aria-label="상품 문의 수정 닫기">×</button>
        </header>
        <div className="grid gap-4 p-6">
          <div className="rounded-2xl border border-hajin-100 bg-hajin-50/60 px-4 py-3 text-xs leading-6 text-stone-600">
            비공개 문의로 유지됩니다. 비밀번호를 새로 입력하면 열람 비밀번호가 변경되고, 비워두면 기존 비밀번호가 유지됩니다.
          </div>
          <label className="grid gap-2 text-sm font-bold">문의 제목
            <input required name="subject" defaultValue={inquiry.subject} maxLength={120} className="h-12 rounded-xl border border-stone-200 px-4 font-medium outline-none focus:border-hajin-600" />
          </label>
          <label className="grid gap-2 text-sm font-bold">비밀번호 변경
            <input name="password" type="password" minLength={4} maxLength={20} placeholder="변경할 때만 입력" className="h-12 rounded-xl border border-stone-200 px-4 font-medium outline-none focus:border-hajin-600" />
          </label>
          <label className="grid gap-2 text-sm font-bold">내용
            <textarea required name="content" defaultValue={inquiry.content || ""} minLength={5} maxLength={3000} className="h-36 resize-none rounded-xl border border-stone-200 p-4 font-medium leading-6 outline-none focus:border-hajin-600" />
          </label>
          {error ? <p className="rounded-xl bg-rose-50 px-4 py-3 text-sm font-bold text-rose-600">{error}</p> : null}
          <div className="grid grid-cols-2 gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-2xl border border-stone-200 py-4 text-sm font-bold">취소</button>
            <button disabled={saving} className="rounded-2xl bg-hajin-600 py-4 text-sm font-bold text-white disabled:cursor-not-allowed disabled:bg-stone-300">{saving ? "저장 중..." : "수정 저장"}</button>
          </div>
        </div>
      </form>
    </Layer>
  );
}

function ProductFeedbackModal({ product, type, onClose, onSaved }: { product: Product; type: "review" | "qna"; onClose: () => void; onSaved?: () => void }) {
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [reviewMedia, setReviewMedia] = useState<string[]>([]);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const isReview = type === "review";
  const title = isReview ? "리뷰 작성" : "상품 문의";
  const description = isReview ? "구매 경험과 사용감을 남겨주세요." : "상품에 대해 궁금한 내용을 남겨주세요.";
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      if (isReview) {
        await submitHajinProductReview(product.slug, {
          rating: Number(form.get("rating") || 5),
          title: String(form.get("title") || ""),
          content: String(form.get("content") || ""),
          imageUrls: reviewMedia,
        });
        window.dispatchEvent(new CustomEvent("hajin:product-review-refresh", { detail: { slug: product.slug } }));
      } else {
        const password = String(form.get("password") || "");
        await submitHajinProductInquiry(product.slug, {
          subject: String(form.get("subject") || "상품 문의"),
          content: String(form.get("content") || ""),
          isSecret: true,
          password,
        });
        window.dispatchEvent(new CustomEvent("hajin:product-inquiry-refresh", { detail: { slug: product.slug } }));
      }
      setSubmitted(true);
      onSaved?.();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "등록 중 오류가 발생했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const uploadReviewMedia = async (files: FileList | null) => {
    const selected = Array.from(files || []).slice(0, Math.max(0, 6 - reviewMedia.length));
    if (!selected.length) return;
    setUploadingMedia(true);
    setError("");
    try {
      const urls: string[] = [];
      for (const file of selected) urls.push(await uploadHajinProductReviewMedia(file));
      setReviewMedia((current) => [...current, ...urls].slice(0, 6));
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : "첨부 파일 업로드에 실패했습니다.");
    } finally {
      setUploadingMedia(false);
    }
  };
  return (
    <Layer onClose={onClose} className="grid place-items-center p-4">
      <form onSubmit={submit} role="dialog" aria-modal="true" aria-label={title} className="w-full max-w-[560px] overflow-hidden rounded-[28px] bg-white shadow-2xl">
        <header className="flex items-start justify-between border-b border-stone-100 px-6 py-5">
          <div>
            <p className="text-xs font-black uppercase tracking-[.16em] text-hajin-600">{isReview ? "Product Review" : "Product Q&A"}</p>
            <h2 className="mt-1 text-2xl font-black">{title}</h2>
            <p className="mt-2 line-clamp-2 text-sm text-stone-500">{product.name}</p>
          </div>
          <button type="button" onClick={onClose} className="grid size-9 place-items-center rounded-full text-2xl text-stone-400 transition hover:bg-stone-100 hover:text-stone-900" aria-label={`${title} 닫기`}>×</button>
        </header>
        {submitted ? (
          <div className="px-6 py-14 text-center">
            <div className="mx-auto grid size-14 place-items-center rounded-full bg-hajin-50 text-2xl text-hajin-600">✓</div>
            <strong className="mt-5 block text-lg">{isReview ? "리뷰가 등록되었습니다." : "상품 문의가 등록되었습니다."}</strong>
            <p className="mt-2 text-sm text-stone-500">{isReview ? "남겨주신 리뷰는 상품 상세에 반영됩니다." : "답변은 마이페이지 문의 내역에서 확인할 수 있습니다."}</p>
            <button type="button" onClick={onClose} className="mt-7 w-full rounded-2xl bg-stone-900 py-4 text-sm font-bold text-white">확인</button>
          </div>
        ) : (
          <div className="grid gap-4 p-6">
            {isReview ? (
              <>
                <label className="grid gap-2 text-sm font-bold">평점
                  <select name="rating" defaultValue="5" className="h-12 rounded-xl border border-stone-200 px-4 font-medium outline-none focus:border-hajin-600">
                    {[5, 4, 3, 2, 1].map((score) => <option key={score} value={score}>{score}점</option>)}
                  </select>
                </label>
                <label className="grid gap-2 text-sm font-bold">제목
                  <input name="title" maxLength={120} placeholder="리뷰 제목을 입력해 주세요." className="h-12 rounded-xl border border-stone-200 px-4 font-medium outline-none focus:border-hajin-600" />
                </label>
                <div className="grid gap-2 text-sm font-bold">사진/동영상 첨부
                  <label className="grid min-h-[92px] cursor-pointer place-items-center rounded-2xl border border-dashed border-stone-300 bg-stone-50 px-4 py-5 text-center transition hover:border-hajin-600 hover:bg-hajin-50">
                    <input type="file" accept="image/*,video/mp4,video/webm,video/quicktime" multiple className="sr-only" onChange={(event) => void uploadReviewMedia(event.currentTarget.files)} />
                    <span className="text-sm font-black text-stone-800">{uploadingMedia ? "업로드 중..." : "사진/동영상 추가"}</span>
                    <span className="mt-1 text-xs font-medium text-stone-500">최대 6개, 파일당 20MB 이하</span>
                  </label>
                  {reviewMedia.length ? <div className="grid grid-cols-6 gap-2 max-sm:grid-cols-3">{reviewMedia.map((url) => {
                    const isVideo = /\.(mp4|webm|mov)$/i.test(url);
                    return <div key={url} className="relative aspect-square overflow-hidden rounded-xl bg-stone-100">
                      {isVideo ? <video src={url} className="h-full w-full object-cover" muted playsInline /> : <img src={url} alt="리뷰 첨부 미리보기" className="h-full w-full object-cover" />}
                      <button type="button" onClick={() => setReviewMedia((current) => current.filter((item) => item !== url))} className="absolute right-1 top-1 grid size-6 place-items-center rounded-full bg-black/65 text-xs text-white">×</button>
                    </div>;
                  })}</div> : null}
                </div>
              </>
            ) : (
              <>
                <div className="rounded-2xl border border-hajin-100 bg-hajin-50/60 px-4 py-3 text-sm leading-6 text-stone-700">
                  <strong className="text-hajin-700">비공개 문의로 등록됩니다.</strong>
                  <p className="text-xs text-stone-500">목록에는 제목과 답변 상태만 노출되고, 내용 열람 시 등록한 비밀번호가 필요합니다.</p>
                </div>
                <label className="grid gap-2 text-sm font-bold">문의 제목
                  <input required name="subject" maxLength={120} placeholder="문의 제목을 입력해 주세요." className="h-12 rounded-xl border border-stone-200 px-4 font-medium outline-none focus:border-hajin-600" />
                </label>
                <label className="grid gap-2 text-sm font-bold">비밀번호
                  <input required name="password" type="password" minLength={4} maxLength={20} placeholder="4~20자 비밀번호" className="h-12 rounded-xl border border-stone-200 px-4 font-medium outline-none focus:border-hajin-600" />
                </label>
              </>
            )}
            <label className="grid gap-2 text-sm font-bold">내용
              <textarea required name="content" minLength={5} maxLength={3000} placeholder={description} className="h-36 resize-none rounded-xl border border-stone-200 p-4 font-medium leading-6 outline-none focus:border-hajin-600" />
            </label>
            {error ? <p className="rounded-xl bg-rose-50 px-4 py-3 text-sm font-bold text-rose-600">{error}</p> : null}
            <div className="grid grid-cols-2 gap-2 pt-2">
              <button type="button" onClick={onClose} className="rounded-2xl border border-stone-200 py-4 text-sm font-bold">취소</button>
              <button disabled={saving} className="rounded-2xl bg-hajin-600 py-4 text-sm font-bold text-white disabled:cursor-not-allowed disabled:bg-stone-300">{saving ? "등록 중..." : "등록하기"}</button>
            </div>
          </div>
        )}
      </form>
    </Layer>
  );
}

function CartPage({
  cart,
  onRemove,
  onCheckout,
}: {
  cart: Product[];
  onRemove: (index: number) => void;
  onCheckout: (items: Product[]) => void;
}) {
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const [deliveryTab, setDeliveryTab] = useState<"general" | "display" | "rental">("general");
  const [customer, setCustomer] = useState<{ name: string } | null>(null);
  const [coupons, setCoupons] = useState<Array<{ id: string; title: string; expiresAt: string | null }>>([]);
  const [pointBalance, setPointBalance] = useState(0);
  const [sessionChecked, setSessionChecked] = useState(false);
  useEffect(() => {
    let active = true;
    fetch("/api/store/hajin/me", { credentials: "include" })
      .then((response) => response.ok ? response.json() : null)
      .then((payload) => {
        if (!active || !payload?.authenticated) return;
        setCustomer(payload.customer);
        return fetch("/api/store/hajin/account", { credentials: "include" })
          .then((response) => response.ok ? response.json() : null)
          .then((account) => {
            if (!active || !Array.isArray(account?.coupons)) return;
            setPointBalance(Number(account?.customer?.rewardBalance || 0));
            setCoupons(account.coupons.map((coupon: { id: string; title?: string; expiresAt?: string | null }) => ({
              id: coupon.id,
              title: coupon.title || "발급 쿠폰",
              expiresAt: coupon.expiresAt || null,
            })));
          });
      })
      .catch(() => {})
      .finally(() => { if (active) setSessionChecked(true); });
    return () => { active = false; };
  }, []);
  const isSelected = (index: number) => selected[index] !== false;
  const quantityOf = (index: number) => quantities[index] || 1;
  const selectedCount = cart.filter((_, index) => isSelected(index)).length;
  const total = cart.reduce(
    (sum, product, index) =>
      sum + (isSelected(index) ? product.price * quantityOf(index) : 0),
    0,
  );
  const checkoutItems = cart.flatMap((product, index) =>
    isSelected(index)
      ? Array.from({ length: quantityOf(index) }, () => product)
      : [],
  );
  const setQuantity = (index: number, change: number) =>
    setQuantities((items) => ({
      ...items,
      [index]: Math.max(1, quantityOf(index) + change),
    }));
  const removeSelected = () => {
    [...cart.keys()]
      .filter((index) => isSelected(index))
      .reverse()
      .forEach(onRemove);
    setSelected({});
    setQuantities({});
  };
  const toggleAll = (checked: boolean) =>
    setSelected(Object.fromEntries(cart.map((_, index) => [index, checked])));
  return (
    <main className="mx-auto max-w-[1140px] px-0 pb-[58px] pt-12">
      <h1 className="mt-7 text-[30px] font-black tracking-[-.06em]">
        장바구니
      </h1>
      <section className="mt-3 flex min-h-[100px] items-center justify-between border-2 border-stone-800 px-9">
        {!sessionChecked ? <p className="text-sm text-stone-500">로그인 상태를 확인하고 있습니다.</p> : customer ? (
          <>
            <p className="text-sm font-bold leading-6"><strong>{customer.name}님은 <span className="text-rose-600">통합회원</span> 입니다.</strong></p>
            <div className="flex items-center gap-6 text-sm font-medium"><button onClick={() => go("/account")}>▧&nbsp; 쿠폰 <strong className="ml-1 text-rose-600">{coupons.length}장</strong></button><span className="border-l border-stone-300 pl-6">ⓟ&nbsp; 포인트 <strong className="ml-1 text-rose-600">{pointBalance.toLocaleString()}P</strong></span></div>
          </>
        ) : (
          <>
            <p className="text-sm font-bold leading-6">회원님은 <strong className="text-rose-600">비로그인</strong> 상태입니다.<br /><span className="font-medium">로그인 후 발급된 쿠폰 혜택을 확인하실 수 있습니다.</span></p>
            <button onClick={() => go("/account")} className="bg-stone-900 px-10 py-3 text-sm font-bold text-white">로그인</button>
          </>
        )}
      </section>
      <CartDeliveryTabs count={cart.length} active={deliveryTab} onChange={setDeliveryTab} />
      {deliveryTab !== "general" ? <section className="mt-5 grid h-[335px] place-items-center border-y border-stone-800 text-center"><div><p className="text-xl text-stone-500">{deliveryTab === "display" ? "전시상품 배송" : "렌탈"} 장바구니에 담긴 상품이 없습니다.</p><button onClick={() => setDeliveryTab("general")} className="mt-5 text-sm font-bold text-hajin-600 underline">일반배송 장바구니 보기</button></div></section> : cart.length ? (
        <div className="mt-5 grid gap-8 lg:grid-cols-[1fr_350px]">
          <section>
            <div className="flex items-center justify-between border-y-2 border-stone-900 px-4 py-4 text-sm">
              <label className="flex items-center gap-2 font-bold">
                <input
                  checked={selectedCount === cart.length}
                  onChange={(event) => toggleAll(event.target.checked)}
                  type="checkbox"
                  className="size-4 accent-stone-900"
                />{" "}
                전체선택 ({selectedCount}/{cart.length})
              </label>
              <button
                onClick={removeSelected}
                disabled={!selectedCount}
                className="text-stone-500 disabled:text-stone-300"
              >
                선택삭제
              </button>
            </div>
            {cart.map((product, index) => (
              <article
                key={`${product.id}-${index}`}
                className="grid grid-cols-[24px_120px_1fr_auto] items-center gap-4 border-b border-stone-200 py-6"
              >
                <input
                  checked={isSelected(index)}
                  onChange={(event) =>
                    setSelected((items) => ({
                      ...items,
                      [index]: event.target.checked,
                    }))
                  }
                  type="checkbox"
                  className="size-4 accent-stone-900"
                />
                <div className="size-[120px]">
                  <ProductVisual product={product} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-bold text-hajin-600">
                    {product.brand}
                  </p>
                  <button
                    onClick={() => go(`/products/${product.slug}`)}
                    className="mt-2 text-left text-[15px] font-medium"
                  >
                    {product.name}
                  </button>
                  <p className="mt-3 text-sm text-stone-500">
                    {product.delivery || "무료배송"} · 내일 도착 예정
                  </p>
                </div>
                <div className="grid justify-items-end gap-4">
                  <button
                    onClick={() => onRemove(index)}
                    className="text-xl text-stone-400"
                    aria-label="상품 삭제"
                  >
                    ×
                  </button>
                  <strong className="text-xl">
                    {money(product.price * quantityOf(index))}원
                  </strong>
                  <div className="flex border border-stone-300">
                    <button
                      onClick={() => setQuantity(index, -1)}
                      disabled={quantityOf(index) === 1}
                      className="px-3 py-1 disabled:text-stone-300"
                    >
                      −
                    </button>
                    <span className="border-x border-stone-300 px-3 py-1 text-sm">
                      {quantityOf(index)}
                    </span>
                    <button
                      onClick={() => setQuantity(index, 1)}
                      className="px-3 py-1"
                    >
                      ＋
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </section>
          <aside className="h-fit border border-stone-300 p-7">
            <h2 className="text-lg font-black">주문금액</h2>
            <div className="mt-6 space-y-4 border-b border-stone-200 pb-5 text-sm">
              <p className="flex justify-between">
                <span>총 상품금액</span>
                <strong>{money(total)}원</strong>
              </p>
              <p className="flex justify-between">
                <span>배송비</span>
                <strong>0원</strong>
              </p>
              <p className="flex justify-between">
                <span>할인금액</span>
                <strong>0원</strong>
              </p>
            </div>
            <p className="mt-6 flex items-end justify-between">
              <span className="font-bold">결제예정금액</span>
              <strong className="text-2xl text-hajin-600">
                {money(total)}원
              </strong>
            </p>
            <button
              onClick={() => {
                onCheckout(checkoutItems);
                go("/order");
              }}
              disabled={!selectedCount}
              className="mt-7 w-full bg-black py-5 font-bold text-white disabled:bg-stone-300"
            >
              주문하기
            </button>
          </aside>
        </div>
      ) : (
        <>
          <section className="mt-5 grid h-[335px] place-items-center border-y border-stone-800 text-center">
            <p className="w-full text-[20px] leading-8 text-[#999]">
              장바구니에 담긴 상품이 없습니다.
            </p>
          </section>
          <div className="mt-16 text-center">
            <button
              onClick={() => go("/")}
              className="bg-stone-800 px-14 py-5 text-lg font-bold text-white"
            >
              계속 쇼핑하기
            </button>
          </div>
        </>
      )}
      <CartNotices />
    </main>
  );
}

function CartDeliveryTabs({ count, active, onChange }: { count: number; active: "general" | "display" | "rental"; onChange: (tab: "general" | "display" | "rental") => void }) {
  const tabs: Array<["general" | "display" | "rental", string, number]> = [["general", "일반배송", count], ["display", "전시상품 배송", 0], ["rental", "렌탈", 0]];
  return (
    <div className="mt-10 grid grid-cols-3 border-b-2 border-stone-800 text-center text-lg font-bold">
      {tabs.map(([tab, label, tabCount]) => <button key={tab} onClick={() => onChange(tab)} className={`border py-4 transition ${active === tab ? "border-2 border-b-0 border-stone-800 bg-white text-stone-900" : "border-stone-300 text-stone-400 hover:bg-stone-50"}`}>{label} <span className={`ml-2 ${active === tab ? "text-rose-600" : ""}`}>{tabCount}</span></button>)}
    </div>
  );
}

function CartNotices() {
  return (
    <section className="mt-20 border-t border-stone-300 pt-7 text-sm leading-7 text-stone-600">
      <h2 className="text-lg font-black text-stone-900">꼭 확인해주세요</h2>
      <ul className="mt-3 list-disc space-y-1 pl-5">
        <li>쿠폰적용 제외 상품은 쿠폰적용 가능 주문금액에서 제외됩니다.</li>
        <li>
          무통장 입금일 경우 3일 이내 해당 계좌로 입금하셔야 주문이 완료되며,
          입금 확인 후 3~5일 이내에 배송됩니다.
        </li>
        <li>
          결제완료 이후에는 배송방법을 변경하실 수 없으므로, 주문 취소 후 새로
          주문해 주시기 바랍니다.
        </li>
        <li>
          교환 및 반품은 배송완료 후 7일 이내만 신청 가능하며, 포장이 훼손되거나
          사용하신 상품은 교환 및 환불되지 않습니다.
        </li>
      </ul>
      <button className="mt-4 font-bold text-stone-900 underline">
        카드 무이자/부분 무이자 혜택 안내
      </button>
    </section>
  );
}

function CheckoutSteps({ active }: { active: "cart" | "order" }) {
  return (
    <ol className="flex justify-end gap-2 whitespace-nowrap text-[11px] sm:gap-5 sm:text-sm">
      <li
        className={
          active === "cart" ? "font-bold text-stone-950" : "text-stone-400"
        }
      >
        01 장바구니
      </li>
      <li className="text-stone-300">›</li>
      <li
        className={
          active === "order" ? "font-bold text-stone-950" : "text-stone-400"
        }
      >
        02 주문/결제
      </li>
      <li className="text-stone-300">›</li>
      <li className="text-stone-400">03 주문완료</li>
    </ol>
  );
}

function OrderPageLegacy({ cart }: { cart: Product[] }) {
  const [payment, setPayment] = useState("card");
  const total = cart.reduce((sum, product) => sum + product.price, 0);
  const shippingFee = cart.reduce((fee, product) => Math.max(fee, Number(product.shippingFee || 0)), 0);
  return (
    <main className="mx-auto max-w-[1040px] px-4 py-7 sm:px-5 sm:py-12">
      <div className="flex flex-col gap-3 border-b border-stone-200 pb-5 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-[27px] font-black sm:text-3xl">주문/결제</h1>
        <ol className="hidden gap-5 text-sm sm:flex">
          <li className="font-bold text-hajin-600">01 장바구니</li>
          <li className="font-bold">02 주문/결제</li>
          <li className="text-stone-400">03 주문완료</li>
        </ol>
      </div>
      <div className="mt-8 grid gap-7 lg:grid-cols-[1fr_320px]">
        <div className="space-y-7">
          <OrderSection title="주문 상품">
            {cart.length ? (
              cart.map((product, index) => (
                <div
                  key={`${product.id}-${index}`}
                  className="flex items-center gap-4 py-3"
                >
                  <div className="size-16">
                    <ProductVisual product={product} />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-hajin-600">
                      {product.brand}
                    </p>
                    <p className="mt-1 text-sm font-medium">{product.name}</p>
                    <p className="mt-2 font-black">{money(product.price)}원</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="py-5 text-sm text-stone-500">
                주문할 상품이 없습니다. 장바구니에서 상품을 담아주세요.
              </p>
            )}
          </OrderSection>
          <OrderSection title="배송지">
            <div className="grid gap-3">
              <input
                className="border border-stone-300 px-4 py-3 text-sm"
                placeholder="받는 분"
              />
              <div className="flex gap-2">
                <input
                  className="min-w-0 flex-1 border border-stone-300 px-4 py-3 text-sm"
                  placeholder="휴대폰 번호"
                />
                <button className="shrink-0 border border-stone-400 px-4 text-sm font-bold">
                  인증
                </button>
              </div>
              <div className="flex gap-2">
                <input
                  className="min-w-0 flex-1 border border-stone-300 px-4 py-3 text-sm"
                  placeholder="우편번호"
                />
                <button className="shrink-0 border border-stone-400 px-4 text-sm font-bold">
                  주소검색
                </button>
              </div>
              <input
                className="border border-stone-300 px-4 py-3 text-sm"
                placeholder="기본 주소"
              />
              <input
                className="border border-stone-300 px-4 py-3 text-sm"
                placeholder="상세 주소"
              />
            </div>
          </OrderSection>
          <OrderSection title="결제 수단">
            <div className="grid grid-cols-3 border border-stone-200 text-sm">
              {[
                ["card", "신용카드"],
                ["bank", "무통장입금"],
                ["simple", "간편결제"],
              ].map(([value, label]) => (
                <button
                  key={value}
                  onClick={() => setPayment(value)}
                  className={`py-4 ${payment === value ? "bg-stone-900 font-bold text-white" : "hover:bg-stone-50"}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <label className="mt-4 flex items-center gap-2 text-xs text-stone-500">
              <input type="checkbox" /> 주문 내용을 확인했으며 결제에
              동의합니다.
            </label>
          </OrderSection>
        </div>
        <aside className="h-fit border border-stone-200 p-6">
          <h2 className="text-lg font-black">최종 결제금액</h2>
          <div className="mt-6 space-y-3 border-b border-stone-200 pb-5 text-sm">
            <p className="flex justify-between">
              <span>상품금액</span>
              <strong>{money(total)}원</strong>
            </p>
            <p className="flex justify-between">
              <span>배송비</span>
              <strong>{money(shippingFee)}원</strong>
            </p>
          </div>
          <p className="mt-5 flex items-end justify-between">
            <span className="font-bold">총 결제금액</span>
            <strong className="text-2xl text-hajin-600">
              {money(total)}원
            </strong>
          </p>
          <button
            disabled={!cart.length}
            className="mt-6 w-full bg-hajin-600 py-4 font-bold text-white disabled:cursor-not-allowed disabled:bg-stone-300"
          >
            {money(total)}원 결제하기
          </button>
        </aside>
      </div>
    </main>
  );
}

function OrderPage({ cart, onComplete }: { cart: Product[]; onComplete: () => void }) {
  const [addresses, setAddresses] = useState<any[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [saveAddress, setSaveAddress] = useState(false);
  const [setDefaultAddress, setSetDefaultAddress] = useState(false);
  const [recipientName, setRecipientName] = useState("");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [recipientZip, setRecipientZip] = useState("");
  const [address, setAddress] = useState("");
  const [address2, setAddress2] = useState("");
  const [depositName, setDepositName] = useState("");
  const [request, setRequest] = useState("배송 시 요청사항을 선택해 주세요.");
  const [agreed, setAgreed] = useState(false);
  const [checkout, setCheckout] = useState<any>(null);
  const [paymentMethod, setPaymentMethod] = useState("bank_transfer");
  const [cashReceiptType, setCashReceiptType] = useState<"NONE" | "INCOME" | "EXPENSE">("NONE");
  const [cashReceiptIssueType, setCashReceiptIssueType] = useState("PHONE");
  const [cashReceiptNumber, setCashReceiptNumber] = useState("");
  const [cardStage, setCardStage] = useState<"connecting" | "notice" | null>(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [complete, setComplete] = useState<{ orderNo: string; totalAmount: number; payment: { bankName: string; accountNumber: string; accountHolder: string; description: string } } | null>(null);
  const [coupons, setCoupons] = useState<any[]>([]);
  const [selectedCouponId, setSelectedCouponId] = useState("");
  const [couponInput, setCouponInput] = useState("");
  const [couponInputMessage, setCouponInputMessage] = useState("");
  const orderAddress2Ref = useRef<HTMLInputElement>(null);
  const total = cart.reduce((sum, product) => sum + product.price, 0);
  const shippingFee = cart.reduce((fee, product) => Math.max(fee, Number(product.shippingFee || 0)), 0);
  const selectedCoupon = coupons.find((coupon) => coupon.selectionId === selectedCouponId && coupon.available) || null;
  const couponDiscount = Number(selectedCoupon?.discountAmount || 0);
  const totalAfterCoupon = Math.max(0, total - couponDiscount) + shippingFee;
  const paymentMethods = Array.isArray(checkout?.methods) ? checkout.methods.filter((method: any) => method?.enabled) : [{ id: "bank_transfer", label: "무통장입금", description: "주문 접수 후 안내된 계좌로 입금해 주세요." }, { id: "card", label: "신용카드", description: "PG 결제창을 통해 카드 결제를 진행합니다." }];
  const isBankTransfer = paymentMethod === "bank_transfer";
  const cashReceiptPlaceholder = cashReceiptIssueType === "BUSINESS_NUMBER" ? "사업자등록번호 10자리" : cashReceiptIssueType === "CASH_RECEIPT_CARD" ? "현금영수증카드 번호" : "휴대폰번호";
  useEffect(() => {
    let active = true;
    fetch("/api/store/hajin/orders", { credentials: "include" }).then(async (response) => ({ response, payload: await response.json().catch(() => null) })).then(({ response, payload }) => {
      if (!active) return;
      if (!response.ok) { setError(payload?.message || "주문 정보를 불러올 수 없습니다."); return; }
      setCheckout(payload);
      const paymentMethods = Array.isArray(payload.methods) ? payload.methods.filter((method: any) => method?.enabled) : [];
      setPaymentMethod(paymentMethods.some((method: any) => method.id === "bank_transfer") ? "bank_transfer" : paymentMethods[0]?.id || "card");
      const savedAddresses = Array.isArray(payload.addresses) ? payload.addresses : [];
      const defaultAddress = payload.address || savedAddresses[0] || null;
      setAddresses(savedAddresses);
      setSelectedAddressId(defaultAddress?.id || "");
      setRecipientName(defaultAddress?.recipientName || payload.customer?.name || "");
      setRecipientPhone(defaultAddress?.phone || "");
      setRecipientZip(defaultAddress?.zipCode || "");
      setAddress(defaultAddress?.address1 || "");
      setAddress2(defaultAddress?.address2 || "");
      setDepositName(payload.customer?.name || "");
    }).catch(() => { if (active) setError("주문 정보를 불러올 수 없습니다."); });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    let active = true;
    fetch(`/api/store/hajin/coupons?subtotal=${total}`, { credentials: "include" })
      .then(async (response) => ({ response, payload: await response.json().catch(() => null) }))
      .then(({ response, payload }) => {
        if (!active) return;
        const nextCoupons = response.ok && Array.isArray(payload?.coupons) ? payload.coupons : [];
        setCoupons(nextCoupons);
        setSelectedCouponId((current) => nextCoupons.some((coupon: any) => coupon.selectionId === current && coupon.available) ? current : "");
      })
      .catch(() => { if (active) setCoupons([]); });
    return () => { active = false; };
  }, [total]);
  const selectAddress = (nextAddress: any) => {
    setSelectedAddressId(nextAddress.id || "");
    setRecipientName(nextAddress.recipientName || "");
    setRecipientPhone(nextAddress.phone || "");
    setRecipientZip(nextAddress.zipCode || "");
    setAddress(nextAddress.address1 || "");
    setAddress2(nextAddress.address2 || "");
    setSaveAddress(false);
    setSetDefaultAddress(false);
  };
  const startNewAddress = () => {
    setSelectedAddressId("");
    setRecipientName("");
    setRecipientPhone("");
    setRecipientZip("");
    setAddress("");
    setAddress2("");
    setSaveAddress(true);
    setSetDefaultAddress(false);
  };
  const applyCouponInput = () => {
    const token = couponInput.trim();
    if (!token) {
      setCouponInputMessage("쿠폰번호 또는 발급코드를 입력해 주세요.");
      return;
    }
    const normalizedToken = token.toUpperCase();
    const coupon = coupons.find((item) => [item.selectionId, item.id, item.customerCouponId, item.issueCode, item.code]
      .filter(Boolean)
      .some((value) => String(value).trim().toUpperCase() === normalizedToken));
    if (!coupon) {
      setCouponInputMessage("등록된 쿠폰을 찾을 수 없습니다. 쿠폰번호를 다시 확인해 주세요.");
      return;
    }
    if (!coupon.available) {
      setCouponInputMessage(coupon.unavailableReason || "현재 주문에는 사용할 수 없는 쿠폰입니다.");
      return;
    }
    setSelectedCouponId(coupon.selectionId);
    setCouponInput(coupon.issueCode || coupon.code || token);
    setCouponInputMessage(`${coupon.title} 쿠폰이 적용되었습니다.`);
  };
  const pay = async (event: FormEvent) => {
    event.preventDefault();
    if (!cart.length || !agreed || !checkout) return;
    if (paymentMethod === "card") {
      setCardStage("connecting");
      window.setTimeout(() => setCardStage("notice"), 1400);
      return;
    }
    setSubmitting(true); setError("");
    const items = Object.values(cart.reduce<Record<string, { productId: string; slug: string; quantity: number }>>((all, product) => {
      const key = product.id || product.slug;
      all[key] = all[key] ? { ...all[key], quantity: all[key].quantity + 1 } : { productId: product.id, slug: product.slug, quantity: 1 };
      return all;
    }, {}));
    try {
      const response = await fetch("/api/store/hajin/orders", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ items, recipientName, recipientPhone, recipientZip, recipientAddress1: address, recipientAddress2: address2, depositName, deliveryMemo: request === "배송 시 요청사항을 선택해 주세요." ? "" : request, couponId: selectedCoupon?.selectionId || "", saveAddress, setDefaultAddress, cashReceiptType, cashReceiptIssueType: cashReceiptType === "NONE" ? "" : cashReceiptIssueType, cashReceiptNumber: cashReceiptType === "NONE" ? "" : cashReceiptNumber }) });
      const payload = await response.json().catch(() => null);
      if (!response.ok) throw new Error(payload?.message || "주문을 접수하지 못했습니다.");
      setComplete(payload);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "주문을 접수하지 못했습니다."); } finally { setSubmitting(false); }
  };
  return (
    <main className="mx-auto max-w-[1040px] px-5 py-12">
      <div className="flex items-center justify-between border-b border-stone-200 pb-5">
        <h1 className="text-3xl font-black">주문/결제</h1>
        <CheckoutSteps active="order" />
      </div>
      <form onSubmit={pay} className="mt-8 grid gap-7 lg:grid-cols-[1fr_320px]">
        <div className="space-y-7">
          <OrderSection
            title={`주문 상품 ${cart.length ? `(${cart.length})` : ""}`}
          >
            {cart.length ? (
              cart.map((product, index) => (
                <div
                  key={`${product.id}-${index}`}
                  className="flex items-start gap-3 border-b border-stone-100 py-3 last:border-0 sm:items-center sm:gap-4"
                >
                  <div className="size-14 shrink-0 sm:size-16">
                    <ProductVisual product={product} />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-hajin-600">
                      {product.brand}
                    </p>
                    <p className="mt-1 line-clamp-2 text-sm font-medium">{product.name}</p>
                    <p className="mt-2 font-black">{money(product.price)}원</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="py-5 text-sm text-stone-500">
                주문할 상품이 없습니다. 장바구니에서 상품을 담아주세요.
              </p>
            )}
          </OrderSection>
          <OrderSection title="배송지">
            <div className="grid gap-3">
              <div className="flex flex-wrap items-center gap-2 border-b border-stone-200 pb-4">
                {addresses.map((savedAddress) => <button key={savedAddress.id} type="button" onClick={() => selectAddress(savedAddress)} className={`rounded-full border px-3 py-2 text-xs font-bold ${selectedAddressId === savedAddress.id ? "border-hajin-600 bg-hajin-50 text-hajin-700" : "border-stone-300 text-stone-600 hover:bg-stone-50"}`}>{savedAddress.label || "배송지"}{savedAddress.isDefault ? " · 기본" : ""}</button>)}
                <button type="button" onClick={startNewAddress} className="rounded-full border border-stone-400 px-3 py-2 text-xs font-bold text-stone-700 hover:bg-stone-50">+ 새 배송지 추가</button>
              </div>
              <input required value={recipientName} onChange={(event) => setRecipientName(event.target.value)}
                className="border border-stone-300 px-4 py-3 text-sm"
                placeholder="받는 분"
              />
              <input required value={recipientPhone} onChange={(event) => setRecipientPhone(event.target.value)} className="border border-stone-300 px-4 py-3 text-sm" placeholder="휴대폰 번호" />
              <div className="flex gap-2">
                <input required value={recipientZip} onChange={(event) => setRecipientZip(event.target.value)}
                  className="min-w-0 flex-1 border border-stone-300 px-4 py-3 text-sm"
                  placeholder="우편번호"
                />
                <button
                  type="button"
                  onClick={() => openHajinPostcode(({ zipCode, address }) => {
                    setRecipientZip(zipCode);
                    setAddress(address);
                    window.setTimeout(() => orderAddress2Ref.current?.focus(), 0);
                  })}
                  className="shrink-0 border border-stone-400 px-4 text-sm font-bold"
                >
                  주소검색
                </button>
              </div>
              <input required value={address} onChange={(event) => setAddress(event.target.value)}
                className="border border-stone-300 px-4 py-3 text-sm"
                placeholder="기본 주소"
              />
              <input ref={orderAddress2Ref} required value={address2} onChange={(event) => setAddress2(event.target.value)}
                className="border border-stone-300 px-4 py-3 text-sm"
                placeholder="상세 주소"
              />
              <select
                value={request}
                onChange={(event) => setRequest(event.target.value)}
                className="border border-stone-300 bg-white px-4 py-3 text-sm"
              >
                <option>배송 시 요청사항을 선택해 주세요.</option>
                <option>부재 시 경비실에 맡겨주세요.</option>
                <option>배송 전 연락 부탁드립니다.</option>
                <option>문 앞에 놓아주세요.</option>
              </select>
              <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-stone-600">
                <label className="flex items-center gap-2"><input type="checkbox" checked={saveAddress} onChange={(event) => { setSaveAddress(event.target.checked); if (!event.target.checked) setSetDefaultAddress(false); }} /> 입력한 배송지를 배송지 목록에 추가</label>
                {saveAddress ? <label className="flex items-center gap-2"><input type="checkbox" checked={setDefaultAddress} onChange={(event) => setSetDefaultAddress(event.target.checked)} /> 기본 배송지로 설정</label> : null}
              </div>
            </div>
          </OrderSection>
          <OrderSection title={`쿠폰 ${coupons.length ? `(${coupons.filter((coupon) => coupon.available).length}장 사용 가능)` : ""}`}>
            <div className="rounded-lg border border-stone-200 bg-stone-50/70 p-4">
              <label className="block text-xs font-bold text-stone-700">쿠폰번호 직접 등록</label>
              <div className="mt-2 flex gap-2">
                <input value={couponInput} onChange={(event) => { setCouponInput(event.target.value); setCouponInputMessage(""); }} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); applyCouponInput(); } }} className="min-w-0 flex-1 rounded-md border border-stone-300 bg-white px-4 py-3 text-sm outline-none transition placeholder:text-stone-400 focus:border-hajin-600 focus:ring-2 focus:ring-hajin-100" placeholder="쿠폰번호 또는 발급코드를 입력하세요" />
                <button type="button" onClick={applyCouponInput} className="shrink-0 rounded-md bg-stone-900 px-5 text-sm font-bold text-white transition hover:bg-stone-700">적용</button>
              </div>
              <p className="mt-2 text-[11px] text-stone-500">발급받은 쿠폰번호를 입력하면 보유 쿠폰에 바로 적용됩니다.</p>
              {couponInputMessage ? <p role="status" className={`mt-2 text-xs font-bold ${selectedCoupon && couponInputMessage.endsWith("적용되었습니다.") ? "text-hajin-600" : "text-rose-600"}`}>{couponInputMessage}</p> : null}
            </div>
            <label className="mt-5 block text-xs font-bold text-stone-700">보유 쿠폰 선택</label>
            <select value={selectedCouponId} onChange={(event) => { setSelectedCouponId(event.target.value); setCouponInputMessage(""); }} className="mt-2 w-full rounded-md border border-stone-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-hajin-600 focus:ring-2 focus:ring-hajin-100">
              <option value="">쿠폰을 선택하지 않음</option>
              {coupons.map((coupon) => <option key={coupon.selectionId} value={coupon.selectionId} disabled={!coupon.available}>{coupon.title}{coupon.available ? ` (-${money(Number(coupon.discountAmount || 0))}원)` : ` · ${coupon.unavailableReason}`}</option>)}
            </select>
            {selectedCoupon ? <p className="mt-3 text-sm font-bold text-hajin-600">{selectedCoupon.title} 적용 · {money(couponDiscount)}원 할인</p> : <p className="mt-3 text-xs text-stone-500">사용 조건을 충족한 쿠폰만 선택할 수 있습니다.</p>}
          </OrderSection>
          <OrderSection title="결제 수단">
            <div className="grid gap-2 sm:grid-cols-2">
              {paymentMethods.map((method: any) => <button key={method.id} type="button" onClick={() => setPaymentMethod(method.id)} className={`border py-4 text-sm font-bold transition ${paymentMethod === method.id ? "border-stone-900 bg-stone-900 text-white" : "border-stone-300 bg-white text-stone-700 hover:bg-stone-50"}`}>{method.label}</button>)}
            </div>
            {isBankTransfer ? <>
              {checkout?.payment ? <div className="mt-4 bg-stone-50 p-4 text-sm leading-6 text-stone-700"><strong>{checkout.payment.bankName} {checkout.payment.accountNumber} ({checkout.payment.accountHolder})</strong><p>{checkout.payment.description}</p></div> : <p className="mt-4 text-sm font-bold text-rose-600">무통장 입금 계좌가 아직 설정되지 않았습니다.</p>}
              <label className="mt-4 grid gap-2 text-sm font-bold">입금자명<input required={isBankTransfer} value={depositName} onChange={(event) => setDepositName(event.target.value)} className="border border-stone-300 px-4 py-3 font-normal" placeholder="입금자명" /></label>
            </> : <div className="mt-4 bg-slate-50 p-4 text-sm leading-6 text-slate-700"><strong>신용카드 결제</strong><p>결제 버튼을 누르면 PG 결제창 연결 상태를 확인합니다.</p></div>}
            <label className="group mt-5 flex cursor-pointer items-center gap-3 rounded-lg border border-stone-200 bg-stone-50 px-4 py-3 text-xs text-stone-600 transition hover:border-stone-300 hover:bg-white">
              <input
                checked={agreed}
                onChange={(event) => setAgreed(event.target.checked)}
                type="checkbox"
                required
                className="sr-only"
              />
              <span aria-hidden className={`grid size-5 shrink-0 place-items-center rounded-md border shadow-sm transition ${agreed ? "border-hajin-600 bg-hajin-600 text-white" : "border-stone-400 bg-white text-transparent"}`}>
                {agreed ? <svg viewBox="0 0 16 16" className="size-3.5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="m3 8 3 3 7-7" /></svg> : null}
              </span>
              <span><strong className="font-bold text-stone-800">주문 내용을 확인했습니다.</strong> 결제에 동의합니다.</span>
            </label>
          </OrderSection>
          {isBankTransfer ? <OrderSection title="현금영수증">
            <p className="text-xs leading-5 text-stone-500">무통장입금 결제 건에 대해 현금영수증을 신청할 수 있습니다.</p>
            <div className="mt-4 grid gap-2 sm:grid-cols-3">
              {[{ value: "NONE", label: "신청 안함", description: "현금영수증을 신청하지 않습니다." }, { value: "INCOME", label: "소득공제", description: "휴대폰번호 또는 카드번호" }, { value: "EXPENSE", label: "지출증빙", description: "사업자등록번호로 발급" }].map((option) => <button key={option.value} type="button" onClick={() => { const next = option.value as "NONE" | "INCOME" | "EXPENSE"; setCashReceiptType(next); setCashReceiptIssueType(next === "EXPENSE" ? "BUSINESS_NUMBER" : "PHONE"); if (next === "NONE") setCashReceiptNumber(""); }} className={`min-h-[78px] rounded-lg border px-4 py-3 text-left transition ${cashReceiptType === option.value ? "border-[#9b624b] bg-[#fffaf7] shadow-sm" : "border-stone-200 bg-white hover:border-stone-400 hover:bg-stone-50"}`}><span className="flex items-center gap-2 text-sm font-bold text-stone-900"><span aria-hidden className={`grid size-5 place-items-center rounded-full border transition ${cashReceiptType === option.value ? "border-[#b65e42]" : "border-stone-300"}`}><span className={`size-2.5 rounded-full transition ${cashReceiptType === option.value ? "scale-100 bg-[#b65e42]" : "scale-0 bg-transparent"}`} /></span>{option.label}</span><span className="ml-7 mt-1 block text-[11px] font-normal text-stone-500">{option.description}</span></button>)}
            </div>
            {cashReceiptType !== "NONE" ? <div className="mt-5 grid gap-4 rounded-lg border border-stone-200 bg-stone-50/70 p-4 sm:grid-cols-[190px_minmax(0,1fr)]"><label className="grid gap-2 text-xs font-bold text-stone-700">발급 구분<select value={cashReceiptIssueType} onChange={(event) => { setCashReceiptIssueType(event.target.value); setCashReceiptNumber(""); }} className="h-11 w-full rounded-md border border-stone-300 bg-white px-3 text-sm font-normal text-stone-900 outline-none focus:border-hajin-600 focus:ring-2 focus:ring-hajin-100"><option value="PHONE">휴대폰번호</option>{cashReceiptType === "INCOME" ? <option value="CASH_RECEIPT_CARD">현금영수증카드</option> : <option value="BUSINESS_NUMBER">사업자등록번호</option>}</select></label><label className="grid gap-2 text-xs font-bold text-stone-700">발급 정보<input required value={cashReceiptNumber} onChange={(event) => setCashReceiptNumber(event.target.value)} inputMode="numeric" className="h-11 w-full rounded-md border border-stone-300 bg-white px-3 text-sm font-normal text-stone-900 outline-none placeholder:text-stone-400 focus:border-hajin-600 focus:ring-2 focus:ring-hajin-100" placeholder={cashReceiptPlaceholder} /></label></div> : null}
          </OrderSection> : null}
        </div>
        <aside className="h-fit border border-stone-200 p-5 sm:p-6 lg:sticky lg:top-[126px]">
          <h2 className="text-lg font-black">최종 결제금액</h2>
          <div className="mt-6 space-y-3 border-b border-stone-200 pb-5 text-sm">
            <p className="flex justify-between">
              <span>상품금액</span>
              <strong>{money(total)}원</strong>
            </p>
            <p className="flex justify-between">
              <span>배송비</span>
              <strong>0원</strong>
            </p>
            {couponDiscount ? <p className="flex justify-between text-hajin-600"><span>쿠폰 할인</span><strong>-{money(couponDiscount)}원</strong></p> : null}
          </div>
          <p className="mt-5 flex items-end justify-between">
            <span className="font-bold">총 결제금액</span>
            <strong className="text-2xl text-hajin-600">
              {money(totalAfterCoupon)}원
            </strong>
          </p>
          {error ? <p role="alert" className="mt-4 text-sm font-bold leading-5 text-rose-600">{error}{error.includes("로그인") ? <button type="button" onClick={() => go("/account")} className="ml-2 underline">로그인</button> : null}</p> : null}
          <button
            disabled={!cart.length || !agreed || !checkout || submitting || (isBankTransfer && !checkout?.payment)}
            className="mt-6 w-full bg-hajin-600 py-4 font-bold text-white disabled:cursor-not-allowed disabled:bg-stone-300"
          >
            {submitting ? "주문 접수 중..." : paymentMethod === "card" ? `${money(totalAfterCoupon)}원 카드 결제하기` : `${money(totalAfterCoupon)}원 주문 접수`}
          </button>
        </aside>
      </form>
      {complete ? (
        <OrderCompleteLayer
          order={complete}
          onClose={() => {
            onComplete();
            go("/");
          }}
        />
      ) : null}
      {cardStage === "connecting" ? <Layer onClose={() => {}} overlayClass="bg-black/55" className="grid place-items-center p-5"><section role="dialog" aria-modal="true" aria-label="카드 결제창 연결 중" className="w-full max-w-[550px] rounded-2xl bg-white px-8 py-12 text-center shadow-2xl"><span className="mx-auto block size-14 animate-spin rounded-full border-4 border-stone-200 border-r-[#8d5c47]" /><p className="mt-5 text-sm font-black tracking-wide text-[#8d5c47]">PG CONNECTION</p><h2 className="mt-6 text-3xl font-black tracking-tight text-slate-800">카드 결제창을 연결하고 있습니다.</h2><p className="mt-5 text-base leading-7 text-slate-500">안전한 결제를 위해 PG사 시스템을 확인하고 있습니다.</p></section></Layer> : null}
      {cardStage === "notice" ? (
        <Layer onClose={() => setCardStage(null)} overlayClass="bg-black/55" className="grid place-items-center p-5">
          <section role="dialog" aria-modal="true" aria-label="카드 결제 안내" className="w-full max-w-[550px] rounded-2xl bg-white px-7 py-10 text-center shadow-2xl sm:px-10 sm:py-11">
            <span className="mx-auto grid size-14 place-items-center rounded-full border-4 border-[#c85a47] text-3xl font-black text-[#c85a47]">!</span>
            <p className="mt-5 text-sm font-black tracking-wide text-[#8d5c47]">PAYMENT NOTICE</p>
            <h2 className="mt-6 break-keep text-[28px] font-black leading-tight tracking-tight text-slate-800 sm:text-3xl">카드 결제를 진행할 수 없습니다.</h2>
            <div className="mt-6 space-y-4 break-keep text-base leading-8 text-slate-500">
              {cardPaymentNoticeText.map((line) => (
                <p key={line}>{line}</p>
              ))}
            </div>
            <button type="button" onClick={() => setCardStage(null)} className="mt-8 w-full rounded-lg bg-[#20262d] py-4 text-lg font-bold text-white">확인</button>
          </section>
        </Layer>
      ) : null}
    </main>
  );
}

function AddressLayer({
  onClose,
  onSelect,
}: {
  onClose: () => void;
  onSelect: (address: string) => void;
}) {
  const [keyword, setKeyword] = useState("");
  const matches = [
    "서울특별시 강남구 테헤란로 152",
    "서울특별시 강남구 역삼로 156",
    "서울특별시 용산구 청파로 74",
  ];
  return (
    <Layer onClose={onClose} className="grid place-items-center p-5">
      <section
        role="dialog"
        aria-modal="true"
        aria-label="주소 검색"
        className="w-full max-w-[560px] bg-white"
      >
        <header className="flex items-center justify-between border-b border-stone-200 px-6 py-5">
          <h2 className="text-xl font-black">주소 검색</h2>
          <button
            onClick={onClose}
            className="text-2xl text-stone-500"
            aria-label="주소 검색 닫기"
          >
            ×
          </button>
        </header>
        <div className="p-6">
          <div className="flex">
            <input
              autoFocus
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              className="min-w-0 flex-1 border border-stone-900 px-4 py-3 text-sm"
              placeholder="도로명, 건물명 또는 지번 입력"
            />
            <button className="bg-stone-900 px-5 text-sm font-bold text-white">
              검색
            </button>
          </div>
          <p className="mt-4 text-xs text-stone-500">
            도로명 주소 또는 건물명을 입력해 주세요.
          </p>
          <div className="mt-6 border-t border-stone-300">
            {matches
              .filter((item) => !keyword || item.includes(keyword))
              .map((item) => (
                <button
                  type="button"
                  key={item}
                  onClick={() => onSelect(item)}
                  className="block w-full border-b border-stone-200 px-3 py-4 text-left text-sm hover:bg-stone-50"
                >
                  <strong className="mr-3 text-xs text-hajin-600">
                    도로명
                  </strong>
                  {item}
                </button>
              ))}
          </div>
        </div>
      </section>
    </Layer>
  );
}

function OrderCompleteLayer({
  order,
  onClose,
}: {
  order: { orderNo: string; totalAmount: number; payment: { bankName: string; accountNumber: string; accountHolder: string; description: string } };
  onClose: () => void;
}) {
  return (
    <Layer onClose={onClose} className="grid place-items-center p-5">
      <section
        role="dialog"
        aria-modal="true"
        className="w-full max-w-[430px] bg-white p-8 text-center"
      >
        <p className="text-sm font-bold text-hajin-600">
          주문이 접수되었습니다
        </p>
        <h2 className="mt-3 text-2xl font-black">
          주문번호 {order.orderNo}
        </h2>
        <p className="mt-4 text-sm leading-6 text-stone-500">
          {order.payment.bankName} {order.payment.accountNumber} ({order.payment.accountHolder})로<br />{money(order.totalAmount)}원을 입금해 주세요. 입금 확인 후 출고됩니다.
        </p>
        <p className="mt-4 rounded-xl bg-stone-50 px-4 py-3 text-sm font-bold leading-6 text-stone-700">
          {customerCenterGuideText}
        </p>
        <button
          onClick={onClose}
          className="mt-7 w-full bg-stone-900 py-4 font-bold text-white"
        >
          쇼핑 계속하기
        </button>
      </section>
    </Layer>
  );
}

function OrderSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="border border-stone-200 p-6">
      <h2 className="border-b border-stone-200 pb-4 text-lg font-black">
        {title}
      </h2>
      <div className="pt-3">{children}</div>
    </section>
  );
}

function AccountPageLegacy() {
  return (
    <main className="mx-auto min-h-[790px] max-w-[400px] px-0 pb-20 pt-[54px]">
      <h1 className="text-center text-[36px] font-black tracking-[-.08em]">
        로그인
      </h1>
    </main>
  );
}

function hajinLoginErrorMessage(code?: string | null) {
  if (!code) return "";
  if (code === "required") return "아이디와 비밀번호를 입력해 주세요.";
  if (code === "not_found") return "존재하지 않는 회원입니다. 아이디를 다시 확인해 주세요.";
  if (code === "blocked") return "이용이 제한된 계정입니다. 고객센터로 문의해 주세요.";
  if (code === "type") return "현재 회원 유형은 공개몰 로그인이 제한되어 있습니다. 관리자에서 회원 구분을 일반회원으로 변경해 주세요.";
  if (code === "invalid") return "아이디 또는 비밀번호가 일치하지 않습니다.";
  return "로그인 정보를 확인해 주세요.";
}

function currentHajinLoginErrorMessage() {
  if (typeof window === "undefined") return "";
  return hajinLoginErrorMessage(new URLSearchParams(window.location.search).get("error"));
}

function hajinSignupErrorMessage(code?: string | null) {
  if (!code) return "";
  if (code === "duplicate") return "이미 사용 중인 아이디입니다. 다른 아이디를 입력해 주세요.";
  if (code === "blocked_username") return "사용할 수 없는 아이디입니다. 다른 아이디를 입력해 주세요.";
  if (code === "invalid") return "입력 정보를 확인해 주세요. 아이디는 영문/숫자 6~20자, 비밀번호는 8자 이상이어야 합니다.";
  return "회원가입 정보를 확인해 주세요.";
}

function currentHajinSignupErrorMessage() {
  if (typeof window === "undefined") return "";
  return hajinSignupErrorMessage(new URLSearchParams(window.location.search).get("error"));
}

function currentHajinSignupStep() {
  if (typeof window === "undefined") return "";
  return new URLSearchParams(window.location.search).get("step") || "";
}

function AccountPage() {
  const [view, setView] = useState<
    "login" | "find-id" | "find-password" | "guest-order"
  >("login");
  const [notice, setNotice] = useState(false);
  const [customer, setCustomer] = useState<HajinCustomerSession | null>(null);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [loginError, setLoginError] = useState(currentHajinLoginErrorMessage);
  const [loginSubmitting, setLoginSubmitting] = useState(false);
  useEffect(() => {
    let active = true;
    fetch("/api/store/hajin/me", { credentials: "include" })
      .then((response) => response.ok ? response.json() : null)
      .then((payload) => {
        if (active && payload?.authenticated) {
          const normalized = normalizeHajinCustomer(payload.customer);
          setCustomer(normalized);
          announceHajinCustomerSession(normalized);
        }
      })
      .catch(() => {})
      .finally(() => { if (active) setSessionChecked(true); });
    return () => { active = false; };
  }, []);
  const heading =
    view === "find-id"
      ? "아이디 찾기"
      : view === "find-password"
        ? "비밀번호 찾기"
        : view === "guest-order"
          ? "비회원 주문조회"
          : "로그인";
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setNotice(true);
  };
  const submitLogin = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const username = String(formData.get("username") || "").trim();
    const password = String(formData.get("password") || "");
    if (!username || !password) {
      setLoginError(hajinLoginErrorMessage("required"));
      return;
    }
    setLoginSubmitting(true);
    setLoginError("");
    try {
      const response = await fetch(form.action, {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      const finalUrl = new URL(response.url || "/account", window.location.origin);
      const errorCode = finalUrl.searchParams.get("error");
      if (errorCode) {
        setLoginError(hajinLoginErrorMessage(errorCode));
        window.history.replaceState(null, "", `/account?error=${encodeURIComponent(errorCode)}`);
        return;
      }
      const meResponse = await fetch("/api/store/hajin/me", { credentials: "include" });
      const payload = meResponse.ok ? await meResponse.json() : null;
      if (payload?.authenticated) {
        const normalized = normalizeHajinCustomer(payload.customer);
        setCustomer(normalized);
        announceHajinCustomerSession(normalized);
        window.history.replaceState(null, "", "/account");
        return;
      }
      setLoginError(hajinLoginErrorMessage("unknown"));
    } catch {
      setLoginError("로그인 처리 중 문제가 발생했습니다. 잠시 후 다시 시도해 주세요.");
    } finally {
      setLoginSubmitting(false);
    }
  };
  if (customer) return <HajinMyPage fallbackCustomer={customer} />;
  if (!sessionChecked) return <main className="mx-auto min-h-[640px] max-w-[430px] px-5 py-20 text-center text-sm text-stone-400">로그인 상태를 확인하고 있습니다.</main>;
  return (
    <main className="mx-auto min-h-[720px] max-w-[460px] px-4 pb-16 pt-9 sm:px-5 sm:pt-[56px] lg:min-h-[824px]">
      <h1 className="text-center text-[31px] font-black tracking-[-.08em] text-[#222] sm:text-[36px]">
        {heading}
      </h1>
      {view === "login" ? (
        <>
          <form action="/api/store/hajin/login" method="post" onSubmit={submitLogin} className="mt-8 rounded-[24px] border border-stone-200 bg-white p-5 shadow-[0_18px_48px_rgba(20,20,20,.08)] sm:mt-[52.8px] sm:rounded-none sm:border-0 sm:p-0 sm:shadow-none">
            <div className="grid gap-2 sm:gap-0">
              <input
                required
                name="username"
                autoComplete="username"
                className={`h-[54px] rounded-xl border px-4 text-base outline-none transition focus:border-stone-900 sm:rounded-none sm:px-3 ${loginError ? "border-rose-300 bg-rose-50/40" : "border-[#d7d7d7]"}`}
                placeholder="아이디"
                aria-label="아이디"
                aria-invalid={Boolean(loginError)}
                onChange={() => loginError ? setLoginError("") : undefined}
              />
              <input
                required
                name="password"
                autoComplete="current-password"
                className={`h-[54px] rounded-xl border px-4 text-base outline-none transition focus:border-stone-900 sm:rounded-none sm:border-t-0 sm:px-3 ${loginError ? "border-rose-300 bg-rose-50/40" : "border-stone-300"}`}
                placeholder="비밀번호"
                type="password"
                aria-label="비밀번호"
                aria-invalid={Boolean(loginError)}
                onChange={() => loginError ? setLoginError("") : undefined}
              />
            </div>
            {loginError ? (
              <p role="alert" className="mt-3 rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm font-bold leading-5 text-rose-700">
                {loginError}
              </p>
            ) : null}
            <button disabled={loginSubmitting} className="mt-4 h-[56px] w-full rounded-xl bg-[#222] text-lg font-bold text-white transition active:scale-[.99] disabled:cursor-wait disabled:bg-stone-400 sm:mt-[15px] sm:h-[60px] sm:rounded-none sm:text-xl">
              {loginSubmitting ? "확인 중..." : "로그인"}
            </button>
            <label className="mt-4 flex items-center gap-2 text-sm font-semibold text-[#545454] sm:mt-[14px]">
              <input type="checkbox" className="size-5 rounded border-stone-300 accent-stone-900 sm:size-6" />{" "}
              아이디 저장
            </label>
          </form>
          <button
            onClick={() => go("/signup")}
            className="mt-4 h-[52px] w-full rounded-xl border border-[#b2b2b2] bg-white text-base font-bold transition active:scale-[.99] sm:mt-[32px] sm:rounded-none sm:font-normal"
          >
            회원가입
          </button>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 px-2 text-sm text-stone-700 sm:mt-[15px] sm:h-[30px] sm:gap-4 sm:px-0 sm:text-base">
            <button onClick={() => setView("find-id")}>아이디 찾기</button>
            <span className="text-stone-300">|</span>
            <button onClick={() => setView("find-password")}>
              비밀번호 찾기
            </button>
            <span className="text-stone-300">|</span>
            <button onClick={() => setView("guest-order")}>
              비회원 구매/주문조회
            </button>
          </div>
        </>
      ) : (
        <AccountRecovery
          view={view}
          onBack={() => setView("login")}
          onSubmit={submit}
        />
      )}
      {notice ? (
        <Layer
          onClose={() => setNotice(false)}
          className="grid place-items-center p-5"
        >
          <section
            role="dialog"
            aria-modal="true"
            className="w-full max-w-[390px] bg-white p-7 text-center"
          >
            <h2 className="text-xl font-black">입력 정보를 확인해 주세요.</h2>
            <p className="mt-3 text-sm leading-6 text-stone-500">
              실제 회원·주문 조회는 새 서버의 인증 API와 연결됩니다.
            </p>
            <button
              onClick={() => setNotice(false)}
              className="mt-6 w-full bg-stone-900 py-4 font-bold text-white"
            >
              확인
            </button>
          </section>
        </Layer>
      ) : null}
    </main>
  );
}

type HajinMyPageData = {
  customer: { username: string; name: string; email: string | null; phone: string | null; rewardBalance: number; createdAt: string };
  addresses: Array<{ id: string; label: string | null; recipientName: string; phone: string; zipCode: string; address1: string; address2: string | null; isDefault: boolean }>;
  recentOrders: Array<{ id: string; orderNo: string; status: string; totalAmount: number; createdAt: string; shippingCarrier: string | null; trackingNumber: string | null }>;
  coupons: Array<{ id: string; title: string; code: string; discount: string; minOrderAmount: number }>;
  wishlists: Array<{ id: string; slug: string; name: string; unitPrice: number; imageUrl: string | null }>;
  inquiries: Array<{ id: string; subject: string; status: string; answer: string | null; createdAt: string }>;
  summary: { orderCount: number; couponCount: number; wishlistCount: number; inquiryCount: number; reviewCount: number };
};

type HajinOrderDetail = {
  id: string; orderNo: string; status: string; paymentMethod: string; subtotal: number; shippingFee: number; couponTitle: string | null; couponDiscount: number; totalAmount: number;
  depositName: string | null; recipientName: string; recipientPhone: string; recipientZip: string; recipientAddress1: string; recipientAddress2: string | null; deliveryMemo: string | null;
  shippingCarrier: string | null; trackingNumber: string | null; trackingUrl: string | null; shippingNote: string | null; cancelReason: string | null; createdAt: string; paidAt: string | null; shippedAt: string | null; deliveredAt: string | null; cancelledAt: string | null;
  bank: { label: string; bankName: string; accountNumber: string; accountHolder: string; description: string };
  cashReceipt: { type: string; issueType: string; number: string } | null;
  items: Array<{ id: string; productName: string; unitPrice: number; quantity: number; lineTotal: number; optionSummary: string | null; optionPrice: number | null; imageUrl: string; slug: string }>;
};

const hajinOrderStatusLabel: Record<string, string> = { PENDING_PAYMENT: "입금 대기", PAYMENT_ERROR: "결제 오류", PAID: "결제 완료", PREPARING: "상품 준비 중", SHIPPED: "배송 중", DELIVERED: "배송 완료", CANCELLED: "주문 취소", REFUNDED: "환불 완료" };
const hajinOrderDate = (value?: string | null) => value ? new Date(value).toLocaleString("ko-KR", { dateStyle: "medium", timeStyle: "short" }) : "-";

function HajinOrderDetailLayer({ order, loading, error, onClose }: { order: HajinOrderDetail | null; loading: boolean; error: string; onClose: () => void }) {
  const title = order ? `${order.orderNo} 주문 상세` : "주문 상세";
  const shippingStatus = order?.deliveredAt
    ? `배송 완료 ${hajinOrderDate(order.deliveredAt)}`
    : order?.shippedAt
      ? `출고 ${hajinOrderDate(order.shippedAt)}`
      : "출고 전";
  return <Layer onClose={onClose} className="grid place-items-center p-4">
    <section role="dialog" aria-modal="true" aria-label={title} className="max-h-[calc(100vh-2rem)] w-[760px] max-w-full overflow-y-auto rounded-2xl bg-white shadow-2xl">
      <header className="sticky top-0 z-10 flex items-center justify-between border-b border-stone-200 bg-white px-6 py-5">
        <div><h2 className="text-xl font-black text-stone-900">{title}</h2>{order ? <p className="mt-1 text-xs text-stone-500">주문일시 {hajinOrderDate(order.createdAt)}</p> : null}</div>
        <button type="button" onClick={onClose} className="grid size-9 place-items-center rounded-full text-2xl text-stone-500 hover:bg-stone-100" aria-label="주문 상세 닫기">×</button>
      </header>
      {loading ? <p className="p-16 text-center text-sm text-stone-500">주문 상세 정보를 불러오는 중입니다.</p> : error ? <div className="p-10 text-center"><p className="text-sm font-bold text-rose-600">{error}</p><button type="button" onClick={onClose} className="mt-5 rounded-lg bg-stone-900 px-5 py-3 text-sm font-bold text-white">확인</button></div> : order ? <div className="p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-stone-50 px-5 py-4"><div><p className="text-xs font-bold text-stone-500">주문 처리 상태</p><strong className="mt-1 block text-lg text-stone-900">{hajinOrderStatusLabel[order.status] || order.status}</strong></div><span className={`rounded-full px-3 py-1.5 text-xs font-black ${["CANCELLED", "REFUNDED", "PAYMENT_ERROR"].includes(order.status) ? "bg-rose-50 text-rose-600" : order.status === "DELIVERED" ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-[#1366dc]"}`}>{hajinOrderStatusLabel[order.status] || order.status}</span></div>
        <div className="mt-5 grid gap-3 text-xs sm:grid-cols-2"><p className="rounded-lg border border-stone-200 px-4 py-3"><strong className="block text-stone-900">결제 완료</strong><span className="mt-1 block text-stone-500">{hajinOrderDate(order.paidAt)}</span></p><p className="rounded-lg border border-stone-200 px-4 py-3"><strong className="block text-stone-900">배송 상태</strong><span className="mt-1 block text-stone-500">{shippingStatus}</span></p></div>
        {order.cancelledAt ? <div className="mt-3 rounded-lg border border-rose-100 bg-rose-50 px-4 py-3 text-xs text-rose-800"><strong>{order.status === "REFUNDED" ? "환불" : "취소"} 처리</strong><span className="ml-2">{hajinOrderDate(order.cancelledAt)}</span>{order.cancelReason ? <p className="mt-1">사유: {order.cancelReason}</p> : null}</div> : null}
        <section className="mt-7"><h3 className="border-b-2 border-stone-900 pb-3 text-base font-black">주문 상품</h3><div className="divide-y divide-stone-100">{order.items.map((item) => <article key={item.id} className="flex gap-4 py-4">{item.imageUrl ? <img src={item.imageUrl} alt="" className="size-16 shrink-0 rounded-lg border border-stone-100 object-contain" /> : <div className="size-16 shrink-0 rounded-lg bg-stone-100" />}<div className="min-w-0 flex-1"><strong className="block text-sm text-stone-900">{item.productName}</strong>{item.optionSummary ? <p className="mt-1 text-xs text-stone-500">옵션: {item.optionSummary}</p> : null}<p className="mt-2 text-xs text-stone-500">{Number(item.unitPrice).toLocaleString()}원 · {item.quantity}개</p></div><strong className="shrink-0 text-sm text-stone-900">{Number(item.lineTotal).toLocaleString()}원</strong></article>)}</div></section>
        <div className="mt-7 grid gap-6 md:grid-cols-2"><section><h3 className="border-b border-stone-300 pb-3 text-base font-black">배송 정보</h3><div className="mt-4 text-sm leading-6 text-stone-600"><p><strong className="text-stone-900">{order.recipientName} · {order.recipientPhone}</strong></p><p>[{order.recipientZip}] {order.recipientAddress1} {order.recipientAddress2 || ""}</p><p className="mt-2">배송 요청: {order.deliveryMemo || "없음"}</p>{order.shippingCarrier || order.trackingNumber ? <p className="mt-2 rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-800">{order.shippingCarrier || "택배사"} · {order.trackingNumber || "운송장 등록 전"}</p> : null}{order.trackingUrl ? <a href={order.trackingUrl} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs font-bold text-blue-700 underline">배송조회</a> : null}{order.shippingNote ? <p className="mt-2 text-xs text-stone-500">{order.shippingNote}</p> : null}</div></section><section><h3 className="border-b border-stone-300 pb-3 text-base font-black">결제 정보</h3><div className="mt-4 space-y-2 text-sm text-stone-600"><p className="flex justify-between"><span>상품금액</span><strong>{Number(order.subtotal).toLocaleString()}원</strong></p><p className="flex justify-between"><span>배송비</span><strong>{Number(order.shippingFee).toLocaleString()}원</strong></p>{order.couponDiscount ? <p className="flex justify-between text-hajin-600"><span>{order.couponTitle || "쿠폰 할인"}</span><strong>-{Number(order.couponDiscount).toLocaleString()}원</strong></p> : null}<p className="flex justify-between border-t border-stone-200 pt-3 font-black text-stone-900"><span>최종 결제금액</span><strong className="text-lg text-hajin-600">{Number(order.totalAmount).toLocaleString()}원</strong></p><p className="pt-1 text-xs">결제수단: {order.paymentMethod === "BANK_TRANSFER" ? "무통장입금" : "신용카드"}</p>{order.paymentMethod === "BANK_TRANSFER" && order.bank.bankName ? <p className="rounded-lg bg-stone-50 p-3 text-xs">{order.bank.bankName} {order.bank.accountNumber} ({order.bank.accountHolder})<br />입금자명: {order.depositName || "-"}</p> : null}{order.cashReceipt ? <p className="rounded-lg bg-stone-50 p-3 text-xs">현금영수증: {order.cashReceipt.type === "INCOME" ? "소득공제" : "지출증빙"} · {order.cashReceipt.number}</p> : null}</div></section></div>
      </div> : null}
    </section>
  </Layer>;
}

function HajinMyPage({ fallbackCustomer }: { fallbackCustomer: { username: string; name: string } }) {
  const [data, setData] = useState<HajinMyPageData | null>(null);
  const [screen, setScreen] = useState<"home" | "verify" | "edit">("home");
  const [currentPassword, setCurrentPassword] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [orderDetail, setOrderDetail] = useState<HajinOrderDetail | null>(null);
  const [orderDetailLoading, setOrderDetailLoading] = useState(false);
  const [orderDetailError, setOrderDetailError] = useState("");
  const refresh = () => fetch("/api/store/hajin/account", { credentials: "include" }).then((response) => response.ok ? response.json() : null).then((payload) => { if (payload) setData(payload); }).catch(() => {});
  useEffect(() => { void refresh(); }, []);
  const openOrderDetail = async (orderId: string) => {
    setOrderDetail(null); setOrderDetailError(""); setOrderDetailLoading(true);
    try {
      const response = await fetch(`/api/store/hajin/account/orders/${encodeURIComponent(orderId)}`, { credentials: "include", cache: "no-store" });
      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload?.order) throw new Error(payload?.message || "주문 상세 정보를 불러오지 못했습니다.");
      setOrderDetail(payload.order);
    } catch (error) { setOrderDetailError(error instanceof Error ? error.message : "주문 상세 정보를 불러오지 못했습니다."); }
    finally { setOrderDetailLoading(false); }
  };
  useEffect(() => {
    if (screen !== "home" || !data?.recentOrders.length) return;
    const main = [...document.querySelectorAll("main")].find((element) => element.querySelector("h1")?.textContent === "마이페이지");
    if (!main) return;
    const handlers: Array<() => void> = [];
    const orderRows = [...main.querySelectorAll("ul li")].slice(0, data.recentOrders.length);
    orderRows.forEach((row, index) => {
      const order = data.recentOrders[index];
      const handler = () => void openOrderDetail(order.id);
      const keyHandler = (event: KeyboardEvent) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); handler(); } };
      row.setAttribute("role", "button"); row.setAttribute("tabindex", "0");
      (row as HTMLElement).style.cursor = "pointer";
      row.addEventListener("click", handler);
      (row as HTMLElement).addEventListener("keydown", keyHandler);
      handlers.push(() => { row.removeEventListener("click", handler); (row as HTMLElement).removeEventListener("keydown", keyHandler); });
    });
    return () => handlers.forEach((cleanup) => cleanup());
  }, [data, screen]);
  const verify = async (event: FormEvent) => {
    event.preventDefault(); setBusy(true); setMessage("");
    try {
      const response = await fetch("/api/store/hajin/account", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ password: currentPassword }) });
      const payload = await response.json().catch(() => null);
      if (!response.ok) throw new Error(payload?.message || "비밀번호를 확인할 수 없습니다.");
      setScreen("edit");
    } catch (error) { setMessage(error instanceof Error ? error.message : "비밀번호를 확인할 수 없습니다."); } finally { setBusy(false); }
  };
  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault(); setBusy(true); setMessage("");
    const form = new FormData(event.currentTarget);
    const payload = {
      currentPassword, name: form.get("name"), email: form.get("email"), phone: form.get("phone"),
      newPassword: form.get("newPassword"), newPasswordConfirm: form.get("newPasswordConfirm"),
      address: { label: form.get("shippingLabel"), recipientName: form.get("shippingRecipientName"), phone: form.get("shippingPhone"), zipCode: form.get("shippingZip"), address1: form.get("shippingAddress1"), address2: form.get("shippingAddress2") },
    };
    try {
      const response = await fetch("/api/store/hajin/account", { method: "PATCH", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      const result = await response.json().catch(() => null);
      if (!response.ok) throw new Error(result?.message || "회원정보를 저장할 수 없습니다.");
      await refresh(); setCurrentPassword(""); setScreen("home"); setMessage("회원정보가 저장되었습니다.");
    } catch (error) { setMessage(error instanceof Error ? error.message : "회원정보를 저장할 수 없습니다."); } finally { setBusy(false); }
  };
  const profile = data?.customer || { username: fallbackCustomer.username, name: fallbackCustomer.name, email: null, phone: null, rewardBalance: 0, createdAt: "" };
  const defaultAddress = data?.addresses.find((address) => address.isDefault) || data?.addresses[0];
  const searchAccountAddress = () => openHajinPostcode(({ zipCode, address }) => {
    const zipInput = document.querySelector<HTMLInputElement>('input[name="shippingZip"]');
    const addressInput = document.querySelector<HTMLInputElement>('input[name="shippingAddress1"]');
    const detailInput = document.querySelector<HTMLInputElement>('input[name="shippingAddress2"]');
    if (zipInput) zipInput.value = zipCode;
    if (addressInput) addressInput.value = address;
    window.setTimeout(() => detailInput?.focus(), 0);
  });
  if (screen === "verify") return <main className="mx-auto min-h-[650px] max-w-[540px] px-5 py-16"><button onClick={() => { setScreen("home"); setMessage(""); }} className="text-sm text-stone-500">‹ 마이페이지로 돌아가기</button><h1 className="mt-8 text-center text-3xl font-black">회원정보 수정</h1><section className="mt-10 border-t-2 border-stone-900 pt-8"><h2 className="text-xl font-bold">비밀번호 재확인</h2><p className="mt-3 text-sm leading-6 text-stone-500">회원님의 정보를 안전하게 보호하기 위해 비밀번호를 한 번 더 확인합니다.</p><form onSubmit={verify} className="mt-7 grid gap-3"><input required autoFocus value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} autoComplete="current-password" type="password" className="border border-stone-300 px-4 py-4" placeholder="현재 비밀번호" /><button disabled={busy} className="bg-stone-900 py-4 font-bold text-white disabled:bg-stone-400">{busy ? "확인 중..." : "확인"}</button></form>{message ? <p role="alert" className="mt-4 text-sm font-bold text-rose-600">{message}</p> : null}</section></main>;
  if (screen === "edit") return <main className="mx-auto min-h-[780px] max-w-[720px] px-5 py-16"><button onClick={() => { setScreen("home"); setCurrentPassword(""); setMessage(""); }} className="text-sm text-stone-500">‹ 마이페이지로 돌아가기</button><h1 className="mt-8 text-center text-3xl font-black">회원정보 수정</h1><form onSubmit={save} className="mt-10"><fieldset className="grid gap-4 border-t-2 border-stone-900 py-7"><legend className="pt-6 text-lg font-black">기본 정보</legend><label className="grid gap-2 text-sm font-bold">아이디<input value={profile.username} disabled className="border border-stone-200 bg-stone-100 px-4 py-3 font-normal text-stone-500" /></label><label className="grid gap-2 text-sm font-bold">이름<input name="name" required defaultValue={profile.name} className="border border-stone-300 px-4 py-3 font-normal" /></label><label className="grid gap-2 text-sm font-bold">휴대폰 번호<input name="phone" required defaultValue={profile.phone || ""} inputMode="tel" className="border border-stone-300 px-4 py-3 font-normal" placeholder="010-0000-0000" /></label><label className="grid gap-2 text-sm font-bold">이메일<input name="email" type="email" defaultValue={profile.email || ""} className="border border-stone-300 px-4 py-3 font-normal" placeholder="name@example.com" /></label></fieldset><fieldset className="grid gap-4 border-t border-stone-200 py-7"><legend className="pt-6 text-lg font-black">비밀번호 변경 <span className="font-normal text-sm text-stone-400">(변경할 때만 입력)</span></legend><label className="grid gap-2 text-sm font-bold">새 비밀번호<input name="newPassword" minLength={8} autoComplete="new-password" type="password" className="border border-stone-300 px-4 py-3 font-normal" placeholder="8자 이상" /></label><label className="grid gap-2 text-sm font-bold">새 비밀번호 확인<input name="newPasswordConfirm" minLength={8} autoComplete="new-password" type="password" className="border border-stone-300 px-4 py-3 font-normal" /></label></fieldset><fieldset className="grid gap-4 border-t border-stone-200 py-7"><legend className="pt-6 text-lg font-black">기본 배송지</legend><label className="grid gap-2 text-sm font-bold">배송지명<input name="shippingLabel" defaultValue={defaultAddress?.label || "기본 배송지"} className="border border-stone-300 px-4 py-3 font-normal" /></label><label className="grid gap-2 text-sm font-bold">받는 분<input name="shippingRecipientName" required defaultValue={defaultAddress?.recipientName || profile.name} className="border border-stone-300 px-4 py-3 font-normal" /></label><label className="grid gap-2 text-sm font-bold">연락처<input name="shippingPhone" required defaultValue={defaultAddress?.phone || profile.phone || ""} className="border border-stone-300 px-4 py-3 font-normal" /></label><label className="grid gap-2 text-sm font-bold">우편번호<div className="flex gap-2"><input name="shippingZip" required defaultValue={defaultAddress?.zipCode || ""} inputMode="numeric" className="min-w-0 flex-1 border border-stone-300 px-4 py-3 font-normal" placeholder="주소검색으로 입력" /><button type="button" onClick={searchAccountAddress} className="shrink-0 border border-stone-900 bg-stone-900 px-4 text-sm font-bold text-white">주소검색</button></div></label><label className="grid gap-2 text-sm font-bold">주소<input name="shippingAddress1" required readOnly onClick={searchAccountAddress} defaultValue={defaultAddress?.address1 || ""} className="cursor-pointer border border-stone-300 bg-stone-50 px-4 py-3 font-normal" placeholder="주소검색을 눌러 입력해 주세요" /></label><label className="grid gap-2 text-sm font-bold">상세 주소<input name="shippingAddress2" defaultValue={defaultAddress?.address2 || ""} className="border border-stone-300 px-4 py-3 font-normal" placeholder="동/호수, 건물명 등" /></label></fieldset>{message ? <p role="alert" className="mb-4 text-sm font-bold text-rose-600">{message}</p> : null}<button disabled={busy} className="w-full bg-hajin-600 py-4 font-bold text-white disabled:bg-stone-400">{busy ? "저장 중..." : "회원정보 저장"}</button></form></main>;
  const summary = data?.summary || { orderCount: 0, couponCount: 0, wishlistCount: 0, inquiryCount: 0, reviewCount: 0 };
  if (orderDetail || orderDetailLoading || orderDetailError) return <HajinOrderDetailLayer order={orderDetail} loading={orderDetailLoading} error={orderDetailError} onClose={() => { setOrderDetail(null); setOrderDetailError(""); }} />;
  return <main className="mx-auto min-h-[700px] max-w-[1000px] px-4 py-9 sm:px-5 sm:py-14"><h1 className="text-center text-[28px] font-black sm:text-3xl">마이페이지</h1><section className="mt-7 rounded-2xl border-2 border-stone-900 px-5 py-6 shadow-[0_14px_34px_rgba(20,20,20,.06)] sm:mt-10 sm:flex sm:items-center sm:justify-between sm:rounded-none sm:border-x-0 sm:px-6 sm:py-8 sm:shadow-none"><div><p className="text-lg font-bold sm:text-xl"><strong className="text-hajin-600">{profile.name}</strong>님, 안녕하세요.</p><p className="mt-2 break-words text-sm text-stone-500">아이디 {profile.username} · 보유 포인트 {Number(profile.rewardBalance).toLocaleString()}P</p></div><button onClick={() => { setScreen("verify"); setMessage(""); }} className="mt-5 w-full rounded-xl border border-stone-700 px-5 py-3 text-sm font-bold sm:mt-0 sm:w-auto sm:rounded-none">회원정보 수정</button></section>{message ? <p className="mt-4 text-center text-sm font-bold text-hajin-600">{message}</p> : null}<section className="mt-5 grid grid-cols-2 overflow-hidden rounded-2xl border border-stone-200 sm:mt-7 sm:grid-cols-4 sm:rounded-none">{[["주문/배송", summary.orderCount], ["사용 가능 쿠폰", summary.couponCount], ["관심상품", summary.wishlistCount], ["1:1 문의", summary.inquiryCount]].map(([label, count]) => <div key={String(label)} className="border-b border-r border-stone-200 p-4 text-center last:border-r-0 sm:border-b-0 sm:p-5"><p className="text-xs text-stone-500 sm:text-sm">{label}</p><p className="mt-2 text-2xl font-black">{count}<span className="ml-1 text-sm font-medium">건</span></p></div>)}</section><section className="mt-6 grid gap-4 md:mt-8 md:grid-cols-2"><article className="rounded-2xl border border-stone-200 p-5 sm:rounded-none sm:p-6"><h2 className="font-black">최근 주문</h2>{data === null ? <p className="mt-5 text-sm text-stone-400">주문 정보를 불러오는 중입니다.</p> : data.recentOrders.length ? <ul className="mt-4 divide-y divide-stone-100">{data.recentOrders.map((order) => <li key={order.id} className="py-3 text-sm"><strong className="block sm:inline">{order.orderNo}</strong><span className="mt-1 block text-stone-500 sm:ml-3 sm:inline">{hajinOrderStatusLabel[order.status] || order.status} · {Number(order.totalAmount).toLocaleString()}원</span></li>)}</ul> : <p className="mt-5 text-sm text-stone-500">최근 주문 내역이 없습니다.</p>}</article><article className="rounded-2xl border border-stone-200 p-5 sm:rounded-none sm:p-6"><h2 className="font-black">배송지 목록 ({data?.addresses.length || 0})</h2>{data?.addresses.map((address) => <div key={address.id} className="mt-3 border-t border-stone-100 pt-3 text-sm leading-6 text-stone-600"><strong className="text-stone-900">{address.label || "배송지"}{address.isDefault ? " · 기본" : ""}</strong><p>{address.recipientName} · {address.phone}</p><p>[{address.zipCode}] {address.address1} {address.address2 || ""}</p></div>)}<button onClick={() => { setScreen("verify"); setMessage(""); }} className="mt-5 text-sm font-bold underline">배송지 및 회원정보 관리</button></article></section><section className="mt-4 grid gap-4 md:mt-5 md:grid-cols-3"><article className="rounded-2xl border border-stone-200 p-5 sm:rounded-none"><h2 className="font-black">쿠폰</h2>{data?.coupons.length ? data.coupons.map((coupon) => <p key={coupon.id} className="mt-3 text-sm"><strong>{coupon.title}</strong><br /><span className="break-all text-stone-500">{coupon.discount} · {coupon.code}</span></p>) : <p className="mt-4 text-sm text-stone-500">사용 가능한 쿠폰이 없습니다.</p>}</article><article className="rounded-2xl border border-stone-200 p-5 sm:rounded-none"><h2 className="font-black">관심상품</h2>{data?.wishlists.length ? data.wishlists.map((item) => <button key={item.id} onClick={() => go(`/products/${item.slug}`)} className="mt-3 block text-left text-sm"><strong className="line-clamp-2">{item.name}</strong><br /><span className="text-stone-500">{Number(item.unitPrice).toLocaleString()}원</span></button>) : <p className="mt-4 text-sm text-stone-500">관심상품이 없습니다.</p>}</article><article className="rounded-2xl border border-stone-200 p-5 sm:rounded-none"><h2 className="font-black">1:1 문의</h2>{data?.inquiries.length ? data.inquiries.map((item) => <p key={item.id} className="mt-3 text-sm"><strong>{item.subject}</strong><br /><span className="text-stone-500">{item.status === "ANSWERED" ? "답변 완료" : "답변 대기"}{item.answer ? " · 답변 있음" : ""}</span></p>) : <p className="mt-4 text-sm text-stone-500">문의 내역이 없습니다.</p>}</article></section><div className="mt-6 grid gap-3 sm:mt-8 sm:grid-cols-3"><button onClick={() => { setScreen("verify"); setMessage(""); }} className="rounded-xl border border-stone-300 py-4 font-bold sm:rounded-none">회원정보 수정</button><button onClick={() => go("/")} className="rounded-xl border border-stone-300 py-4 font-bold sm:rounded-none">쇼핑 계속하기</button><form action="/api/store/hajin/logout" method="post"><button className="h-full w-full rounded-xl bg-stone-900 py-4 font-bold text-white sm:rounded-none">로그아웃</button></form></div></main>;
}

function AccountRecovery({ view, onBack, onSubmit }: { view: "find-id" | "find-password" | "guest-order"; onBack: () => void; onSubmit: (event: FormEvent) => void }) {
  const guest = view === "guest-order";
  return <><p className="mt-4 text-center text-sm leading-6 text-stone-500">{guest ? "주문 시 입력한 정보로 주문 내역을 확인할 수 있습니다." : "본인 인증 후 가입 정보를 확인할 수 있습니다."}</p><form onSubmit={onSubmit} className="mt-7 rounded-[24px] border border-stone-200 bg-white p-5 shadow-[0_18px_48px_rgba(20,20,20,.08)] sm:mt-10 sm:rounded-none sm:border-x-0 sm:border-b-0 sm:border-t-2 sm:border-stone-900 sm:p-0 sm:pt-7 sm:shadow-none"><div className="grid gap-3">{guest ? <><input required className="rounded-xl border border-stone-300 px-4 py-4 text-base outline-none focus:border-stone-900 sm:rounded-none sm:text-sm" placeholder="주문번호" /><input required className="rounded-xl border border-stone-300 px-4 py-4 text-base outline-none focus:border-stone-900 sm:rounded-none sm:text-sm" placeholder="주문 시 입력한 비밀번호" type="password" /></> : <><input required className="rounded-xl border border-stone-300 px-4 py-4 text-base outline-none focus:border-stone-900 sm:rounded-none sm:text-sm" placeholder="이름" />{view === "find-password" ? <input required className="rounded-xl border border-stone-300 px-4 py-4 text-base outline-none focus:border-stone-900 sm:rounded-none sm:text-sm" placeholder="아이디" /> : null}<div className="grid gap-2 sm:flex"><input required className="min-w-0 flex-1 rounded-xl border border-stone-300 px-4 py-4 text-base outline-none focus:border-stone-900 sm:rounded-none sm:text-sm" placeholder="휴대폰 번호" /><button type="button" className="h-12 shrink-0 rounded-xl border border-stone-400 px-4 text-sm font-bold sm:h-auto sm:rounded-none">인증</button></div></>}<button className="mt-2 rounded-xl bg-stone-900 py-4 font-bold text-white sm:mt-3 sm:rounded-none">{guest ? "주문조회" : view === "find-id" ? "아이디 찾기" : "비밀번호 재설정"}</button></div></form><button onClick={onBack} className="mt-5 w-full py-3 text-sm text-stone-500 sm:mt-6">‹ 로그인으로 돌아가기</button></>;
}

function SignupPage() {
  const signupStep = currentHajinSignupStep();
  const signupError = currentHajinSignupErrorMessage();
  const [detailsOpen, setDetailsOpen] = useState(() => signupStep === "terms" || signupStep === "profile" || Boolean(signupError));
  const benefits = [["ⓟ", "할인 포인트 적립", "통합회원 가입 시\n1,000P 자동 적립"], ["5%", "쿠폰 제공", "상품 구매시 사용 가능한\n5%할인쿠폰 제공"], ["▣", "프로모션 참여", "상품가격할인 혜택이 있는\n다양한 프로모션 참여"], ["%", "유익한 정보 제공", "할인혜택, 맞춤 상품 등의\n다양한 정보 제공"]];
  if (detailsOpen) return <SignupDetails initialProfileOpen={signupStep === "profile" || Boolean(signupError)} onBack={() => setDetailsOpen(false)} />;
  return <main className="mx-auto min-h-[720px] px-4 pb-14 pt-8 sm:px-5 sm:pt-0 lg:min-h-[764px]"><h1 className="text-center text-[28px] font-black leading-tight tracking-[-.08em] text-[#333] sm:text-[36px] sm:font-semibold sm:leading-[92px]">전자랜드 온라인점 회원가입</h1><p className="mt-3 text-center text-sm font-normal leading-6 text-[#555] sm:-mt-px sm:leading-[39px]">회원가입 후 포인트 적립 및 다양한 혜택을 받아보세요.</p><section className="mx-auto mt-9 grid max-w-[460px] grid-cols-2 gap-4 sm:mt-[80px] sm:max-w-[calc(100%-40px)] sm:gap-x-5 sm:gap-y-8 lg:w-[865px] lg:max-w-none lg:grid-cols-[121px_153px_164px_157px] lg:gap-x-[90px] lg:gap-y-0">{benefits.map(([symbol, title, description]) => <div key={title} className="rounded-2xl border border-stone-100 bg-white p-4 text-center shadow-[0_12px_28px_rgba(20,20,20,.05)] sm:rounded-none sm:border-0 sm:p-0 sm:shadow-none"><span className="mx-auto grid size-[72px] place-items-center rounded-full bg-stone-100 text-[24px] font-black text-stone-900 sm:size-[104px] sm:text-[32px] lg:size-[120px] lg:text-[36px]">{symbol}</span><h2 className="mt-3 text-[15px] font-bold leading-5 sm:mt-[18px] sm:whitespace-nowrap sm:text-[18px] sm:leading-[23px]">{title}</h2><p className="mt-2 whitespace-pre-line text-[12px] leading-[18px] text-stone-700 sm:mt-[10px] sm:text-sm sm:leading-[21px]">{description}</p></div>)}</section><div className="mx-auto mt-8 w-full max-w-[435px] sm:mt-[50px]"><button onClick={() => setDetailsOpen(true)} className="h-[56px] w-full rounded-xl bg-[#222] text-base font-bold text-white sm:h-[60px] sm:rounded-none sm:text-lg sm:font-normal">통합회원 가입</button></div></main>;
}

function SignupDetails({ onBack, initialProfileOpen = false }: { onBack: () => void; initialProfileOpen?: boolean }) {
  const [profileOpen, setProfileOpen] = useState(initialProfileOpen);
  const [terms, setTerms] = useState([false, false, false, false, false]);
  const [expandedTerm, setExpandedTerm] = useState<number | null>(null);
  const termLabels = [
    "(필수) 전자랜드 온라인점 회원약관 동의",
    "(필수) 전자랜드 온라인점 개인정보처리 동의",
    "(필수) 전자랜드 온라인점 멤버십 서비스 약관 동의",
    "(선택) 마케팅 목적의 개인정보 수집 및 이용 동의",
    "(선택) 개인정보의 선택적인 제공에 동의합니다.",
  ];
  const termDetails = [
    {
      title: "서비스 이용 기본 약관",
      items: [
        "회원은 전자랜드 온라인점 쇼핑몰에서 상품 검색, 장바구니, 주문, 배송조회, 고객센터 서비스를 이용할 수 있습니다.",
        "회원 정보는 본인 확인과 주문 처리 목적에 맞게 정확하게 입력해야 하며, 타인의 정보를 사용할 수 없습니다.",
        "부정 주문, 시스템 악용, 타 회원에게 피해를 주는 행위가 확인될 경우 서비스 이용이 제한될 수 있습니다.",
      ],
    },
    {
      title: "개인정보 수집 및 이용",
      items: [
        "수집 항목: 아이디, 비밀번호, 이름, 휴대폰번호, 이메일, 배송지 정보, 주문 및 결제 이력",
        "이용 목적: 회원 식별, 주문/배송 처리, 고객 상담, 쿠폰 및 포인트 제공, 서비스 품질 개선",
        "보관 기간: 회원 탈퇴 시까지 보관하며, 관계 법령에 따라 필요한 정보는 정해진 기간 동안 분리 보관합니다.",
      ],
    },
    {
      title: "멤버십 서비스 약관",
      items: [
        "회원은 구매 및 이벤트 참여 조건에 따라 포인트, 쿠폰, 회원 전용 혜택을 받을 수 있습니다.",
        "쿠폰과 포인트의 사용 조건, 유효기간, 적용 대상은 각 혜택별 안내 기준을 따릅니다.",
        "취소, 반품, 환불이 발생하면 지급된 혜택은 회수되거나 사용이 제한될 수 있습니다.",
      ],
    },
    {
      title: "마케팅 정보 수신",
      items: [
        "동의 시 할인 행사, 신상품, 기획전, 쿠폰 안내를 문자, 알림, 이메일 등으로 받을 수 있습니다.",
        "마케팅 수신 동의는 선택 사항이며, 동의하지 않아도 회원가입과 기본 서비스 이용에는 제한이 없습니다.",
        "수신 동의는 마이페이지 회원정보 또는 고객센터를 통해 언제든지 철회할 수 있습니다.",
      ],
    },
    {
      title: "개인정보 선택 제공",
      items: [
        "이벤트 참여, 제휴 혜택 제공, 맞춤 상담이 필요한 경우 필요한 범위 내에서 선택 정보를 활용할 수 있습니다.",
        "제공 대상, 제공 항목, 이용 목적, 보관 기간은 개별 이벤트 또는 혜택 안내에서 별도로 고지합니다.",
        "선택 제공에 동의하지 않아도 필수 회원 서비스 이용에는 영향을 주지 않습니다.",
      ],
    },
  ];
  const setAll = (checked: boolean) => setTerms(terms.map(() => checked));
  if (profileOpen)
    return <SignupProfileForm onBack={() => setProfileOpen(false)} />;
  return (
    <main className="mx-auto min-h-[820px] max-w-[800px] px-4 pb-14 pt-9 sm:px-5 sm:pt-[47px] lg:px-0">
      <button onClick={onBack} className="sr-only">
        가입 유형 선택으로 돌아가기
      </button>
      <h1 className="text-center text-[28px] font-black tracking-[-.08em] sm:text-[36px]">
        전자랜드 온라인점 회원가입
      </h1>
      <p className="mt-3 text-center text-sm leading-6 text-stone-600 sm:leading-5">
        전자랜드 온라인점 통합회원으로 가입하시면 전자랜드 온라인점과
        <br />
        전국 매장에서 포인트 적립 및 다양한 혜택을 받으실 수 있으며 프로모션 및
        다양한 이벤트 참여가 가능합니다.
        <br />
        필수 약관 동의 후 가입 정보를 입력하면 바로 회원가입할 수 있습니다.
      </p>
      <section className="mt-6 sm:mt-[15px]">
        <div className="grid grid-cols-3 gap-2 border-b border-stone-800 pb-4 sm:gap-0">
          <StepMark
            active
            icon="▤"
            step="STEP 1"
            label="약관동의"
          />
          <StepMark icon="▧" step="STEP 2" label="정보입력" />
          <StepMark icon="▣" step="STEP 3" label="가입완료" />
        </div>
        <div className="mt-4 overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-[0_14px_34px_rgba(20,20,20,.06)] sm:mt-0 sm:rounded-none sm:border-x-0 sm:border-t-0 sm:shadow-none">
          <label className="flex items-center gap-3 border-b border-stone-300 bg-stone-50 px-4 py-4 text-sm font-black sm:gap-2 sm:bg-white sm:px-2 sm:py-3">
            <input
              checked={terms.every(Boolean)}
              onChange={(event) => setAll(event.target.checked)}
              type="checkbox"
              className="size-5 shrink-0 accent-rose-500 sm:size-6"
            />{" "}
            <span>전체동의</span>
          </label>
          {termLabels.map((label, index) => {
            const open = expandedTerm === index;
            const detail = termDetails[index];
            return (
              <div
                key={label}
                className={`border-b border-stone-100 px-4 py-3.5 last:border-b-0 transition ${open ? "bg-[#fff8fa]" : "bg-white"}`}
              >
                <div className="flex items-center gap-3">
                  <label className="flex min-w-0 flex-1 items-center gap-3">
                    <input
                      checked={terms[index]}
                      onChange={(event) =>
                        setTerms(
                          terms.map((value, itemIndex) =>
                            itemIndex === index ? event.target.checked : value,
                          ),
                        )
                      }
                      type="checkbox"
                      className="size-5 shrink-0 accent-rose-500 sm:size-6"
                    />
                    <span
                      className={
                        label.startsWith("(선택)")
                          ? "min-w-0 flex-1 truncate text-[13px] font-semibold leading-5 text-stone-600 sm:text-sm"
                          : "min-w-0 flex-1 truncate text-[13px] font-bold leading-5 text-stone-900 sm:text-sm"
                      }
                      title={label}
                    >
                      {label}
                    </span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setExpandedTerm(open ? null : index)}
                    className={`inline-flex h-9 w-6 shrink-0 items-center justify-center bg-transparent text-[24px] font-light leading-none transition ${open ? "rotate-90 text-rose-500" : "-rotate-90 text-stone-500 hover:text-stone-900"}`}
                    aria-expanded={open}
                    aria-label={`${label} 상세 ${open ? "닫기" : "열기"}`}
                  >
                    <span aria-hidden>‹</span>
                  </button>
                </div>
                {open ? (
                  <div className="mt-3 rounded-2xl border border-rose-100 bg-white px-4 py-3.5 shadow-[0_12px_30px_rgba(15,23,42,.05)]">
                    <p className="text-xs font-black uppercase tracking-[.12em] text-rose-500">{detail.title}</p>
                    <ul className="mt-2 grid gap-2 text-[12px] leading-5 text-stone-600">
                      {detail.items.map((item) => (
                        <li key={item} className="flex gap-2">
                          <span className="mt-2 size-1 shrink-0 rounded-full bg-rose-400" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </section>
      <button onClick={() => setProfileOpen(true)} disabled={!terms.slice(0, 3).every(Boolean)} className="mt-8 w-full rounded-xl bg-hajin-600 py-4 font-bold text-white disabled:cursor-not-allowed disabled:bg-stone-300 sm:mt-10 sm:rounded-none">가입 정보 입력</button>
    </main>
  );
}

function StepMark({
  active = false,
  icon,
  step,
  label,
}: {
  active?: boolean;
  icon: string;
  step: string;
  label: string;
}) {
  return (
    <div className={`grid min-w-0 justify-items-center gap-1 rounded-2xl px-1.5 py-3 text-center sm:flex sm:items-center sm:gap-3 sm:rounded-none sm:px-0 sm:py-0 sm:text-left ${active ? "bg-rose-50 sm:bg-transparent" : "bg-stone-50 sm:bg-transparent"}`}>
      <span
        className={`grid size-9 place-items-center rounded-full text-lg sm:size-14 sm:text-2xl lg:size-20 lg:text-3xl ${active ? "bg-rose-500 text-white" : "bg-stone-100 text-stone-300"}`}
      >
        {icon}
      </span>
      <span className="min-w-0">
        <strong
          className={`block whitespace-nowrap text-[11px] leading-4 sm:text-sm lg:text-lg ${active ? "text-rose-500" : "text-stone-800"}`}
        >
          {step}
        </strong>
        <span className="block whitespace-nowrap text-[12px] font-bold leading-4 sm:text-sm lg:text-base">{label}</span>
      </span>
    </div>
  );
}

function SignupProfileForm({ onBack }: { onBack: () => void }) {
  const signupZipRef = useRef<HTMLInputElement>(null);
  const signupAddressRef = useRef<HTMLInputElement>(null);
  const signupAddress2Ref = useRef<HTMLInputElement>(null);
  const [signupError, setSignupError] = useState(currentHajinSignupErrorMessage);
  const [signupSubmitting, setSignupSubmitting] = useState(false);
  const searchSignupAddress = () => openHajinPostcode(({ zipCode, address }) => {
    if (signupZipRef.current) signupZipRef.current.value = zipCode;
    if (signupAddressRef.current) signupAddressRef.current.value = address;
    window.setTimeout(() => signupAddress2Ref.current?.focus(), 0);
  });
  const submitSignup = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const username = String(formData.get("username") || "").trim();
    const password = String(formData.get("password") || "");
    if (!/^[A-Za-z0-9]{6,20}$/.test(username) || password.length < 8) {
      setSignupError(hajinSignupErrorMessage("invalid"));
      return;
    }
    setSignupSubmitting(true);
    setSignupError("");
    try {
      const response = await fetch(form.action, {
        method: "POST",
        body: formData,
        credentials: "include",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload?.ok) {
        const code = String(payload?.error || "invalid").toLowerCase();
        setSignupError(hajinSignupErrorMessage(code));
        if (typeof window !== "undefined") {
          window.history.replaceState(null, "", `/signup?step=profile&error=${encodeURIComponent(code)}`);
        }
        return;
      }
      announceHajinCustomerSession(payload.customer);
      if (typeof window !== "undefined") window.history.replaceState(null, "", "/");
      go("/");
    } catch {
      setSignupError("회원가입 처리 중 문제가 발생했습니다. 잠시 후 다시 시도해 주세요.");
    } finally {
      setSignupSubmitting(false);
    }
  };
  return (
    <main className="mx-auto max-w-[560px] px-4 py-10 sm:px-5 sm:py-16">
      <button onClick={onBack} className="text-sm text-stone-500">
        ‹ 약관동의
      </button>
      <h1 className="mt-6 text-center text-[28px] font-black sm:text-3xl">회원정보 입력</h1>
      <p className="mt-3 text-center text-sm text-stone-500">
        인증 절차 없이 가입에 필요한 정보를 입력해 주세요.
      </p>
      <form action="/api/store/hajin/register" method="post" onSubmit={submitSignup} className="mt-8 rounded-[24px] border border-stone-200 bg-white p-5 shadow-[0_18px_48px_rgba(20,20,20,.08)] sm:mt-10 sm:rounded-none sm:border-x-0 sm:border-b-0 sm:border-t-2 sm:border-stone-900 sm:p-0 sm:shadow-none">
        {signupError ? (
          <p role="alert" className="mb-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-bold leading-5 text-rose-600 sm:rounded-none">
            {signupError}
          </p>
        ) : null}
        <input type="hidden" name="customerType" value="PERSONAL" />
        <fieldset className="grid gap-4 py-7">
          <legend className="pt-6 text-lg font-black">기본 정보</legend>
          <label className="grid gap-2 text-sm font-bold">
            아이디
            <input name="username" required minLength={6} maxLength={20} pattern="[A-Za-z0-9]{6,20}" autoComplete="username" onChange={() => signupError ? setSignupError("") : undefined} className="rounded-xl border border-stone-300 px-4 py-3 font-normal outline-none focus:border-stone-900 sm:rounded-none" placeholder="영문, 숫자 6~20자" />
          </label>
          <label className="grid gap-2 text-sm font-bold">
            비밀번호
            <input name="password" required minLength={8} autoComplete="new-password" type="password" className="rounded-xl border border-stone-300 px-4 py-3 font-normal outline-none focus:border-stone-900 sm:rounded-none" placeholder="8자 이상 입력" />
          </label>
          <label className="grid gap-2 text-sm font-bold">
            이름
            <input name="name" required autoComplete="name" className="rounded-xl border border-stone-300 px-4 py-3 font-normal outline-none focus:border-stone-900 sm:rounded-none" />
          </label>
          <label className="grid gap-2 text-sm font-bold">
            휴대폰 번호
            <input name="phone" required autoComplete="tel" className="rounded-xl border border-stone-300 px-4 py-3 font-normal outline-none focus:border-stone-900 sm:rounded-none" placeholder="010-0000-0000" />
          </label>
          <label className="grid gap-2 text-sm font-bold">
            이메일 <span className="font-normal text-stone-400">(선택)</span>
            <input name="email" type="email" autoComplete="email" className="rounded-xl border border-stone-300 px-4 py-3 font-normal outline-none focus:border-stone-900 sm:rounded-none" placeholder="name@example.com" />
          </label>
        </fieldset>
        <fieldset className="grid gap-4 border-t border-stone-200 py-7">
          <legend className="pt-6 text-lg font-black">기본 배송지</legend>
          <label className="grid gap-2 text-sm font-bold">받는 분<input name="shippingRecipientName" required autoComplete="shipping name" className="rounded-xl border border-stone-300 px-4 py-3 font-normal outline-none focus:border-stone-900 sm:rounded-none" /></label>
          <label className="grid gap-2 text-sm font-bold">연락처<input name="shippingPhone" required autoComplete="shipping tel" className="rounded-xl border border-stone-300 px-4 py-3 font-normal outline-none focus:border-stone-900 sm:rounded-none" placeholder="010-0000-0000" /></label>
          <label className="grid gap-2 text-sm font-bold">
            우편번호
            <div className="flex gap-2">
              <input ref={signupZipRef} name="shippingZip" required inputMode="numeric" pattern="[0-9]{5,6}" autoComplete="shipping postal-code" className="min-w-0 flex-1 rounded-xl border border-stone-300 px-4 py-3 font-normal outline-none focus:border-stone-900 sm:rounded-none" placeholder="주소검색으로 입력" />
              <button type="button" onClick={searchSignupAddress} className="shrink-0 rounded-xl border border-stone-900 bg-stone-900 px-4 text-sm font-bold text-white transition hover:bg-stone-700 sm:rounded-none">주소검색</button>
            </div>
          </label>
          <label className="grid gap-2 text-sm font-bold">주소<input ref={signupAddressRef} name="shippingAddress1" required autoComplete="shipping address-line1" readOnly onClick={searchSignupAddress} className="cursor-pointer rounded-xl border border-stone-300 bg-stone-50 px-4 py-3 font-normal outline-none focus:border-stone-900 sm:rounded-none" placeholder="주소검색을 눌러 입력해 주세요" /></label>
          <label className="grid gap-2 text-sm font-bold">상세 주소 <span className="font-normal text-stone-400">(선택)</span><input ref={signupAddress2Ref} name="shippingAddress2" autoComplete="shipping address-line2" className="rounded-xl border border-stone-300 px-4 py-3 font-normal outline-none focus:border-stone-900 sm:rounded-none" placeholder="동/호수, 건물명 등" /></label>
        </fieldset>
        <button disabled={signupSubmitting} className="w-full rounded-xl bg-hajin-600 py-4 font-bold text-white transition disabled:cursor-wait disabled:bg-stone-400 sm:rounded-none">
          {signupSubmitting ? "가입 처리 중..." : "가입하기"}
        </button>
      </form>
    </main>
  );
}

function Footer() {
  const [footerInfo, setFooterInfo] = useState<any>({
    brandName: "전자랜드",
    companyName: "(주)에스와이에스리테일",
    representative: "김형영",
    phone: "",
    privacyManager: "",
    businessNumber: "106-81-01704",
    hostingService: "㈜에스와이에스리테일",
    mailOrderNumber: "용산구청 제00064호",
    medicalDeviceSalesNumber: "제105호",
    healthFoodSalesNumber: "제850호",
    foodImportSalesNumber: "제34호",
    email: "priceking@etland.co.kr",
    address: "서울시 용산구 청파로 74(한강로 3가) 전자랜드 신관 3층",
    businessCheckUrl: "",
    copyrightText: "ETLAND © All Rights Reserved.",
    customerCenters: [],
    certificationMarks: [
      { label: "KG이니시스 에스크로", imageUrl: "/assets/etland/footer/sponsor01.png", href: "https://mark.inicis.com/mark/escrow_popup_v3.php?mid=sysreT0001", description: "" },
      { label: "ISMS 인증범위", imageUrl: "/assets/etland/footer/sponsor02.png", href: "/assets/etland/footer/isms-certificate.pdf", description: "" },
      { label: "공정거래위원회", imageUrl: "/assets/etland/footer/sponsor03.png", href: "" },
      { label: "VeriSign Trusted", imageUrl: "/assets/etland/footer/sponsor04.png", href: "" },
      { label: "현금영수증 우수기업", imageUrl: "/assets/etland/footer/sponsor05.png", href: "" },
    ],
    footerNotices: [
      "전자랜드 쇼핑몰 내에는 개별 입점사의 상품이 포함되어 있으며 이 경우 (주)에스와이에스리테일은 통신판매중개자로서 거래당사자가 아니므로 해당 상품정보, 거래 정보 및 거래에 대하여 책임을 지지 않습니다.",
      "(주)에스와이에스리테일은 전자랜드 쇼핑몰 지점 상품입니다.",
    ],
  });
  useEffect(() => {
    let active = true;
    fetch("/api/store/hajin/copyright")
      .then((response) => response.ok ? response.json() : null)
      .then((payload) => {
        if (active && payload?.copyright) setFooterInfo((current: any) => ({ ...current, ...payload.copyright }));
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);
  const certificationMarks = (footerInfo.certificationMarks || []).filter((mark: any) => mark?.label && mark?.imageUrl);
  const notices = (footerInfo.footerNotices || []).filter(Boolean);
  const openCertificationMark = (event: MouseEvent<HTMLAnchorElement>, mark: any) => {
    const href = String(mark?.href || "");
    if (!href) return;
    if (href.includes("mark.inicis.com/mark/escrow_popup")) {
      event.preventDefault();
      window.open(href, "mark", "scrollbars=no,resizable=no,width=565,height=683");
    }
  };
  const businessRegistration = [
    footerInfo.businessNumber ? `사업자등록번호 : ${footerInfo.businessNumber}` : "",
    footerInfo.hostingService ? `호스팅서비스 : ${footerInfo.hostingService}` : "",
    footerInfo.mailOrderNumber ? `통신판매업신고 : ${footerInfo.mailOrderNumber}` : "",
  ].filter(Boolean).join(" ㅣ ");
  const salesRegistrations = [
    footerInfo.medicalDeviceSalesNumber ? `의료기기판매업신고 : ${footerInfo.medicalDeviceSalesNumber}` : "",
    footerInfo.healthFoodSalesNumber ? `건강기능식품판매업신고 : ${footerInfo.healthFoodSalesNumber}` : "",
    footerInfo.foodImportSalesNumber ? `식품등수입판매업신고 : ${footerInfo.foodImportSalesNumber}` : "",
  ].filter(Boolean).join(" ㅣ ");
  return (
    <footer className="mt-[30px] border-t border-stone-200 bg-white text-stone-700">
      <div className="mx-auto max-w-[1280px] px-5 py-6 lg:px-0 lg:py-8">
        <div className="max-w-[680px] break-words text-xs leading-6 text-stone-500">
          <p>
            {[footerInfo.companyName, footerInfo.brandName].filter(Boolean).join(" / ")}
            {footerInfo.representative ? <><br />대표이사 : {footerInfo.representative}</> : null}
            {footerInfo.address ? <><br />주소 : {footerInfo.address}</> : null}
            {businessRegistration ? <><br />{businessRegistration}</> : null}
            {salesRegistrations ? <><br />{salesRegistrations}</> : null}
            {footerInfo.email ? <><br />이메일 : {footerInfo.email}</> : null}
          </p>
        </div>
        {certificationMarks.length ? <div className="mt-8 flex flex-wrap items-center justify-center gap-x-10 gap-y-6 border-b border-stone-200 py-6 lg:justify-between">
          {certificationMarks.map((mark: any) => {
            const body = (
              <span className="block">
                <img src={mark.imageUrl} alt={mark.label} className="block h-auto max-w-full object-contain" />
              </span>
            );
            return mark.href ? <a key={`${mark.label}-${mark.imageUrl}`} href={mark.href} target="_blank" rel="noreferrer" onClick={(event) => openCertificationMark(event, mark)}>{body}</a> : <span key={`${mark.label}-${mark.imageUrl}`}>{body}</span>;
          })}
        </div> : null}
        <div className="mt-10 border-t border-stone-100 pt-7 text-[11px] leading-5 text-stone-400">
          {notices.map((notice: string) => <p key={notice}>{notice}</p>)}
          {footerInfo.copyrightText ? <p className="mt-3">{footerInfo.copyrightText}</p> : null}
        </div>
      </div>
    </footer>
  );
}

function FloatingTools() {
  return (
    <aside className="fixed bottom-4 left-3 z-30 w-11 overflow-hidden rounded-md border border-stone-200 bg-white text-center text-[11px] shadow-sm sm:bottom-5 sm:left-0 sm:w-12 sm:rounded-r-md sm:rounded-l-none">
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        className="grid w-full place-items-center gap-1 py-3 text-stone-600 hover:bg-stone-50"
      >
        <span className="text-3xl leading-6">↑</span>
        <span className="sr-only">맨 위로</span>
      </button>
    </aside>
  );
}

const categoryKeywords: Record<string, string[]> = {
  tv: ["tv", "텔레비전", "빔"],
  kitchen: ["냉장", "김치", "밥솥", "전자레인지", "오븐", "정수", "주방", "음식물"],
  living: ["세탁", "건조", "청소", "공기", "마사지"],
  season: ["에어컨", "선풍", "제습", "난방", "온풍"],
  health: ["안마", "건강", "헬스", "의료"],
  computer: ["노트북", "키보드", "태블릿", "컴퓨터", "모니터"],
  mobile: ["갤럭시", "아이폰", "태블릿", "모바일"],
  refurb: ["리퍼", "전시"],
  home: ["침대", "가구", "소파", "책상"],
  sports: ["골프", "스포츠", "운동"],
};

function fallbackCategoryProducts(slug: string) {
  if (slug === "kitchen") return kitchenListingSnapshot;
  if (slug === "tv") return tvCategorySnapshot;
  const terms = categoryKeywords[slug] || [];
  const matches = catalogProducts.filter((product) => terms.some((term) => `${product.name} ${product.brand}`.toLowerCase().includes(term)));
  return matches;
}

export default function App() {
  const storeApiEnabled = isHajinStoreApiEnabled();
  const [page, setPage] = useState<Page>(getPage);
  const [cart, setCart] = useState<Product[]>(() => {
    try {
      const stored = window.localStorage.getItem("hajin-cart");
      return stored ? (JSON.parse(stored) as Product[]) : [];
    } catch {
      return [];
    }
  });
  const [checkoutCart, setCheckoutCart] = useState<Product[] | null>(null);
  const [liveStore, setLiveStore] = useState<HajinStorefrontData | null>(null);
  const [liveCategory, setLiveCategory] = useState<{ key: string; products: Product[] } | null>(null);
  const [liveCategoryLoadingKey, setLiveCategoryLoadingKey] = useState<string | null>(null);
  const [liveSearch, setLiveSearch] = useState<{ query: string; products: Product[] } | null>(null);
  const [liveProduct, setLiveProduct] = useState<{ slug: string; product: Product | null } | null>(null);
  useEffect(() => {
    if (page.kind === "product") window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [page]);
  const refreshLiveStore = () => {
    loadHajinStorefront().then((data) => { if (data) setLiveStore(data); }).catch(() => {});
  };
  useEffect(() => {
    let active = true;
    loadHajinStorefront().then((data) => { if (active && data) setLiveStore(data); }).catch(() => {});
    return () => { active = false; };
  }, []);
  useEffect(() => {
    if (page.kind !== "benefit" || page.slug !== "cash-special") return;
    let active = true;
    // The manager can change this curated shelf while the storefront stays
    // open. Reloading when the page is entered keeps the new selection and
    // its category filters in sync without a browser refresh.
    loadHajinStorefront().then((data) => { if (active && data) setLiveStore(data); }).catch(() => {});
    return () => { active = false; };
  }, [page]);
  useEffect(() => {
    let active = true;
    let timer = 0;
    const refreshAtKoreaMidnight = () => {
      timer = window.setTimeout(async () => {
        const data = await loadHajinStorefront().catch(() => null);
        if (active && data) setLiveStore(data);
        if (active) refreshAtKoreaMidnight();
      }, millisecondsUntilKoreaMidnight());
    };
    refreshAtKoreaMidnight();
    return () => { active = false; window.clearTimeout(timer); };
  }, []);
  useEffect(() => {
    let active = true;
    if (page.kind !== "category") {
      setLiveCategory(null);
      setLiveCategoryLoadingKey(null);
      return () => { active = false; };
    }
    const key = `${page.slug}:${page.subCategory || ""}`;
    setLiveCategoryLoadingKey(key);
    loadHajinCategoryProducts(page.slug, categorySearchTerm(page.subCategory))
      .then((categoryProducts) => {
        if (active && categoryProducts) setLiveCategory({ key, products: categoryProducts });
      })
      .catch(() => { if (active) setLiveCategory(null); })
      .finally(() => { if (active) setLiveCategoryLoadingKey(null); });
    return () => { active = false; };
  }, [page]);
  useEffect(() => {
    let active = true;
    if (page.kind !== "search") {
      setLiveSearch(null);
      return () => { active = false; };
    }
    loadHajinProducts({ q: page.query })
      .then((products) => { if (active && products) setLiveSearch({ query: page.query, products }); })
      .catch(() => { if (active) setLiveSearch({ query: page.query, products: [] }); });
    return () => { active = false; };
  }, [page]);
  useEffect(() => {
    let active = true;
    if (page.kind !== "product") {
      setLiveProduct(null);
      return () => { active = false; };
    }
    loadHajinProduct(page.slug)
      .then((product) => { if (active) setLiveProduct({ slug: page.slug, product }); })
      .catch(() => { if (active) setLiveProduct({ slug: page.slug, product: null }); });
    return () => { active = false; };
  }, [page]);
  useEffect(() => {
    window.localStorage.setItem("hajin-cart", JSON.stringify(cart));
  }, [cart]);
  const add = (product: Product) => {
    if (product.soldOut) return;
    setCart((items) => [...items, product]);
  };
  const buy = (items: Product[]) => {
    const sellableItems = items.filter((item) => !item.soldOut);
    if (!sellableItems.length) return;
    setCheckoutCart(sellableItems);
    setCart((current) => [...current, ...sellableItems]);
  };
  window.onpopstate = () => setPage(getPage());
  const content = useMemo(() => {
    if (page.kind === "benefit") return <BenefitPage slug={page.slug} liveStore={liveStore} />;
    if (page.kind === "category") {
      if (page.slug === "best") return <SourceMenuPage source="best" liveStore={liveStore} />;
      const category = liveStore?.categories.find((item) => item.slug === page.slug) || categories.find((item) => item.slug === page.slug);
      const categoryKey = `${page.slug}:${page.subCategory || ""}`;
      const list = liveCategory?.key === categoryKey ? liveCategory.products : storeApiEnabled ? [] : fallbackCategoryProducts(page.slug);
      const categoryLoading = storeApiEnabled && liveCategoryLoadingKey === categoryKey && liveCategory?.key !== categoryKey;
      return (
        <Listing
          title={page.subCategory || category?.name || "상품"}
          categorySlug={page.slug}
          subCategory={page.subCategory}
          list={list}
          attentionList={storeApiEnabled ? list : page.slug === "kitchen" ? kitchenAttentionSnapshot : list}
          loading={categoryLoading}
          onAdd={add}
        />
      );
    }
    if (page.kind === "search")
      return (
        <SearchResultsLive
          query={page.query}
          list={liveSearch?.query === page.query ? liveSearch.products : storeApiEnabled ? [] : catalogProducts.filter((product) => `${product.name} ${product.brand}`.toLowerCase().includes(page.query.toLowerCase()))}
        />
      );
    if (page.kind === "product") {
      const product = liveProduct?.slug === page.slug ? liveProduct.product : storeApiEnabled ? null : [...refrigeratorSearchSnapshot, ...refrigeratorCategoryPageSnapshot, ...catalogProducts].find((item) => item.slug === page.slug) || products[0];
      return product ? <ProductPage product={product} relatedProducts={liveStore?.products || []} onAdd={add} onBuy={buy} /> : <main className="mx-auto grid min-h-[520px] max-w-[1280px] place-items-center px-5 text-center"><div><h1 className="text-2xl font-black">판매 중인 상품을 찾을 수 없습니다.</h1><button onClick={() => go("/")} className="mt-5 rounded-xl bg-hajin-600 px-5 py-3 text-sm font-bold text-white">홈으로 돌아가기</button></div></main>;
    }
    if (page.kind === "cart")
      return (
        <CartPage
          cart={cart}
          onCheckout={setCheckoutCart}
          onRemove={(index) => {
            setCheckoutCart(null);
            setCart((items) => items.filter((_, itemIndex) => itemIndex !== index));
          }}
        />
      );
    if (page.kind === "order") return <OrderPage cart={checkoutCart || cart} onComplete={() => { setCart([]); setCheckoutCart(null); }} />;
    if (page.kind === "account") return <AccountPage />;
    if (page.kind === "signup") return <SignupPage />;
      return <Home onAdd={add} liveStore={liveStore} apiEnabled={storeApiEnabled} onTodayDealExpired={refreshLiveStore} />;
  }, [cart.length, checkoutCart, liveCategory, liveCategoryLoadingKey, liveProduct, liveSearch, liveStore, page, storeApiEnabled]);
  return (
    <div className={`min-w-[320px] bg-white font-sans text-[#18221d] ${page.kind === "category" ? "" : "lg:pb-[120px]"}`}>
      <Header cartCount={cart.length} navigationCategories={categories} />
      {content}
      <Footer />
      <HajinLiveChat />
      <FloatingTools />
    </div>
  );
}
